import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { InfiniteCanvas, type InfiniteCanvasHandle } from './components/InfiniteCanvas';
import { BaseNode } from './components/nodes/BaseNode';
import { MediaLookupsContext, MediaOwnerContext } from './hooks/useFileUrl';
import { MediaLoadingBar } from './components/MediaLoadingBar';
import { createMediaLookups } from './utils/mediaProgress';
import { CardFarView } from './components/nodes/CardFarView';
import { farPictureOf, isFilmCard } from './utils/farZoom';
import { VideoGeneratorNode } from './components/nodes/VideoGeneratorNode';
import { ImageGeneratorNode } from './components/nodes/ImageGeneratorNode';
import { SketchNode } from './components/nodes/SketchNode';
import { ScriptNode } from './components/nodes/ScriptNode';
import { TextPromptNode } from './components/nodes/TextPromptNode';
import { WriteNode } from './components/nodes/WriteNode';
import { EntityNode } from './components/nodes/EntityNode';
import { StorageNode } from './components/nodes/StorageNode';
import { StoryboardNode } from './components/nodes/StoryboardNode';
import { CanvasNode, NodeType, ViewportTransform, NodeData, Position, Connection, HistoryItem, LightHistoryItem, UserProfile, ApiKeys, UserSubscription, SUBSCRIPTION_LIMITS, RecentFileMetadata, DeviceSession } from './types';
import { Video as VideoIcon, Sparkles, History, LogOut, Loader2, AlertTriangle, Check, Settings2, Mail, LogIn, UserPlus, Lock, Cloud, Database, Copy, Globe, DollarSign, Key, FileText, User, Menu, FolderOpen, Save, Download, Upload, Plus, File, Trash2, X, Edit, Edit2, Wand2, Crown, Zap, ArrowLeft, HardDrive, LayoutGrid, Home, PieChart, Users, Eye, UserMinus, PanelLeft, Music } from 'lucide-react';
import { REFERENCE_COMPACT_HEIGHT, ReferenceNode } from './components/nodes/ReferenceNode';
import { QuickEditorNode } from './components/nodes/QuickEditorNode';
import { EditPage } from './components/EditPage';
import { PageScrollLock } from './components/PageScrollLock';
import { LocalFolderWindow } from './components/LocalFolderWindow';
import { launchedFolderHelperRecently } from './utils/folderHelper';
import { useHeldNodeResults } from './hooks/useHeldNodeResults';
import { isPickedInputId, withoutPicked } from './utils/pickedReferences';
import { audioReferenceInputs, connectedAudioInputs, connectedEntities, connectedImageInputs, connectedPromptSource, connectedVideoInputs, imageReferenceInputs, videoReferenceInputs } from './utils/nodeInputs';
import { WorkspaceViewSwitch, type WorkspaceView } from './components/WorkspaceViewSwitch';
import { WORKSPACE_EDIT_NODE_ID, isWorkspaceEditNode, collectWorkspaceVideos, collectWorkspaceAudio, collectReferenceChoices, collectReferenceNodes } from './utils/workspaceEdit';
import { buildNodeClipboard, nextSelection, parseNodeClipboard, placePastedNodes, SYSTEM_CLIPBOARD_LIMIT, type NodeClipboard } from './utils/nodeClipboard';
import { afterMerge, changedHere, keepWholeLocalCopy, listsLeftToCloud, localCopyIsNewer, localCopyOfFile, mergeRemoteConnections, mergeRemoteHistory, mergeRemoteNodes, nodeIdsAddedWith, nodesToKeep, savedNodesAfterMerge, syncedConnectionIds, syncedNodeIdsAfterSave, type LocalCopy } from './utils/canvasSync';
import { saveProject, loadProject, loginUser, registerUser, initFirebase, isFirebaseActive, loginWithGoogle, logoutUser, listProjects, createProject, deleteProject, uploadProjectToCloud, migrateLegacyProject, ProjectMetadata, processNodesForExport, processHistoryForExport, blobUrlToBase64, rehydrateNodes, subscribeToAuthChanges, subscribeToSubscription, subscribeToProject, getDailyGenerationCount, incrementGenerationCount, resetPassword, getLocalStoragePrefs, LocalStoragePrefs, deleteUserAccount, getOnboardingStatus, completeOnboarding, createLocalWorkspace, saveLocalWorkspace, loadLocalWorkspace, listLocalWorkspaces, deleteLocalWorkspace, renameLocalWorkspace, duplicateLocalWorkspace, LocalWorkspace, LocalWorkspaceMetadata, getOrCreateDefaultLocalWorkspace, updateLastOpened, updateWorkspaceMeta, getLastOpenedMap, clearLastOpened, deleteNodeFromCloud, syncAllLocalProjectsToCloud, resolveInvitations, unshareProject, loadProjectReferenceNodes, syncLibraryProject, unshareLibraryProject, listMyProjectInvitations, answerProjectInvitation, type ProjectInvitation } from './utils/storage';
import { acceptProjectCopy, cancelProjectCopy, declineProjectCopy, listProjectCopies, sendProjectCopy, type ProjectCopies, type ProjectCopy } from './utils/projectCopies';
import { useProjectReferences } from './hooks/useProjectReferences';
import { hasLegacyNodes, migrateLegacyNodes } from './utils/legacyNodes';
import { afterPaint, createAutoSaves, createPendingSaves, failIfCloudFailed, onceOffScreen } from './utils/pendingSaves';
import { desktopApp } from './utils/desktopFolder';
import { API_KEY_STORAGE, loadApiKeys, removeApiKey, saveApiKey } from './utils/apiKeyStore';
import { COVER_WAIT_CLOUD_MS, COVER_WAIT_MS, workspaceCover } from './utils/workspaceCover';
import { historyItemFields, historyTotalCost, isFullDataHistoryItem } from './utils/historyItems';
import { cloudPendingAfterSave, marksAfterSave, savedNodesAfterSave, unsavedNodeIds, whatToSave, type SavedWorkspace } from './utils/unsavedNodes';
import { NodeKindIcon, nodeKind, nodeTone } from './components/nodes/nodeTheme';
import { NodeCommentsPanel } from './components/NodeCommentsPanel';
import { addComment, deleteComment, watchComments } from './utils/commentStore';
import { giveReaction, takeBackReaction, watchReactions } from './utils/reactionStore';
import { commentCounts, commentsOf, type NodeComment } from './utils/nodeComments';
import { keepList, sameJson } from './utils/unchanged';
import { sharedReferenceChoices } from './utils/projectReferences';
import type { ReferenceSharing } from './components/nodes/AddReferencesButton';
import { firebaseConfig } from './firebaseConfig';
import { Gallery } from './components/Gallery';
import { WorkspacesPage } from './pages/WorkspacesPage';
import type { WorkspaceRef, CreateFolderInput, DeleteFolderMode, DeleteFolderProgress, DeleteFolderResult, LibraryJump } from './components/library/types';
import { buildLibraryWorkspaces, clearStoredLocations, formatRelativeTime, knownWorkspaceCheck, LibraryWorkspace } from './utils/workspaceLibrary';
import { listWorkspaceFolders, listSharedLibraryFolders, createWorkspaceFolder, updateWorkspaceFolder, deleteWorkspaceFolders, WorkspaceFolder, FolderColor, FolderKind } from './utils/workspaceFolders';
import { getDescendantFolderIds, collectWorkspaceIdsInFolder, getFolderById, getFolderPath, makeUniqueSiblingName } from './utils/folderTree';
import { accountFolderName, placesFromLibrary } from './utils/localPlaces';
import { CanvasLibraryPanel } from './components/library/CanvasLibraryPanel';
import { NameDialog } from './components/library/LibraryDialogs';
import { LibraryMenu } from './components/library/LibraryMenu';
import { LoadingScreen } from './components/GlassOrb';
import { BrandMark } from './components/BrandMark';
import { useThemeWatcher } from './hooks/useTheme';
import { ProjectTile, WorkspaceThumb } from './components/library/ProjectTile';
import type { MenuEntry } from './components/library/LibraryMenu';
import { ChevronDown, ChevronRight, Folder as FolderIcon } from 'lucide-react';
import { warmLoadingPictures } from './utils/sayings';
import { HomePage } from './pages/HomePage';
import { AnalyticsPage } from './pages/AnalyticsPage';
import { useFeatureAccess, getEffectiveTier } from './utils/subscriptionAccess';
import { useIsAdmin, useProGrant } from './utils/proGrant';
import { PaymentModal } from './components/PaymentModal';
import { AuthModal } from './components/AuthModal';
import { SubscriptionPage } from './pages/SubscriptionPage';
import { SettingsPage } from './pages/SettingsPage';
import { IdeasPage } from './components/IdeasPage';
import { AI_MODELS } from './models/config';
import { imageSettingsOf, videoSettingsOf } from './utils/generatorSettings';
import { DEFAULT_GENERATOR, type PromptToCanvas } from './utils/promptWriter';
import { readWorkspacePeople, type IdeaUser } from './utils/ideaStore';
import type { WorkspacePerson } from './utils/ideas';
import { mergeNodeReactions, toggleNodeReaction, type NodeReaction, type NodeReactions } from './utils/nodeReactions';
import { TermsPage } from './pages/TermsPage';
import { PrivacyPage } from './pages/PrivacyPage';
import { AboutPage } from './pages/AboutPage';
import { BugsPage } from './pages/BugsPage';
import { BugReviewPage } from './pages/BugReviewPage';
import { NotFoundPage } from './pages/NotFoundPage';
import { isFileSystemAccessSupported, pickSaveDirectory, getLocalSaveSettings, setAutoSaveEnabled, clearStoredDirectoryHandle, verifyPermission, downloadLocalFile, getLocalSaveStats, LocalFileInfo, syncCloudToLocal, CloudSyncProgress, saveWorkspaceLocally, isWorkspaceAutoSaveEnabled, setWorkspaceAutoSaveEnabled, renameWorkspaceFolder, loadWorkspaceLocally, loadWorkspaceLocallyAt, findLocalWorkspaceFolders, setWorkspacePlaces, organiseLocalFolder, setLocalFolderAccount, isWorkspaceFileOf, syncLocalStoragePrefsToCloud, getStoredDirectoryHandle, setLocalFileMap, getLocalFileMap, registerLocalFileMapping, clearLocalFileMap, listLocalWorkflows, getWorkflowsDirectory, CustomWorkflow, isAutoSaveReady, autoSaveFile, getUserLocalStorage, setUserLocalStorage, requestLocalFolderAccess } from './utils/localSave';
import { findEmptyPosition, findEmptyPositionsForMultiple, positionOverlapsNodes } from './utils/canvasUtils';
import { getComfyUIClient, getComfyUIConfig, saveComfyUIConfig } from './utils/comfyui';
import { listUserFiles, clearSessionBlobs } from './utils/fileRegistry';
import { catchUpFolder, catchUpSummary, libraryCatchUpEntries, workspaceCatchUpEntries, type CatchUpProgress, type CatchUpResult } from './utils/folderCatchUp';
import { TwitterAuthOverlay } from './components/TwitterAuthOverlay';
import { isTwitterConnected, getTwitterUsername, getTwitterUserTier, disconnectTwitter } from './utils/twitter';
import { ShareNode } from './components/nodes/ShareNode';
import { useThumbnailGenerator } from './hooks/useThumbnailGenerator';
import { logger, debugLog } from './utils/logger';
import { AuthGuard } from './components/AuthGuard';
import { NotificationBell } from './components/NotificationBell';
import { useToast } from './components/ToastProvider';
import { cacheAuthState, clearCachedAuthState, getCachedAuthState } from './utils/authPersistence';
import { logCacheEvent, logSignInEvent, logSessionEvent, initAuthSession } from './utils/authLogger';
import { SessionManager } from './utils/sessionManager';
import { ShareModal } from './components/ShareModal';
import { PresenceAvatars } from './components/PresenceAvatars';
import { ProfileMenu } from './components/ProfileMenu';
import { ChangelogPanel } from './components/ChangelogPanel';
import { usePresence } from './hooks/usePresence';
import { useCollaboration } from './hooks/useCollaboration';
import { CollaboratorCursors } from './components/CollaboratorCursors';
import { Share2, History as HistoryIcon2, Images, MoreHorizontal } from 'lucide-react';

const generateId = () => Math.random().toString(36).substr(2, 9);

/**
 * What is known about how one workspace is saved. Noted when the workspace is loaded, kept up by
 * every save and cloud update, and of no use for any other workspace (`key`).
 */
interface SavedNote {
  key: string;
  /**
   * The workspace as it was loaded or last saved (the very arrays): a save writes what differs
   * from it, and nothing is saved while nothing does. Its nodes are null when it is not known
   * which nodes are saved.
   */
  saved: SavedWorkspace;
  /** The view the cloud is known to hold: a cloud update moves this one only when the view really changed there. */
  cloudView: ViewportTransform | null;
  /** The history the cloud is known to hold: it is not sent again while it is this very array. */
  cloudHistory: (HistoryItem | LightHistoryItem)[] | null;
  /** The stamps of this session's own saves (see mergeRemoteNodes). */
  ownStamps: Set<number>;
  /**
   * Set while the canvas holds a copy read from this device (the folder's file, or the device's
   * own copy when the cloud could not be read) and the cloud has not been heard yet: when that
   * copy was saved, whether the cloud had not taken it and, for the device's own copy, the
   * nodes as they were read. The cloud's first update settles which of the two is the newer one
   * (localCopyIsNewer) and clears this. Until then nothing is saved while nothing is changed
   * here (changedHere), and a save of what was changed leaves the cloud's connections and
   * history alone unless they were changed too (listsLeftToCloud).
   */
  localCopy: LocalCopy | null;
}


/** How often the library brings each of this person's shared projects in step on its own */
const PROJECT_SYNC_EVERY_MS = 5 * 60_000;

export default function App() {
  // Light, dark or the system's: applied before the first paint by index.html, kept here
  useThemeWatcher();
  const navigate = useNavigate();
  const location = useLocation();
  const toast = useToast();

  const [transform, setTransform] = useState<ViewportTransform>({
    x: 0,
    y: 0,
    scale: 1,
  });

  // Project Data - Start empty, will be populated when workspace loads
  const [nodes, setNodes] = useState<CanvasNode[]>([]);
  const [connections, setConnections] = useState<Connection[]>([]);
  // Use union type to support both legacy HistoryItem and new LightHistoryItem
  const [history, setHistory] = useState<(HistoryItem | LightHistoryItem)[]>([]);
  
  // Workspace State
  const [currentProjectId, setCurrentProjectId] = useState<string | null>(null);
  const [currentProjectName, setCurrentProjectName] = useState<string>("Untitled Project");
  const [projectList, setProjectList] = useState<ProjectMetadata[]>([]);
  const [localWorkspaces, setLocalWorkspaces] = useState<LocalWorkspaceMetadata[]>([]);
  const [currentLocalWorkspaceId, setCurrentLocalWorkspaceId] = useState<string | null>(null);
  const [isLocalWorkspaceMode, setIsLocalWorkspaceMode] = useState(false); // True when using local-only workspace
  const [showWorkspaceModal, setShowWorkspaceModal] = useState(false);
  const [isLoadingWorkspace, setIsLoadingWorkspace] = useState(true); // Show loading on initial load
  const [showLandingPage, setShowLandingPage] = useState(() => {
    // Show landing/workspaces by default, or if URL is /workspaces
    return location.pathname === '/workspaces' || location.pathname === '/';
  });
  const [recentFiles, setRecentFiles] = useState<RecentFileMetadata[]>([]);
  // Workspaces library page: flat workspace list + project/folder tree
  const [libraryWorkspaces, setLibraryWorkspaces] = useState<LibraryWorkspace[]>([]);
  const [folders, setFolders] = useState<WorkspaceFolder[]>([]);
  /** Other people's projects shared with this person, with their folders (utils/workspaceFolders.ts) */
  const [sharedFolders, setSharedFolders] = useState<WorkspaceFolder[]>([]);
  /** Projects others want to share with this person, waiting for their answer */
  const [projectInvitations, setProjectInvitations] = useState<ProjectInvitation[]>([]);
  /** Copies of whole projects: sent by this person, and ones waiting for their answer */
  const [projectCopies, setProjectCopies] = useState<ProjectCopies>({ sent: [], received: [] });
  /** When each of this person's shared projects was last brought in step (syncSharedProjectsFor) */
  const projectSyncedAt = useRef(new Map<string, number>());
  const [libraryLoaded, setLibraryLoaded] = useState(false);
  const [libraryError, setLibraryError] = useState<string | null>(null);
  const libraryUidRef = useRef<string | null>(null); // uid the library state was loaded for
  // Canvas project panel (left): the open workspace's project, its folders and sibling workspaces
  const [isProjectPanelOpen, setIsProjectPanelOpen] = useState<boolean>(() => {
    try { return localStorage.getItem('ns-canvas-project-panel') !== '0' && window.innerWidth >= 1024; } catch { return true; }
  });
  const [projectPanelExpanded, setProjectPanelExpanded] = useState<Set<string>>(() => new Set());
  /** Where a notification's link sends the person in the library (a project, Shared with me); cleared once the page is there */
  const [libraryJump, setLibraryJump] = useState<LibraryJump | null>(null);
  const [renamingWorkspace, setRenamingWorkspace] = useState<LibraryWorkspace | null>(null);
  const [isNavigatingHome, setIsNavigatingHome] = useState(false);
  // Set the moment another workspace is chosen, so the loading screen answers the click at once
  const [switchingTo, setSwitchingTo] = useState<{ name: string; step: 'saving' | 'loading' } | null>(null);
  const switchingRef = useRef(false);
  // Saves of workspaces switched away from, still running behind the next one
  const pendingSavesRef = useRef(createPendingSaves());

  // Sharing & Collaboration State
  const [showShareModal, setShowShareModal] = useState(false);
  const [showChangelogPanel, setShowChangelogPanel] = useState(false);
  const currentProjectMeta = projectList.find(p => p.id === currentProjectId);
  const isSharedProject = currentProjectMeta?.isShared === true;
  const sharedPermission = currentProjectMeta?.sharedPermission;
  const isViewOnly = isSharedProject && sharedPermission === 'view';
  // For shared projects, track the owner's userId so file references can be resolved cross-user
  const projectOwnerId = currentProjectMeta?.ownerId;

  // Interaction State
  // Selected nodes (Shift/Ctrl-click, Shift-drag box, Ctrl+A); the last one is the node single-node actions work on
  const [selectedNodeIds, setSelectedNodeIdsState] = useState<string[]>([]);
  // A selection that is already the one asked for keeps its array, so it renders nothing: a press
  // on the selected node (every drag starts with one), a click on empty canvas with nothing selected
  const setSelectedNodeIds = useCallback((next: string[] | ((current: string[]) => string[])) => {
    setSelectedNodeIdsState(current => keepList(current, typeof next === 'function' ? next(current) : next));
  }, []);
  const selectedNodeId = selectedNodeIds.length > 0 ? selectedNodeIds[selectedNodeIds.length - 1] : null;
  const setSelectedNodeId = useCallback((id: string | null) => setSelectedNodeIds(id ? [id] : []), [setSelectedNodeIds]);
  const selectedIdSet = useMemo(() => new Set(selectedNodeIds), [selectedNodeIds]);
  const [pendingConnection, setPendingConnection] = useState<{ sourceId: string; mousePos: { x: number; y: number } } | null>(null);
  const [isGalleryOpen, setIsGalleryOpen] = useState(false);
  // The page of the open workspace: the node canvas or the Edit page (the timeline editor)
  const [workspaceView, setWorkspaceView] = useState<WorkspaceView>('nodes');
  /** A screenplay is opened full on the Ideas page: the bars, the page switch and the menu step away; the account button stays */
  const [writingOpen, setWritingOpen] = useState(false);
  /** The page a workspace was last on, kept on this device by the workspace's id: Ideas stays Ideas when it is opened again */
  const WORKSPACE_VIEW_KEY = 'noodle.workspaceView';
  const rememberedWorkspaceView = (key: string | null): WorkspaceView => {
    try {
      const view = key ? localStorage.getItem(`${WORKSPACE_VIEW_KEY}:${key}`) : null;
      return view === 'ideas' || view === 'edit' ? view : 'nodes';
    } catch { return 'nodes'; }
  };
  const rememberWorkspaceView = (key: string | null, view: WorkspaceView) => {
    try { if (key) localStorage.setItem(`${WORKSPACE_VIEW_KEY}:${key}`, view); } catch { /* no storage: the workspace opens on its nodes next time */ }
  };
  
  // UI State
  const [isFileMenuOpen, setIsFileMenuOpen] = useState(false);
  const [isAccountMenuOpen, setIsAccountMenuOpen] = useState(false);
  const [isStoragePanelOpen, setIsStoragePanelOpen] = useState(false);
  // Local data bag for the Storage right-panel (not persisted as a canvas node)
  const [storagePanelData, setStoragePanelData] = useState<NodeData>({} as NodeData);
  const [expandedStorageNodes, setExpandedStorageNodes] = useState<Record<string, { width: number; height: number }>>({});
    const [showAnalyticsPage, setShowAnalyticsPage] = useState(() => location.pathname === '/analytics');

  // Auth State
  const [user, setUser] = useState<UserProfile | null>(null);
  const [authChecked, setAuthChecked] = useState(false); // Track if initial auth check is done
  const [clientId, setClientId] = useState<string>('');
  const [showSettingsPage, setShowSettingsPage] = useState(() => location.pathname === '/settings');
  // A node asking for a provider key opens Settings on the API Keys tab.
  const [settingsInitialTab, setSettingsInitialTab] = useState<'status' | 'api'>('status');
  const openApiKeySettings = () => {
    setSettingsInitialTab('api');
    setShowSettingsPage(true);
  };
  const [showAuthModal, setShowAuthModal] = useState(false);
  
  // Subscription State
  const [subscription, setSubscription] = useState<UserSubscription | null>(null);
  const [subscriptionLoaded, setSubscriptionLoaded] = useState(false); // Track if initial subscription check is done
  const [showSubscriptionModal, setShowSubscriptionModal] = useState(false);
  // Counts the times the Pro dialog on the landing page asked for the sign-in panel to open
  const [landingAuthRequest, setLandingAuthRequest] = useState(0);
  const [showSubscriptionPage, setShowSubscriptionPage] = useState(() => location.pathname === '/subscription');
  const [dailyGenerationCount, setDailyGenerationCount] = useState(0);
  
  // Legal Pages State — initialise from URL path instead of sessionStorage
  const [showTermsPage, setShowTermsPage] = useState(() => location.pathname === '/terms');
  const [showPrivacyPage, setShowPrivacyPage] = useState(() => location.pathname === '/privacy');
  const [showAboutPage, setShowAboutPage] = useState(() => location.pathname === '/about');
  // Known issues (/bugs) is for everyone, signed in or not; its review (/bugs/review) for admins
  const [showBugsPage, setShowBugsPage] = useState(() => location.pathname === '/bugs');
  const [showBugReviewPage, setShowBugReviewPage] = useState(() => location.pathname === '/bugs/review');
  
  // Known public routes — any path not in this set triggers the 404 page
  const KNOWN_ROUTES = ['/', '/terms', '/privacy', '/about', '/bugs', '/bugs/review', '/settings', '/subscription', '/analytics', '/workspaces'];
  const [showNotFoundPage, setShowNotFoundPage] = useState(() => !KNOWN_ROUTES.includes(location.pathname));
  
  // ── URL ↔ state sync ──────────────────────────────────────────────────────
  // When URL changes (browser back/forward), sync boolean flags
  useEffect(() => {
    const path = location.pathname;
    setShowTermsPage(path === '/terms');
    setShowPrivacyPage(path === '/privacy');
    setShowAboutPage(path === '/about');
    setShowBugsPage(path === '/bugs');
    setShowBugReviewPage(path === '/bugs/review');
    setShowSettingsPage(path === '/settings');
    setShowSubscriptionPage(path === '/subscription');
    setShowAnalyticsPage(path === '/analytics');
    if (path === '/workspaces') setShowLandingPage(true);
    // Show 404 for any unrecognised path
    setShowNotFoundPage(!KNOWN_ROUTES.includes(path));
  }, [location.pathname]);

  // When state flags change, push URL (avoid duplicate pushes)
  useEffect(() => {
    // Don't rewrite URL when showing 404 — keep the bad path visible
    if (showNotFoundPage) return;
    // Not before the signed-in person is known: /workspaces would be rewritten to / and back
    if (!authChecked) return;
    let target = '/';
    if (showTermsPage) target = '/terms';
    else if (showPrivacyPage) target = '/privacy';
    else if (showAboutPage) target = '/about';
    else if (showBugReviewPage) target = '/bugs/review';
    else if (showBugsPage) target = '/bugs';
    else if (showSettingsPage) target = '/settings';
    else if (showSubscriptionPage) target = '/subscription';
    else if (showAnalyticsPage) target = '/analytics';
    else if (showLandingPage && user) target = '/workspaces';
    // else keep '/' (homepage or canvas)
    if (location.pathname !== target) {
      navigate(target, { replace: false });
    }
  }, [showTermsPage, showPrivacyPage, showAboutPage, showBugsPage, showBugReviewPage, showSettingsPage, showSubscriptionPage, showAnalyticsPage, showLandingPage, showNotFoundPage, user, authChecked]);

  // Feature access based on subscription
  // Pro access given by hand to this address (utils/proGrant.ts) counts with what was paid for
  const proGrant = useProGrant(user?.email);
  const featureAccess = useFeatureAccess(subscription || undefined, proGrant);
  // Whoever runs the site: the Admin tab in Settings (an address listed in `admins`, written in the console)
  const isAdmin = useIsAdmin(user?.email);
  
  // Config UI State
  const [configTab, setConfigTab] = useState<'status' | 'api' | 'rules' | 'cors' | 'autosave' | 'account' | 'comfyui' | 'twitter'>('status');
  const [isFirebaseConfigured, setIsFirebaseConfigured] = useState(false);
  const [permissionError, setPermissionError] = useState(false);
  
  // Twitter Integration State
  const [showTwitterAuthOverlay, setShowTwitterAuthOverlay] = useState(false);
  const [twitterConnected, setTwitterConnected] = useState(false);
  const [twitterUsername, setTwitterUsername] = useState<string | null>(null);
  const [twitterUserTier, setTwitterUserTier] = useState<'basic' | 'premium'>('basic');
  
  // Auto-save settings
  const [autoSaveEnabled, setAutoSaveEnabledState] = useState(false);
  const [autoSaveFolderName, setAutoSaveFolderName] = useState<string | null>(null);
  const [autoSaveSupported] = useState(isFileSystemAccessSupported());
  /** Counts the times a folder was connected, so the folder catches up with the open workspace each time */
  const [folderRound, setFolderRound] = useState(0);
  // The window onto the local auto-save folder
  const [showLocalFolder, setShowLocalFolder] = useState(false);
  const chooseFolderFromWindow = () => {
      setShowLocalFolder(false);
      void handleSelectLocalFolder().then(chosen => { if (chosen !== false) setShowLocalFolder(true); });
  };
  const [localFiles, setLocalFiles] = useState<LocalFileInfo[]>([]);
  const [localFilesLoading, setLocalFilesLoading] = useState(false);
  const [localFilesExpanded, setLocalFilesExpanded] = useState<Set<string>>(new Set());
  const [localSaveStats, setLocalSaveStats] = useState<{ totalFiles: number; totalSize: number; workspaces: string[] } | null>(null);
  const [cloudSyncProgress, setCloudSyncProgress] = useState<CloudSyncProgress | null>(null);
  const [isCloudSyncing, setIsCloudSyncing] = useState(false);
  
  // ComfyUI settings
  const [comfyUIUrl, setComfyUIUrl] = useState(() => getComfyUIConfig().baseUrl);
  const [comfyUIConnected, setComfyUIConnected] = useState(false);
  const [comfyUICheckpoints, setComfyUICheckpoints] = useState<string[]>([]);
  const [selectedComfyCheckpoint, setSelectedComfyCheckpoint] = useState<string>('');
  const [customWorkflows, setCustomWorkflows] = useState<CustomWorkflow[]>([]);
  const [customWorkflowsLoading, setCustomWorkflowsLoading] = useState(false);
  const [customWorkflowInfo, setCustomWorkflowInfo] = useState<string | null>(null);
  const [customWorkflowError, setCustomWorkflowError] = useState<string | null>(null);
  // NOTE: workspaceAutoSave is re-evaluated when user changes (see useEffect below)
  const [workspaceAutoSave, setWorkspaceAutoSave] = useState(false);
  const previousProjectNameRef = useRef<string | null>(null);



  
  // Cross-device local storage prompt
  const [showLocalStoragePrompt, setShowLocalStoragePrompt] = useState(false);
  const [cloudLocalStoragePrefs, setCloudLocalStoragePrefs] = useState<LocalStoragePrefs | null>(null);
  // Name of the folder this browser already has when only its permission is missing (after a restart)
  const [reconnectFolderName, setReconnectFolderName] = useState<string | null>(null);
  const [reconnectProblem, setReconnectProblem] = useState<'refused' | 'missing' | null>(null);
  const [localStoragePromptDismissed, setLocalStoragePromptDismissed] = useState(
    getUserLocalStorage(undefined, 'localStoragePromptDismissed') === 'true'
  );
  
  // Local sync progress state
  const [localSyncStatus, setLocalSyncStatus] = useState<{
    isActive: boolean;
    step: 'idle' | 'connecting' | 'scanning' | 'importing' | 'complete' | 'error';
    message: string;
    progress?: { current: number; total: number };
    folderName?: string;
  }>({ isActive: false, step: 'idle', message: '' });
  
  // Delete account state
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  
  // Onboarding state
  const [showOnboarding, setShowOnboarding] = useState(false);
  
  // Auto-save indicator state
  const [autoSaveStatus, setAutoSaveStatus] = useState<{
    isActive: boolean;
    type: 'local' | 'cloud' | 'both';
    message: string;
    timestamp?: number;
  }>({ isActive: false, type: 'local', message: '' });
  const autoSaveStatusTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  
  // Api Keys State: the keys kept (this tab's, and in the desktop app those saved on this computer),
  // there from the first render so nothing asks for a key that is already saved
  const [keptKeys] = useState(loadApiKeys);
  const [apiKeys, setApiKeys] = useState<ApiKeys>(() => ({
      googleGenAi: keptKeys.keys.googleGenAi || '',
      openAi: keptKeys.keys.openAi || '',
      venice: keptKeys.keys.venice || '',
      vertexApiKey: keptKeys.keys.vertexApiKey || '',
      vertexProjectId: keptKeys.keys.vertexProjectId || '',
      vertexLocation: keptKeys.keys.vertexLocation || 'us-central1',
  }));
  /** Whether saved keys are there after the app closes (the desktop app), else only for this tab */
  const keysLast = keptKeys.lasting;

  // Add state for input values (separate from apiKeys to avoid exposing)
  const [inputApiKeys, setInputApiKeys] = useState({
    googleGenAi: '',
    openAi: '',
    venice: '',
    vertexApiKey: '',
    vertexProjectId: '',
    vertexLocation: '' // blank means "keep the saved value" on Save
  });
  
  // Email/Pass Auth State
  const [authMode, setAuthMode] = useState<'login' | 'signup' | 'forgot'>('login');
  
  // Refs
  // The auto-saves waiting for their two seconds and the saves under way: closing the window runs
  // the waiting one at once and waits for them all (flushSaves)
  const autoSavesRef = useRef(createAutoSaves());
  const transformRef = useRef(transform);
  const nodesRef = useRef(nodes);
  const selectedNodeIdsRef = useRef(selectedNodeIds);
  const connectionsRef = useRef(connections);
  const isViewOnlyRef = useRef(isViewOnly);
  const featureAccessRef = useRef(featureAccess);
  const userRef = useRef(user);
  const toastRef = useRef(toast);
  // The open workspace, so a paste can tell whether the nodes came from this one
  const workspaceKeyRef = useRef<string | null>(null);
  const workspaceViewRef = useRef(workspaceView);
  const fileInputRef = useRef<HTMLInputElement>(null);
  // Node and connection ids the canvas had when it last matched the cloud (load, save, cloud update), and the
  // nodes a save under way is writing: a cloud update is merged against these instead of replacing local work
  const syncedNodeIdsRef = useRef<Set<string>>(new Set());
  const syncedConnectionIdsRef = useRef<Set<string>>(new Set());
  const savingNodeIdsRef = useRef<Set<string>>(new Set());
  // How the workspace last loaded is saved; leaving it saves only what changed since, and nothing is
  // saved while nothing has
  const savedNoteRef = useRef<SavedNote | null>(null);
  /** What is noted about workspace `key`; null when the note is of another one (loaded since, or none yet): then nothing is known. */
  const savedNoteOf = (key: string | null): SavedNote | null => (key && savedNoteRef.current?.key === key ? savedNoteRef.current : null);
  // Counts the saves made on leaving a cloud workspace: an auto-save that was under way when one began holds an older state of the canvas
  const leaveCountRef = useRef(0);
  const historyRef = useRef(history);
  const isLoadingWorkspaceRef = useRef(true);
  // The last copy made in this tab, for when the system clipboard could not hold it
  const lastCopiedRef = useRef<NodeClipboard | null>(null);

  // Session & concurrent-access state
  const sessionManagerRef = useRef<SessionManager | null>(null);
  // Undefined while the list is being fetched (Settings shows it as loading)
  const [activeSessions, setActiveSessions] = useState<DeviceSession[] | undefined>([]);
  const [currentSessionId, setCurrentSessionId] = useState<string | null>(null);
  const dirtyNodeIdsRef = useRef<Set<string>>(new Set());
  
  // Presence & Collaboration
  const enablePresence = !!currentProjectId && !isLocalWorkspaceMode && !!user;
  const { otherUsers } = usePresence(currentProjectId, enablePresence);
  // Cursors are shared by the CollaboratorCursors component, not from here: a cursor update must
  // not render the whole app. This user's own cursor is sent only while someone else is here to see it.
  const othersPresent = enablePresence && otherUsers.length > 0;
  const { collaborationState, onLocalSave } = useCollaboration(
    currentProjectId,
    user,
    enablePresence,
  );

  // Thumbnail generation
  const { pictureBeforeCover, generateThumbnail, thumbnailToSave, forgetThumbnail } = useThumbnailGenerator();
  /** Where the open workspace's files are looked up, as its nodes look them up: for its cover picture */
  const coverMediaOptions = () => ({ userId: user?.sub, workspaceName: currentProjectName, ownerUserId: projectOwnerId || undefined });
  const canvasRef = useRef<InfiniteCanvasHandle | null>(null);
  // The lookups of the canvas's pictures, counted for the loading bar in the top bar
  const [mediaLookups] = useState(createMediaLookups);
  const containerRef = useRef<HTMLDivElement>(null);
  const contentLayerRef = useRef<HTMLDivElement>(null);

  const refreshCustomWorkflows = useCallback(async () => {
      if (!autoSaveEnabled) {
          setCustomWorkflows([]);
          setCustomWorkflowInfo('Enable Local Auto-Save to connect custom workflows.');
          setCustomWorkflowError(null);
          setCustomWorkflowsLoading(false);
          return;
      }

      setCustomWorkflowsLoading(true);
      setCustomWorkflowError(null);
      setCustomWorkflowInfo(null);

      try {
          const workflows = await listLocalWorkflows();
          setCustomWorkflows(workflows);

          if (workflows.length === 0) {
              setCustomWorkflowInfo('No workflow JSON files detected. Add files to the workflows folder.');
          } else {
              setCustomWorkflowInfo(`Loaded ${workflows.length} workflow${workflows.length === 1 ? '' : 's'}.`);
          }
      } catch (err: any) {
          console.error('Failed to load custom workflows', err);
          setCustomWorkflowError(err?.message || 'Failed to load workflows. Re-select your local folder to grant access.');
      } finally {
          setCustomWorkflowsLoading(false);
      }
  }, [autoSaveEnabled]);

  const ensureWorkflowsFolder = useCallback(async () => {
      if (!autoSaveEnabled) {
          setCustomWorkflowError('Enable Local Auto-Save to manage custom workflows.');
          return;
      }

      try {
          const dir = await getWorkflowsDirectory(true);
          if (dir) {
              setCustomWorkflowError(null);
              setCustomWorkflowInfo('Workflows folder is ready. Drop ComfyUI JSON files inside to use them.');
          } else {
              setCustomWorkflowError('Unable to access the workflows folder. Try selecting the local folder again.');
          }
      } catch (err: any) {
          console.error('Failed to prepare workflows folder', err);
          setCustomWorkflowError(err?.message || 'Failed to prepare workflows folder.');
      }
  }, [autoSaveEnabled]);

  // Sync refs
  useEffect(() => { transformRef.current = transform; }, [transform]);
  useEffect(() => { nodesRef.current = nodes; }, [nodes]);
  selectedNodeIdsRef.current = selectedNodeIds;
  connectionsRef.current = connections;
  historyRef.current = history;
  isViewOnlyRef.current = isViewOnly;
  featureAccessRef.current = featureAccess;
  userRef.current = user;
  toastRef.current = toast;
  // As the loading screen: it covers the canvas only for signed-in users
  isLoadingWorkspaceRef.current = !!user && isLoadingWorkspace;
  workspaceKeyRef.current = currentProjectId || currentLocalWorkspaceId || null;
  useEffect(() => { workspaceViewRef.current = workspaceView; }, [workspaceView]);
  // A workspace opens on the page it was last on, on this device (its nodes the first time)
  useEffect(() => { setWorkspaceView(rememberedWorkspaceView(currentProjectId || currentLocalWorkspaceId || null)); setSelectedNodeIds([]); }, [currentProjectId, currentLocalWorkspaceId]);
  
  // Twitter connection state sync
  useEffect(() => {
    const refreshTwitterState = () => {
      setTwitterConnected(isTwitterConnected());
      setTwitterUsername(getTwitterUsername());
      setTwitterUserTier(getTwitterUserTier());
    };
    
    refreshTwitterState();
    // Refresh on window focus (in case auth happened in a popup)
    window.addEventListener('focus', refreshTwitterState);
    return () => window.removeEventListener('focus', refreshTwitterState);
  }, []);

  // --- INIT (runs once on mount) ---
  useEffect(() => {
      const active = initFirebase();
      setIsFirebaseConfigured(active);

      // Initialize auth session for logging
      initAuthSession();
      
      // Check for cached auth state for logging purposes
      // But DON'T set user from cache - wait for Firebase to confirm
      // Setting user from cache triggers effects that require active Firebase auth
      const cachedAuth = getCachedAuthState();
      if (cachedAuth) {
          logCacheEvent('hit', { userId: cachedAuth.user.sub });
          // NOTE: We used to setUser(cachedAuth.user) here but that caused
          // "Missing or insufficient permissions" errors because Firebase
          // auth session wasn't restored yet when other effects ran
      } else {
          logCacheEvent('miss', {});
      }
      
      // Safety timeout - if Firebase auth doesn't respond, mark auth as checked, so the app is
      // never stuck on "Checking your sign-in" forever. Someone signed in on this device before
      // (the cache) is given longer: confirming a sign-in takes a few requests, and giving up early
      // showed them the front page instead of their library on a slow connection.
      const authTimeoutMs = cachedAuth ? 20_000 : 5_000;
      const authTimeout = setTimeout(() => {
          setAuthChecked((current) => {
              if (!current) {
                  console.warn(`Auth check timed out after ${authTimeoutMs / 1000} seconds, proceeding without auth`);
                  return true;
              }
              return current;
          });
      }, authTimeoutMs);
      
      const unsubscribe = subscribeToAuthChanges((u) => {
          clearTimeout(authTimeout); // Clear timeout since we got a response
          setUser(u);
          setAuthChecked(true); // Auth state is now known

          if (u) {
              // The loading cards' pictures, fetched while nothing else is happening, so the first wait has its picture
              warmLoadingPictures();
              // Cache auth state for offline access and faster startup
              cacheAuthState(u);
              logSignInEvent('success', { userId: u.sub, method: 'firebase' });
              setShowAuthModal(false);

              // Resolve pending workspace invitations
              resolveInvitations().then(result => {
                  if (result.count > 0) {
                      result.resolved.forEach(inv => {
                          toast.success(inv.kind === 'project'
                              ? `You've been added to the project "${inv.projectName}"`
                              : `You've been added to workspace "${inv.projectName}"`);
                      });
                  }
              }).catch(() => {});
          } else {
              // User logged out or not signed in
              clearCachedAuthState();
          }
      });
      
      return () => {
          clearTimeout(authTimeout);
          unsubscribe();
      };
  }, []);

  // --- THE ACCOUNT'S OWN FOLDER INSIDE THE LOCAL FOLDER ---
  // Each account's files go under a folder named after it (utils/localPlaces.ts accountFolderName),
  // so two accounts that chose the same folder on this computer keep their files apart. Settings
  // shows the name; signed out there is none.
  const [autoSaveAccountFolder, setAutoSaveAccountFolder] = useState<string | null>(null);
  useEffect(() => {
      setAutoSaveAccountFolder(setLocalFolderAccount(user?.sub, user ? accountFolderName(user) : null));
  }, [user]);

  // --- LOAD AUTO-SAVE SETTINGS ---
  useEffect(() => {
      const loadAutoSaveSettings = async () => {
          const settings = await getLocalSaveSettings(user?.sub);
          setAutoSaveEnabledState(settings.enabled);
          if (settings.directoryHandle) {
              setAutoSaveFolderName(settings.directoryHandle.name);
          }
      };
      loadAutoSaveSettings();
  }, [user]);

  // --- RE-EVALUATE USER-SCOPED SETTINGS WHEN USER CHANGES ---
  useEffect(() => {
      setWorkspaceAutoSave(isWorkspaceAutoSaveEnabled(user?.sub));
      setLocalStoragePromptDismissed(
          getUserLocalStorage(user?.sub, 'localStoragePromptDismissed') === 'true'
      );
  }, [user]);

  useEffect(() => {
      refreshCustomWorkflows();
  }, [refreshCustomWorkflows]);
  
  // --- CHECK FOR CROSS-DEVICE LOCAL STORAGE ---
  useEffect(() => {
      const checkCrossDeviceLocalStorage = async () => {
          // Only check if user is logged in and API is supported
          if (!user || !autoSaveSupported || localStoragePromptDismissed) return;
          
          // Check what Firebase says about local storage
          const cloudPrefs = await getLocalStoragePrefs(user.sub);
          if (!cloudPrefs?.hasLocalStorageEnabled) return; // No local storage enabled anywhere
          
          setCloudLocalStoragePrefs(cloudPrefs);
          
          // Check if current device/browser has local storage set up
          const localHandle = await getStoredDirectoryHandle(user?.sub);
          setReconnectFolderName(null);
          setReconnectProblem(null);
          if (localHandle) {
              // Already set up on this device/browser, verify permission
              // Only query, don't request (request requires user activation)
              try {
                  const hasPermission = await verifyPermission(localHandle, false, false);
                  if (hasPermission) {
                      // All good, don't show prompt
                      return;
                  }
              } catch (e) {
                  console.warn('Failed to query permission:', e);
              }
              // The folder is known here; the browser only needs permission again, which takes one click
              setReconnectFolderName(localHandle.name);
          }
          
          // User has local storage enabled elsewhere but not here - show prompt
          logger.debug('Storage', '📢 Local storage enabled elsewhere but not on this device/browser');
          setShowLocalStoragePrompt(true);
      };
      
      checkCrossDeviceLocalStorage();
  }, [user, autoSaveSupported, localStoragePromptDismissed]);

  // --- FILE & ACCOUNT MENU CLICK OUTSIDE HANDLER ---
  useEffect(() => {
      const handleClickOutside = (e: MouseEvent) => {
          if (isFileMenuOpen && !(e.target as HTMLElement).closest('.file-menu-container')) {
              setIsFileMenuOpen(false);
          }
          if (isAccountMenuOpen && !(e.target as HTMLElement).closest('.account-menu-container')) {
              setIsAccountMenuOpen(false);
          }
      };
      window.addEventListener('click', handleClickOutside);
      return () => {
          window.removeEventListener('click', handleClickOutside);
      };
  }, [isFileMenuOpen, isAccountMenuOpen]);

  // --- SUBSCRIPTION LISTENER ---
  useEffect(() => {
      // Only fetch subscription data after Firebase auth is confirmed
      // This prevents "Missing or insufficient permissions" errors when
      // user is set from cache but Firebase auth session isn't restored yet
      if (!user || !authChecked) {
          setSubscription(null);
          setSubscriptionLoaded(false);
          setDailyGenerationCount(0);
          return;
      }
      
      // Subscribe to real-time subscription updates
      const unsubscribe = subscribeToSubscription(user.sub, (sub) => {
          setSubscription(sub);
          setSubscriptionLoaded(true); // Mark subscription as loaded (even if null = free user)
      });
      
      // Fetch daily generation count
      getDailyGenerationCount(user.sub).then(setDailyGenerationCount);
      
      return () => {
          unsubscribe();
      };
  }, [user, authChecked]);

  // --- CLIPBOARD (COPY / PASTE) LOGIC ---
  useEffect(() => {
      // Shortcuts act on the node canvas only, not on the library or other pages
      const canvasShowing = () => !!document.querySelector('[data-canvas-container]');

      const handleKeyDown = async (e: KeyboardEvent) => {
          if (workspaceViewRef.current !== 'nodes' || !canvasShowing()) return; // the canvas is covered by another page, or not shown
          if (['INPUT', 'TEXTAREA', 'SELECT'].includes((document.activeElement as HTMLElement).tagName) || (document.activeElement as HTMLElement).isContentEditable) {
              return;
          }
          const mod = e.ctrlKey || e.metaKey;
          const key = e.key.toLowerCase();
          if (mod && key === 'c') {
              // Text selected on the page is copied as text, as usual
              if (window.getSelection()?.toString()) return;
              const clipboard = buildNodeClipboard(nodesRef.current, connectionsRef.current, selectedNodeIdsRef.current, workspaceKeyRef.current || undefined);
              if (!clipboard) return;
              e.preventDefault();
              lastCopiedRef.current = clipboard;
              // The system clipboard carries the copy to other tabs and workspaces; very large copies stay in this tab
              const text = JSON.stringify(clipboard);
              if (text.length <= SYSTEM_CLIPBOARD_LIMIT) {
                  try { await navigator.clipboard.writeText(text); } catch (err) { console.warn('Could not write the clipboard; the copy stays in this tab', err); }
              }
              const nodeCount = clipboard.nodes.length;
              const linkCount = clipboard.connections.length;
              toastRef.current.success(
                  nodeCount === 1 ? 'Copied 1 node' : `Copied ${nodeCount} nodes`,
                  `${linkCount ? `With ${linkCount} connection${linkCount === 1 ? '' : 's'}. ` : ''}Paste with ${navigator.platform.startsWith('Mac') ? 'Cmd' : 'Ctrl'}+V here or in another workspace.`,
              );
          } else if (mod && key === 'a') {
              e.preventDefault();
              setSelectedNodeIds(nodesRef.current.filter(node => !isWorkspaceEditNode(node)).map(node => node.id));
          } else if (key === 'escape') {
              setSelectedNodeIds([]);
          }
      };

      /**
       * Paste copied nodes into the open workspace: new ids, connections wired to the copies,
       * placed in the middle of the view on free space, and selected.
       */
      const pasteNodes = (clipboard: NodeClipboard) => {
          if (isViewOnlyRef.current) {
              toastRef.current.error('This workspace is view only', 'Ask its owner for edit access to paste nodes here.');
              return;
          }
          if (isLoadingWorkspaceRef.current) {
              toastRef.current.info('The workspace is still opening', 'Paste again once it has loaded.');
              return;
          }
          const existing = nodesRef.current.filter(node => !isWorkspaceEditNode(node));
          if (!featureAccessRef.current.canAddNode(existing.length + clipboard.nodes.length - 1)) {
              // Over the plan's node limit: offer the upgrade, as adding a node does
              if (userRef.current) setShowSubscriptionPage(true);
              else setShowSubscriptionModal(true);
              return;
          }
          const t = transformRef.current;
          const center = { x: (window.innerWidth / 2 - t.x) / t.scale, y: (window.innerHeight / 2 - t.y) / t.scale };
          const fromHere = !clipboard.sourceWorkspace || clipboard.sourceWorkspace === workspaceKeyRef.current;
          const placed = placePastedNodes(clipboard, { existingNodes: existing, center, markAsCopy: fromHere, makeId: generateId });
          placed.nodes.forEach(node => dirtyNodeIdsRef.current.add(node.id));
          setNodes(prev => [...prev, ...placed.nodes]);
          if (placed.connections.length > 0) setConnections(prev => [...prev, ...placed.connections]);
          setSelectedNodeIds(placed.nodes.map(node => node.id));
          // Bring the pasted group into view, as adding a node does
          const left = Math.min(...placed.nodes.map(node => node.position.x));
          const top = Math.min(...placed.nodes.map(node => node.position.y));
          const right = Math.max(...placed.nodes.map(node => node.position.x + (node.size?.width ?? 320)));
          const bottom = Math.max(...placed.nodes.map(node => node.position.y + (node.size?.height ?? 200)));
          requestAnimationFrame(() => canvasRef.current?.panToNode(left, top, right - left, bottom - top));
      };
      // Set when a paste event arrived for the latest Ctrl+V, so the in-tab fallback below does not paste twice
      let pasteEventSeen = false;

      const handlePaste = async (e: ClipboardEvent) => {
          pasteEventSeen = true;
          if (workspaceViewRef.current !== 'nodes' || !canvasShowing()) return;
          if (['INPUT', 'TEXTAREA', 'SELECT'].includes((document.activeElement as HTMLElement).tagName) || (document.activeElement as HTMLElement).isContentEditable) {
              return;
          }
          if (e.clipboardData?.files && e.clipboardData.files.length > 0) {
              e.preventDefault();
              const file = e.clipboardData.files[0];
              if (file.type.startsWith('image/')) {
                   const reader = new FileReader();
                   reader.onload = (event) => {
                       const result = event.target?.result as string;
                       const img = new Image();
                       img.onload = () => {
                           const t = transformRef.current;
                           const centerX = (window.innerWidth / 2 - t.x) / t.scale;
                           const centerY = (window.innerHeight / 2 - t.y) / t.scale;
                           const aspectRatio = img.naturalWidth / img.naturalHeight;
                           const width = 320;
                           const height = width / aspectRatio + 50;
                           
                           // Find empty position for pasted image
                           setNodes(prev => {
                               const position = findEmptyPosition(
                                   centerX - width / 2,
                                   centerY - height / 2,
                                   width,
                                   height,
                                   prev
                               );
                               const newNode: CanvasNode = {
                                   id: generateId(),
                                   type: NodeType.MEDIA_REFERENCE,
                                   position,
                                   size: { width, height },
                                   data: { title: 'Pasted image', referenceKind: 'image', imageUrl: result, originalWidth: img.naturalWidth, originalHeight: img.naturalHeight, timestamp: Date.now() }
                               };
                               setSelectedNodeId(newNode.id);
                               return [...prev, newNode];
                           });
                       };
                       img.src = result;
                   };
                   reader.readAsDataURL(file);
              }
              return;
          }

          const fromClipboard = parseNodeClipboard(e.clipboardData?.getData('text'));
          const inTab = lastCopiedRef.current;
          // The newer copy wins: the system clipboard may still hold an older one when a big copy stayed in this tab
          const clipboard = fromClipboard && inTab && (inTab.copiedAt ?? 0) > (fromClipboard.copiedAt ?? 0) ? inTab : fromClipboard ?? inTab;
          if (clipboard) {
              e.preventDefault();
              pasteNodes(clipboard);
          }
      };

      // Some browsers send no paste event outside text fields: then Ctrl+V pastes this tab's last copy
      const handleCtrlVPaste = (e: KeyboardEvent) => {
          if (!lastCopiedRef.current || workspaceViewRef.current !== 'nodes' || !canvasShowing()) return;
          if (['INPUT', 'TEXTAREA', 'SELECT'].includes((document.activeElement as HTMLElement).tagName) || (document.activeElement as HTMLElement).isContentEditable) {
              return;
          }
          if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'v') {
              pasteEventSeen = false;
              window.setTimeout(() => {
                  if (!pasteEventSeen && lastCopiedRef.current) pasteNodes(lastCopiedRef.current);
              }, 150);
          }
      };

      window.addEventListener('keydown', handleKeyDown);
      window.addEventListener('paste', handlePaste);
      window.addEventListener('keydown', handleCtrlVPaste);
      return () => {
          window.removeEventListener('keydown', handleKeyDown);
          window.removeEventListener('paste', handlePaste);
          window.removeEventListener('keydown', handleCtrlVPaste);
      };
  }, []);

  // --- WORKSPACE LOADING ---
  
  type ReadWorkspace =
    | { from: 'folder'; projectId: string; name: string; data: NonNullable<Awaited<ReturnType<typeof loadWorkspaceLocally>>['data']> }
    | { from: 'cloud'; projectId: string; project: Awaited<ReturnType<typeof loadProject>> };

  /** A workspace's contents, read without showing them: from the local folder when kept there, else the cloud. */
  const readWorkspace = async (projectId: string, cloudSync: boolean): Promise<ReadWorkspace> => {
      // Try local-first if workspace auto-save is enabled
      if (workspaceAutoSave && autoSaveFolderName) {
          const projectMeta = projectList.find(p => p.id === projectId);
          if (projectMeta?.name) {
              logger.debug('Storage', '📁 Trying to load workspace from local:', projectMeta.name);
              const localResult = await loadWorkspaceLocally(projectMeta.name, user?.sub);
              // Only this workspace's own file: it is found by name, and another workspace of the same name may have written it
              if (localResult.success && localResult.data && isWorkspaceFileOf(localResult.data, projectId)) {
                  return { from: 'folder', projectId, name: projectMeta.name, data: localResult.data };
              }
              logger.debug('Storage', '📁 Local workspace not found (or another workspace\'s), falling back to cloud');
          }
      }
      logger.debug('Storage', `readWorkspace: calling loadProject for ${projectId}, cloudSync=${cloudSync}`);
      return { from: 'cloud', projectId, project: await loadProject(projectId, cloudSync) };
  };

  /**
   * Notes what was just put on the canvas as what is saved: opening a workspace is not an edit,
   * so nothing is written until something changes. `cloud` says what the cloud is known to hold
   * of it (nothing, when left out). `localCopy`: the canvas holds a copy read from this device,
   * and whether it or the cloud's is the newer one is settled by the cloud's first update.
   */
  const noteLoaded = (
      key: string,
      loaded: SavedWorkspace,
      cloud?: { view: ViewportTransform; history: (HistoryItem | LightHistoryItem)[] },
      localCopy: LocalCopy | null = null,
  ) => {
      savedNoteRef.current = { key, saved: loaded, cloudView: cloud?.view ?? null, cloudHistory: cloud?.history ?? null, ownStamps: new Set(), localCopy };
      // Marks left by the workspace that was open before mean nothing here
      dirtyNodeIdsRef.current.clear();
  };

  const loadWorkspace = async (projectId: string, cloudSync: boolean = false, prefetched?: Promise<ReadWorkspace>) => {
      const read = await (prefetched ?? readWorkspace(projectId, cloudSync));

      if (read.from === 'folder') {
          const data = read.data;
          logger.debug('Storage', '✅ Loaded workspace from local storage');
          const loaded = { nodes: data.nodes || [], connections: data.connections || [], history: data.history || [], name: data.projectName || read.name };
          setNodes(loaded.nodes);
          setConnections(loaded.connections);
          syncedNodeIdsRef.current = new Set(loaded.nodes.map((n: CanvasNode) => n.id));
          syncedConnectionIdsRef.current = new Set(loaded.connections.map((c: Connection) => c.id));
          setTransform(data.transform || { x: 0, y: 0, scale: 1 });
          setHistory(loaded.history);
          setCurrentProjectId(projectId);
          setCurrentProjectName(loaded.name);
          // The folder's copy is what is on the canvas. With cloud sync, whether it or the cloud's
          // is the newer one is settled by the cloud's first update (see the real-time sync below):
          // until then nothing of this copy is sent to the cloud except what is changed here
          noteLoaded(projectId, loaded, undefined, cloudSync && user ? localCopyOfFile(data) : null);

          // Load the localFileMap for offline image resolution
          if (data.localFileMap) {
              setLocalFileMap(data.localFileMap);
              logger.debug('Storage', '📁 Loaded localFileMap with', Object.keys(data.localFileMap).length, 'entries');
          }
          return;
      }

      const project = read.project;
      logger.debug('Storage', `loadWorkspace: loadProject returned ${project?.nodes?.length ?? 'null'} nodes`);
      if (project) {
          logger.debug('Storage', `loadWorkspace: setting ${project.nodes.length} nodes in state`);
          const loaded = { nodes: project.nodes, connections: project.connections, history: project.history || [], name: project.name || "Untitled" };
          setNodes(loaded.nodes);
          setConnections(loaded.connections);
          syncedNodeIdsRef.current = new Set(project.nodes.map(n => n.id));
          syncedConnectionIdsRef.current = new Set(project.connections.map(c => c.id));
          setTransform(project.transform);
          setHistory(loaded.history);
          setCurrentProjectId(projectId);
          setCurrentProjectName(loaded.name);
          if (!cloudSync) {
              // Saved on this device only, and read from there
              noteLoaded(projectId, loaded);
          } else if (project.source === 'cloud') {
              noteLoaded(projectId, loaded, { view: project.transform, history: loaded.history });
          } else {
              // The cloud could not be read and this is the device's own copy: what the cloud
              // holds is not known, so the next save writes everything. That save waits for a
              // change made here (the nodes as read are kept to tell one): opening is not an edit,
              // and a copy saved unasked would get a date that makes it pass for the newer one.
              // Should the cloud be heard first, its first update settles which copy is the newer
              // one, as for the folder's
              noteLoaded(projectId, { ...loaded, nodes: null }, undefined, { savedAt: project.lastSaved || null, pending: false, loaded: loaded.nodes });
          }
      } else {
          logger.warn('Storage', 'loadWorkspace: loadProject returned null!');
      }
  };

  const refreshProjectList = async () => {
      if (!user) return;
      const projects = await listProjects(user.sub, featureAccess.limits.cloudSyncEnabled);
      setProjectList(projects);
  };

  const handleSyncAllToCloud = async () => {
      if (!user) return;
      setIsCloudSyncing(true);
      try {
          const result = await syncAllLocalProjectsToCloud(user.sub);
          console.log(`[SyncAll] Done: ${result.synced} synced, ${result.failed} failed, ${result.skipped} skipped`);
          await refreshProjectList();
          if (result.synced > 0) {
              toast.success('Sync complete', `${result.synced} workspace${result.synced !== 1 ? 's' : ''} synced to cloud.`);
          } else if (result.failed > 0) {
              toast.error('Sync failed', `${result.failed} workspace${result.failed !== 1 ? 's' : ''} failed to sync. Check console for details.`);
          } else {
              toast.success('Already synced', 'All workspaces are already in the cloud.');
          }
      } catch (e) {
          console.error('[SyncAll] Error:', e);
          toast.error('Sync failed', 'Could not sync workspaces to cloud.');
      } finally {
          setIsCloudSyncing(false);
      }
  };

  const handleCreateProject = async () => {
      if (!user) return;
      const name = prompt("Enter workspace name:", "My New Workspace");
      if (!name) return;
      
      try {
          const newId = await createProject(user.sub, name, featureAccess.limits.cloudSyncEnabled);
          if (newId) {
              await refreshProjectList();
              await loadWorkspace(newId, featureAccess.limits.cloudSyncEnabled);
          } else {
              toast.error('Failed to create project', 'An unknown error occurred.');
          }
      } catch (e: any) {
          console.error(e);
          toast.error('Error creating project', e.message);
      }
  };

  // --- LOCAL WORKSPACE MANAGEMENT ---
  const refreshLocalWorkspaces = async () => {
      try {
          const workspaces = await listLocalWorkspaces(user?.sub);
          setLocalWorkspaces(workspaces);
      } catch (e) {
          console.error('Failed to refresh local workspaces:', e);
      }
  };

  const handleCreateLocalWorkspace = async () => {
      const name = prompt("Enter workspace name:", "My New Workspace");
      if (!name) return;
      
      try {
          const workspace = await createLocalWorkspace(name, user?.sub);
          await refreshLocalWorkspaces();
          // As a switch: the workspace that is open is saved first
          await handleSwitchWorkspace(workspace.id, true);
      } catch (e: any) {
          console.error(e);
          toast.error('Error creating workspace', e.message);
      }
  };

  const loadLocalWorkspaceById = async (workspaceId: string) => {
      setIsLoadingWorkspace(true);
      try {
          const workspace = await loadLocalWorkspace(workspaceId);
          if (workspace) {
              const loaded = { nodes: workspace.nodes || [], connections: workspace.connections || [], history: workspace.history || [], name: workspace.name };
              setNodes(loaded.nodes);
              setConnections(loaded.connections);
              setTransform(workspace.transform || { x: 0, y: 0, scale: 1 });
              setHistory(loaded.history);
              setCurrentLocalWorkspaceId(workspaceId);
              setCurrentProjectName(loaded.name);
              setIsLocalWorkspaceMode(true);
              noteLoaded(workspaceId, loaded);
              
              // Clear cloud project references when in local mode
              setCurrentProjectId(null);
              
              // Load localFileMap for offline image resolution
              if (workspace.localFileMap) {
                  setLocalFileMap(workspace.localFileMap);
              }
              
              logger.debug('Storage', '✅ Loaded local workspace:', workspace.name);
          }
      } catch (e) {
          console.error('Failed to load local workspace:', e);
      } finally {
          setIsLoadingWorkspace(false);
      }
  };

  const handleRenameLocalWorkspace = async () => {
      if (!currentLocalWorkspaceId) return;
      const newName = prompt("Enter new workspace name:", currentProjectName);
      if (newName && newName !== currentProjectName) {
          try {
              await renameLocalWorkspace(currentLocalWorkspaceId, newName);
              setCurrentProjectName(newName);
              await refreshLocalWorkspaces();
          } catch (e: any) {
              console.error(e);
              toast.error('Error renaming workspace', e.message);
          }
      }
  };

  const handleDeleteLocalWorkspace = async (workspaceId: string) => {
      if (!confirm("Are you sure you want to delete this workspace? This cannot be undone.")) return;
      
      try {
          await deleteLocalWorkspace(workspaceId);
          await refreshLocalWorkspaces();
          
          // If we deleted the current workspace, load another one or create a new one
          if (workspaceId === currentLocalWorkspaceId) {
              const remaining = await listLocalWorkspaces(user?.sub);
              if (remaining.length > 0) {
                  await loadLocalWorkspaceById(remaining[0].id);
              } else {
                  // Create a new default workspace
                  const newWorkspace = await createLocalWorkspace('Untitled Workspace', user?.sub);
                  await refreshLocalWorkspaces();
                  await loadLocalWorkspaceById(newWorkspace.id);
              }
          }
      } catch (e: any) {
          console.error(e);
          toast.error('Error deleting workspace', e.message);
      }
  };

  // --- LANDING PAGE HANDLERS ---
  
  /**
   * Loads everything the Workspaces library shows (cloud projects, local-store
   * workspaces, last-opened timestamps, project/folder tree) and derives the
   * `recentFiles` list the Analytics page still consumes. Each read is guarded
   * so one failure does not block the others.
   */
  const loadRecentFiles = async () => {
    const uid = user?.sub ?? null;
    const cloudSync = featureAccess.limits.cloudSyncEnabled;
    try {
      logger.debug('Storage', '🏠 Loading workspace library...');

      const [projectsResult, localsResult, lastOpenedResult, foldersResult, sharedFoldersResult, invitationsResult, copiesResult] = await Promise.allSettled([
        uid ? listProjects(uid, cloudSync) : Promise.resolve([] as ProjectMetadata[]),
        listLocalWorkspaces(uid ?? undefined),
        getLastOpenedMap(uid ?? undefined),
        uid ? listWorkspaceFolders(uid, cloudSync) : Promise.resolve([] as WorkspaceFolder[]),
        uid && cloudSync ? listSharedLibraryFolders(uid) : Promise.resolve([] as WorkspaceFolder[]),
        uid && cloudSync ? listMyProjectInvitations() : Promise.resolve([] as ProjectInvitation[]),
        uid && cloudSync ? listProjectCopies() : Promise.resolve({ sent: [], received: [] } as ProjectCopies),
      ]);

      // The account changed while we were loading: let the new account's load win.
      if (libraryUidRef.current !== uid) return;

      // On failure keep the previous list rather than wiping it (projectList also
      // drives the shared/view-only flags of the open workspace).
      let projects = projectList;
      if (projectsResult.status === 'fulfilled') {
        projects = projectsResult.value;
        setProjectList(projects);
      } else {
        console.warn('[library] Failed to load cloud projects, keeping previous list:', projectsResult.reason);
      }

      let locals = localWorkspaces;
      if (localsResult.status === 'fulfilled') {
        locals = localsResult.value;
        setLocalWorkspaces(locals);
      } else {
        console.warn('[library] Failed to load local workspaces, keeping previous list:', localsResult.reason);
      }

      const lastOpened = lastOpenedResult.status === 'fulfilled' ? lastOpenedResult.value : new Map<string, number>();
      if (lastOpenedResult.status === 'rejected') {
        console.warn('[library] Failed to read last-opened timestamps:', lastOpenedResult.reason);
      }

      if (foldersResult.status === 'fulfilled') {
        setFolders(foldersResult.value);
        // This person's shared projects are brought in step now and then, in case something was
        // filed into one where the app could not do it at once (a save that reached the cloud later)
        const now = Date.now();
        for (const f of foldersResult.value) {
          if (f.kind !== 'project' || !(f.sharedUserIds?.length)) continue;
          if (now - (projectSyncedAt.current.get(f.id) ?? 0) < PROJECT_SYNC_EVERY_MS) continue;
          projectSyncedAt.current.set(f.id, now);
          void syncLibraryProject(f.id);
        }
      } else {
        console.warn('[library] Failed to load folders, keeping previous list:', foldersResult.reason);
      }

      if (sharedFoldersResult.status === 'fulfilled') setSharedFolders(sharedFoldersResult.value);
      if (invitationsResult.status === 'fulfilled') setProjectInvitations(invitationsResult.value);
      if (copiesResult.status === 'fulfilled') setProjectCopies(copiesResult.value);

      // Never let a failed read look like an empty library: surface it on the page.
      const failure = [projectsResult, localsResult, foldersResult].find(r => r.status === 'rejected') as PromiseRejectedResult | undefined;
      if (failure) {
        const reason = failure.reason;
        const detail = reason instanceof Error ? reason.message : (reason && typeof reason === 'object' && 'name' in reason ? String((reason as { name: unknown }).name) : String(reason));
        setLibraryError(detail || 'Unknown error');
      } else {
        setLibraryError(null);
      }

      const library = buildLibraryWorkspaces(projects, locals, lastOpened);
      setLibraryWorkspaces(library);

      // Recent files for the Analytics page: top 10 by lastOpened ?? lastSaved, local + cloud.
      const files: RecentFileMetadata[] = [...library]
        .sort((a, b) => (b.lastOpened ?? b.lastSaved) - (a.lastOpened ?? a.lastSaved))
        .slice(0, 10)
        .map(w => ({
          id: w.id,
          name: w.name,
          lastSaved: w.lastSaved,
          lastOpened: w.lastOpened,
          createdAt: w.createdAt,
          nodeCount: w.nodeCount,
          thumbnail: w.thumbnail,
          isLocal: w.isLocal,
          isFavorite: w.isFavorite,
          ownerId: w.ownerId,
          isShared: w.isShared,
          sharedPermission: w.sharedPermission,
          sharedBy: w.sharedBy,
          folderId: w.folderId,
        }));
      logger.debug('Storage', `🏠 Library loaded: ${library.length} workspaces, ${files.length} recent`);
      setRecentFiles(files);
    } catch (error) {
      console.error('Failed to load workspace library:', error);
      setLibraryError(error instanceof Error ? error.message : String(error));
      setRecentFiles([]);
    } finally {
      if (libraryUidRef.current === uid) setLibraryLoaded(true);
    }
  };

  /** `prefetched`: the workspace's contents, already being read (a switch starts reading at once). */
  const handleOpenWorkspace = async (workspaceId: string, isLocal?: boolean, prefetched?: Promise<ReadWorkspace>) => {
    setShowLandingPage(false);
    setIsLoadingWorkspace(true);

    try {
      // Switched away from moments ago: read it back only once its save is in
      await pendingSavesRef.current.waitFor(workspaceId);
      if (isLocal) {
        await loadLocalWorkspaceById(workspaceId);
      } else {
        // Leave local-workspace mode before loading a cloud workspace, otherwise
        // presence stays off and a later save could target the old local record.
        setIsLocalWorkspaceMode(false);
        setCurrentLocalWorkspaceId(null);
        await loadWorkspace(workspaceId, featureAccess.limits.cloudSyncEnabled, prefetched);
      }

      // Record the open for this account on this device; the library reloads on the way back.
      // The canvas does not wait for it (it reports its own failures and never rejects).
      void updateLastOpened(workspaceId, isLocal, undefined, user?.sub);
    } catch (error) {
      console.error('Failed to open workspace:', error);
      toast.error('Failed to open workspace', 'Please try again.');
      setShowLandingPage(true);
    } finally {
      setIsLoadingWorkspace(false);
    }
  };

  /** Creates a cloud workspace filed under `folderId` (null = unfiled) and opens it. */
  const handleCreateWorkspaceInFolder = async (name: string, folderId: string | null) => {
    if (!user) throw new Error('Sign in to create a workspace');
    setShowLandingPage(false);
    setIsLoadingWorkspace(true);
    setIsLocalWorkspaceMode(false);
    setCurrentLocalWorkspaceId(null);

    try {
      const newId = await createProject(user.sub, name, featureAccess.limits.cloudSyncEnabled, folderId);
      if (!newId) throw new Error('Workspace could not be created');
      syncSharedProjectsFor([folderId]);
      await refreshProjectList();
      await loadWorkspace(newId, featureAccess.limits.cloudSyncEnabled);
      await updateLastOpened(newId, false, undefined, user.sub);
      // Refresh the library lists in the background so the canvas project panel shows the new workspace
      void loadRecentFiles();
    } catch (error) {
      console.error('Failed to create workspace:', error);
      toast.error('Failed to create workspace', 'Please try again.');
      setShowLandingPage(true);
      throw error;
    } finally {
      setIsLoadingWorkspace(false);
    }
  };

  /**
   * After this person moved or made something in their library: each of their shared projects
   * it touched is brought in step, so the project's people see it, or stop seeing it
   * (netlify/functions/utils/projectSharing.ts). Folder ids are looked up in `list`, the
   * library as it was before the change.
   */
  const syncSharedProjectsFor = (folderIds: Array<string | null | undefined>, list: WorkspaceFolder[] = folders) => {
    const projects = new Set<string>();
    for (const id of folderIds) {
      const project = id ? getFolderPath(list, id)[0] : undefined;
      if (project?.kind === 'project' && (project.sharedUserIds?.length ?? 0) > 0) projects.add(project.id);
    }
    for (const id of projects) {
      projectSyncedAt.current.set(id, Date.now());
      void syncLibraryProject(id).then(result => {
        if (result.error) console.warn('[sharing] The shared project could not be brought up to date:', result.error);
      });
    }
  };

  const handleImportFile = () => {
    // The library stays visible until a file is actually chosen (the input's
    // onChange hides it), so cancelling the picker leaves the user where they were.
    fileInputRef.current?.click();
  };

  // --- WORKSPACES LIBRARY HANDLERS ---
  // Every handler resolves once storage and the lists are up to date and rejects
  // with an Error on failure; the page owns the toasts/dialogs for these.

  /**
   * After a workspace is deleted (or access to it is lost) make sure it is no
   * longer the open one, otherwise the autosave effects would recreate it.
   */
  const forgetOpenWorkspaceIfDeleted = (workspaceId: string) => {
    const wasCloud = workspaceId === currentProjectId;
    const wasLocal = workspaceId === currentLocalWorkspaceId;
    if (!wasCloud && !wasLocal) return;
    if (wasCloud) setCurrentProjectId(null);
    if (wasLocal) setCurrentLocalWorkspaceId(null);
    setNodes([]);
    setConnections([]);
    setHistory([]);
  };

  const handleRenameWorkspaceFromLibrary = async (ref: WorkspaceRef, name: string) => {
    const trimmed = name.trim();
    if (!trimmed) throw new Error('Name is required');
    try {
      if (ref.isLocal) {
        await renameLocalWorkspace(ref.id, trimmed);
      } else {
        await updateWorkspaceMeta(ref.id, false, { name: trimmed }, featureAccess.limits.cloudSyncEnabled);
      }
      if (ref.id === currentProjectId || ref.id === currentLocalWorkspaceId) {
        setCurrentProjectName(trimmed);
      }
      await loadRecentFiles();
    } catch (error) {
      console.error('Failed to rename workspace:', error);
      throw error;
    }
  };

  const handleMoveWorkspacesFromLibrary = async (refs: WorkspaceRef[], folderId: string | null) => {
    const from = refs.map(ref => libraryWorkspaces.find(w => w.id === ref.id)?.folderId ?? null);
    let failed = 0;
    for (const ref of refs) {
      try {
        // Filing a legacy ownerless local workspace also stamps it with this account.
        const patch = ref.isLocal && user ? { folderId, ownerId: user.sub } : { folderId };
        await updateWorkspaceMeta(ref.id, ref.isLocal, patch, featureAccess.limits.cloudSyncEnabled);
      } catch (error) {
        failed++;
        console.error('Failed to move workspace:', ref.id, error);
      }
    }
    syncSharedProjectsFor([...from, folderId]);
    await loadRecentFiles();
    if (failed > 0) throw new Error(`${failed} of ${refs.length} could not be moved`);
  };

  const handleToggleFavoriteFromLibrary = async (ref: WorkspaceRef, favorite: boolean) => {
    try {
      const patch = ref.isLocal && user ? { isFavorite: favorite, ownerId: user.sub } : { isFavorite: favorite };
      await updateWorkspaceMeta(ref.id, ref.isLocal, patch, featureAccess.limits.cloudSyncEnabled);
      await loadRecentFiles();
    } catch (error) {
      console.error('Failed to toggle favorite:', error);
      throw error;
    }
  };

  const handleDeleteWorkspaceFromLibrary = async (ref: WorkspaceRef) => {
    try {
      if (ref.isLocal) {
        await deleteLocalWorkspace(ref.id);
      } else {
        await deleteProject(ref.id, featureAccess.limits.cloudSyncEnabled);
      }
      await clearLastOpened(ref.id);
      forgetOpenWorkspaceIfDeleted(ref.id);
      await loadRecentFiles();
    } catch (error) {
      console.error('Failed to delete workspace:', error);
      throw error;
    }
  };

  /** Leaves a workspace shared with the user. The page shows its own confirm dialog. */
  const handleLeaveSharedWorkspaceFromLibrary = async (projectId: string) => {
    if (!user) throw new Error('Not signed in');
    let result: { success: boolean; error?: string };
    try {
      result = await unshareProject(projectId, { userId: user.sub });
    } catch (error) {
      console.error('Failed to leave shared workspace:', error);
      throw new Error('Could not leave shared workspace');
    }
    if (!result.success) throw new Error(result.error || 'Could not leave shared workspace');
    // If it was the open workspace, drop it so autosave never writes to a project we no longer have access to.
    forgetOpenWorkspaceIfDeleted(projectId);
    await loadRecentFiles();
  };

  /** Leaves a library project shared with the user, and with it all of its workspaces. The page shows its own confirm dialog. */
  const handleLeaveSharedLibraryProject = async (projectId: string) => {
    if (!user) throw new Error('Not signed in');
    const result = await unshareLibraryProject(projectId, { userId: user.sub });
    if (!result.success) throw new Error(result.error || 'Could not leave the project');
    // Its workspaces are no longer this person's to save: the open one is dropped if it was one of them
    for (const w of libraryWorkspaces) if (w.sharedProjectId === projectId) forgetOpenWorkspaceIfDeleted(w.id);
    await loadRecentFiles();
  };

  /** Accepts or declines an invitation to a project; resolves once the library shows the answer */
  const handleAnswerProjectInvitation = async (invitationId: string, accept: boolean) => {
    const result = await answerProjectInvitation(invitationId, accept);
    if (!result.success) throw new Error(result.error || 'The invitation could not be answered');
    setProjectInvitations(prev => prev.filter(inv => inv.id !== invitationId));
    await loadRecentFiles();
  };

  /** Sends a copy of a project to an address; the sent list shows it at once */
  const handleSendProjectCopy = async (projectId: string, email: string): Promise<ProjectCopy> => {
    const copy = await sendProjectCopy(projectId, email);
    setProjectCopies(prev => ({ ...prev, sent: [copy, ...prev.sent.filter(c => c.id !== copy.id)] }));
    return copy;
  };

  /** Accepts a copy of a project (made on the server, slice by slice, onProgress told each time) or declines it; the library shows the result */
  const handleAnswerProjectCopy = async (copyId: string, accept: boolean, onProgress?: (copy: ProjectCopy) => void): Promise<ProjectCopy | null> => {
    const result = accept ? await acceptProjectCopy(copyId, onProgress) : await declineProjectCopy(copyId);
    setProjectCopies(prev => ({ ...prev, received: prev.received.filter(c => c.id !== copyId) }));
    if (accept) await loadRecentFiles();
    return result;
  };

  /** The sender takes back a copy nobody has accepted yet */
  const handleCancelProjectCopy = async (copyId: string) => {
    const copy = await cancelProjectCopy(copyId);
    setProjectCopies(prev => ({ ...prev, sent: prev.sent.map(c => (c.id === copyId ? copy : c)) }));
  };

  const handleCreateFolder = async (input: CreateFolderInput): Promise<WorkspaceFolder> => {
    if (!user) throw new Error('Not signed in');
    const folder = await createWorkspaceFolder(user.sub, input, featureAccess.limits.cloudSyncEnabled);
    syncSharedProjectsFor([input.parentId]);
    await loadRecentFiles();
    return folder;
  };

  /** "New folder" or "New project" in the canvas's library panel: made, shown in its outline, or said why not */
  const handleCreateFolderFromPanel = async (name: string, kind: FolderKind, parentId: string | null): Promise<WorkspaceFolder | null> => {
    try {
      const unique = makeUniqueSiblingName(name, folders.filter(f => (f.parentId ?? null) === parentId).map(f => f.name));
      const folder = await handleCreateFolder({ name: unique, kind, parentId });
      // The outline opens down to it
      if (parentId) setProjectPanelExpanded(prev => { const next = new Set(prev); getFolderPath(folders, parentId).forEach(f => next.add(f.id)); return next; });
      return folder;
    } catch (error) {
      toast.error(kind === 'project' ? 'Could not create the project' : 'Could not create the folder', error instanceof Error ? error.message : 'Please try again.');
      return null;
    }
  };

  const handleRenameFolder = async (id: string, name: string) => {
    if (!user) throw new Error('Not signed in');
    await updateWorkspaceFolder(user.sub, id, { name }, featureAccess.limits.cloudSyncEnabled);
    await loadRecentFiles();
  };

  const handleRecolorFolder = async (id: string, color: FolderColor) => {
    if (!user) throw new Error('Not signed in');
    await updateWorkspaceFolder(user.sub, id, { color }, featureAccess.limits.cloudSyncEnabled);
    await loadRecentFiles();
  };

  const handleMoveFolder = async (id: string, parentId: string) => {
    if (!user) throw new Error('Not signed in');
    const fromParent = getFolderById(folders, id)?.parentId ?? null;
    await updateWorkspaceFolder(user.sub, id, { parentId }, featureAccess.limits.cloudSyncEnabled);
    syncSharedProjectsFor([fromParent, parentId]);
    await loadRecentFiles();
  };

  /**
   * Deletes a project/folder and its whole subtree. mode 'keep' moves the
   * workspaces inside to the deleted folder's parent; 'all' deletes them too.
   */
  const handleDeleteFolder = async (
    id: string,
    mode: DeleteFolderMode,
    onProgress?: (progress: DeleteFolderProgress) => void,
  ): Promise<DeleteFolderResult> => {
    if (!user) throw new Error('Not signed in');
    const cloudSync = featureAccess.limits.cloudSyncEnabled;
    const scopeIds = [id, ...getDescendantFolderIds(folders, id)];
    const items = collectWorkspaceIdsInFolder(folders, libraryWorkspaces, id);
    // Kept workspaces go to the parent, or to "Not in a project" when the parent is gone too.
    const parentId = getFolderById(folders, id)?.parentId ?? null;
    const target = parentId && getFolderById(folders, parentId) ? parentId : null;
    const total = items.length;
    let done = 0;
    let failed = 0;

    for (const item of items) {
      try {
        if (mode === 'keep') {
          await updateWorkspaceMeta(item.id, item.isLocal, { folderId: target }, cloudSync);
        } else {
          if (item.isLocal) {
            await deleteLocalWorkspace(item.id);
          } else {
            await deleteProject(item.id, cloudSync);
          }
          await clearLastOpened(item.id);
          forgetOpenWorkspaceIfDeleted(item.id);
        }
      } catch (error) {
        failed++;
        console.error(`Failed to ${mode === 'keep' ? 'move' : 'delete'} workspace while deleting folder:`, item.id, error);
      }
      done++;
      onProgress?.({ done, total });
    }

    await deleteWorkspaceFolders(user.sub, scopeIds, cloudSync);
    // A deleted shared project's people lose its workspaces; a deleted folder's are still in its project
    syncSharedProjectsFor([id]);
    await loadRecentFiles();
    return { deletedFolders: scopeIds.length, affectedWorkspaces: total - failed, failedWorkspaces: failed };
  };

  const handleRefreshLibrary = loadRecentFiles;

  // ── Canvas project panel derived state ────────────────────────────────────
  const openWorkspaceId = currentLocalWorkspaceId ?? currentProjectId;
  const openLibraryWorkspace = useMemo(
    () => (openWorkspaceId ? libraryWorkspaces.find(w => w.id === openWorkspaceId) : undefined),
    [libraryWorkspaces, openWorkspaceId],
  );
  // The open workspace's project: one of this person's own, or (for a workspace shared through a project) the shared one
  const openWorkspaceProject = useMemo(
    () => (openLibraryWorkspace?.folderId ? getFolderPath(folders, openLibraryWorkspace.folderId)[0] ?? getFolderPath(sharedFolders, openLibraryWorkspace.folderId)[0] ?? null : null),
    [folders, sharedFolders, openLibraryWorkspace?.folderId],
  );
  const projectPanelContext = useMemo(() => {
    if (openWorkspaceProject) return { kind: 'project' as const, project: openWorkspaceProject };
    if (openLibraryWorkspace?.isShared) return { kind: 'shared' as const };
    return { kind: 'unfiled' as const };
  }, [openWorkspaceProject, openLibraryWorkspace?.isShared]);

  // Where "New workspace" in the panel creates: next to the open workspace, else at the project root.
  const projectPanelTargetFolderId = useMemo(() => {
    if (openLibraryWorkspace?.folderId && (folders.some(f => f.id === openLibraryWorkspace.folderId) || sharedFolders.some(f => f.id === openLibraryWorkspace.folderId))) return openLibraryWorkspace.folderId;
    return openWorkspaceProject?.id ?? null;
  }, [openLibraryWorkspace?.folderId, folders, sharedFolders, openWorkspaceProject?.id]);

  // When a different workspace opens, open the outline down to it (nothing else is "selected" in the canvas).
  useEffect(() => {
    if (!projectPanelTargetFolderId) return;
    setProjectPanelExpanded(prev => {
      const path = getFolderPath([...folders, ...sharedFolders], projectPanelTargetFolderId);
      // Already open down to it (the usual case when switching inside a project): nothing to render
      if (path.every(f => prev.has(f.id))) return prev;
      const next = new Set(prev);
      path.forEach(f => next.add(f.id));
      return next;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openWorkspaceId, projectPanelTargetFolderId, folders]);

  // Reference nodes are shared across a project: the other workspaces' ones are offered by link
  // (A workspace still being saved, as one just left is, is read once its save is done)
  const readReferenceNodes = useCallback(
    (workspace: { id: string; isLocal: boolean }) => pendingSavesRef.current.waitFor(workspace.id).catch(() => undefined).then(() => (workspace.isLocal
      ? loadLocalWorkspace(workspace.id).then(found => found?.nodes || [])
      : loadProjectReferenceNodes(workspace.id, featureAccess.limits.cloudSyncEnabled))),
    [featureAccess.limits.cloudSyncEnabled],
  );
  const referenceFolders = useMemo(() => [...folders, ...sharedFolders], [folders, sharedFolders]);
  const projectReferences = useProjectReferences({ openWorkspaceId, workspaces: libraryWorkspaces, folders: referenceFolders, read: readReferenceNodes });
  const referenceSharing = useMemo<ReferenceSharing | undefined>(() => (projectReferences.scope ? {
    projectName: projectReferences.scope.projectName,
    loading: projectReferences.loading,
    readWorkspaceIds: projectReferences.readIds,
    refresh: projectReferences.refresh,
  } : undefined), [projectReferences.scope, projectReferences.loading, projectReferences.readIds, projectReferences.refresh]);

  // The outline shows the open workspace under its live name (renames in the canvas apply at once).
  const projectPanelWorkspaces = useMemo(
    () => libraryWorkspaces.map(w => (w.id === openWorkspaceId && currentProjectName ? { ...w, name: currentProjectName } : w)),
    [libraryWorkspaces, openWorkspaceId, currentProjectName],
  );
  // The panel also shows what the open workspace has cost so far, as its History says now. Kept
  // apart from the list above, which the local folder follows, so a generation moves nothing there
  const openWorkspaceCost = useMemo(() => historyTotalCost(history), [history]);
  const panelWorkspaces = useMemo(
    () => projectPanelWorkspaces.map(w => (w.id === openWorkspaceId ? { ...w, totalCost: openWorkspaceCost } : w)),
    [projectPanelWorkspaces, openWorkspaceId, openWorkspaceCost],
  );

  // The local folder follows the library: each workspace's folder under its project and folders
  // (utils/localPlaces.ts). New files go there at once; the desktop app also moves the folders that
  // are elsewhere (made before, or left behind by a rename or a move here), a moment after a change.
  const localPlaces = useMemo(() => placesFromLibrary(projectPanelWorkspaces, folders), [projectPanelWorkspaces, folders]);
  useEffect(() => { setWorkspacePlaces(localPlaces, openWorkspaceId); }, [localPlaces, openWorkspaceId]);
  useEffect(() => {
    const userId = user?.sub;
    if (!userId || localPlaces.length === 0) return;
    const placed = new Set(localPlaces.map(place => place.id));
    const unplacedNames = projectPanelWorkspaces.filter(w => !placed.has(w.id)).map(w => w.name);
    // A folder Windows would not let go of (a file in it open: the workspace loading at start-up,
    // a picture open in another program) is tried again every half minute for a while
    let timer = 0;
    let retries = 0;
    const organise = () => {
      organiseLocalFolder(localPlaces, unplacedNames, userId).then(
        result => {
          if (result.moved.length > 0) logger.info('Storage', `📁 Moved ${result.moved.length} workspace folder(s) to their projects`);
          if (result.failed > 0 && retries++ < 20) timer = window.setTimeout(organise, 30_000);
        },
        error => console.warn('[local folder] could not organise:', error),
      );
    };
    timer = window.setTimeout(organise, 1500);
    return () => window.clearTimeout(timer);
  }, [localPlaces, projectPanelWorkspaces, user?.sub]);

  const toggleProjectPanel = (open: boolean) => {
    setIsProjectPanelOpen(open);
    try { localStorage.setItem('ns-canvas-project-panel', open ? '1' : '0'); } catch { /* ignore */ }
  };

  // The one-time tip under the Library button: shown until it is put away, or the library opened
  const [libraryTipSeen, setLibraryTipSeen] = useState<boolean>(() => {
    try { return localStorage.getItem('ns-library-tip') === '1'; } catch { return true; }
  });
  const dismissLibraryTip = () => {
    setLibraryTipSeen(true);
    try { localStorage.setItem('ns-library-tip', '1'); } catch { /* ignore */ }
  };

  // The caret on the workspace's name: the workspaces beside this one, the folders next to it,
  // the whole library, and a new workspace here, without leaving the canvas
  const workspaceSwitcherItems = useMemo<MenuEntry[]>(() => {
    // Inside someone else's project nothing is made next to the open workspace: a new one goes outside any project
    const here = openWorkspaceProject?.isShared ? null : projectPanelTargetFolderId;
    const beside = panelWorkspaces
      .filter(w => w.id !== openWorkspaceId && !w.isShared && (here ? w.folderId === here : !w.folderId || !folders.some(f => f.id === w.folderId)))
      .sort((a, b) => b.lastSaved - a.lastSaved)
      .slice(0, 8);
    const items: MenuEntry[] = beside.map(w => ({
      id: w.id, label: w.name, icon: <WorkspaceThumb thumbnail={w.thumbnail} px={16} />,
      onSelect: () => { void handleSwitchWorkspace(w.id, w.isLocal); },
    }));
    for (const f of (here ? folders.filter(f => f.parentId === here) : []).slice(0, 5)) {
      items.push({
        id: `folder:${f.id}`, label: f.name, icon: <FolderIcon className="w-4 h-4" />,
        onSelect: () => { setProjectPanelExpanded(prev => { const next = new Set(prev); next.add(f.id); return next; }); toggleProjectPanel(true); },
      });
    }
    if (items.length) items.push('separator');
    items.push({ id: 'browse', label: 'Browse the whole library', icon: <PanelLeft className="w-4 h-4" />, onSelect: () => toggleProjectPanel(true) });
    if (!isViewOnly) {
      items.push({
        id: 'new', label: openWorkspaceProject && !openWorkspaceProject.isShared ? `New workspace in ${openWorkspaceProject.name}` : 'New workspace', icon: <Plus className="w-4 h-4" />,
        onSelect: () => { void handleCreateWorkspaceInFolder('Untitled', here).catch(() => {}); },
      });
    }
    return items;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [panelWorkspaces, openWorkspaceId, projectPanelTargetFolderId, folders, openWorkspaceProject, isViewOnly]);

  /**
   * How far the app's own panels reach over the canvas: a workspace's thumbnail shows the part
   * they leave free. The header, the library panel with the tool rail beside it, the storage
   * panel, and the page switch at the bottom.
   */
  const coveredCanvasEdges = () => ({
    top: 72,
    left: (user && isProjectPanelOpen ? 316 : 16) + (isViewOnly ? 0 : 60),
    right: user && (isStoragePanelOpen || commentNodeId) ? 380 : 0,
    bottom: 64,
  });

  /** A workspace is being left and its leave-save covers every unsaved change: an auto-save still waiting has nothing left to do. */
  const cancelPendingAutoSave = () => {
    autoSavesRef.current.cancelWaiting();
  };

  /**
   * For when the window is about to close or reload: saves at once what an auto-save is still
   * waiting to save, and resolves when no save is under way any more (that one, an auto-save
   * already started, the save of a workspace just left). It reads refs only, so the copy of it
   * that any render made does the same.
   */
  const flushSaves = async (): Promise<void> => {
    await autoSavesRef.current.flush();
    await pendingSavesRef.current.waitForAll();
  };

  /**
   * Saves the cloud workspace that is being left, from the lists as they are now: the
   * thumbnail, the workspace's details and the nodes changed since they were saved (all of
   * them when that is not known), to the cloud and to this device's copy. Where workspaces are
   * also kept in the local folder, the folder's file is written from the same lists once the
   * cloud has answered: that file is what opens the workspace again, and it says whether the
   * cloud took the save. Rejects when changes of the workspace did not get through.
   *
   * What it needs of the open workspace (what is noted as saved, the marks, the folder's file
   * map) is read at once, before the next workspace replaces it.
   */
  const saveCloudWorkspaceOnLeaving = async (
    id: string,
    leaving: { name: string; nodes: CanvasNode[]; connections: Connection[]; history: (HistoryItem | LightHistoryItem)[]; transform: ViewportTransform },
    thumbnail: string | undefined,
  ): Promise<void> => {
    const userId = user!.sub;
    const note = savedNoteOf(id);
    const marked = [...dirtyNodeIdsRef.current];
    const alsoChanged = [...marked, ...savingNodeIdsRef.current];
    const changed = unsavedNodeIds(note?.saved.nodes ?? null, leaving.nodes, alsoChanged);
    // Whether anything of the workspace itself is still to be saved (its view and its thumbnail
    // do not count, nor does a copy read from this device that is merely not known to be in the cloud)
    const carriesChanges = changedHere(note?.saved ?? null, note?.localCopy, leaving, alsoChanged);
    const wasPending = !!note?.localCopy?.pending;
    const folderFiles = workspaceAutoSave && autoSaveFolderName ? getLocalFileMap() : null;
    // An auto-save still under way holds an older state of the canvas: it leaves the folder's file to this save
    leaveCountRef.current++;
    // This save covers the unsaved changes, so they are not carried into the next workspace
    dirtyNodeIdsRef.current.clear();
    // The canvas holds a copy read from this device and the cloud has not been heard yet: with
    // nothing changed here there is nothing to save, and the details this copy has (connections,
    // history) may be older than the cloud's
    if (note?.localCopy && !carriesChanges) {
      forgetThumbnail(id);
      return;
    }

    let result: Awaited<ReturnType<typeof saveProject>> | null = null;
    let failure: { error: unknown } | null = null;
    try {
      // (a save that carries nothing must not make this device's copy look newer than it is, should the cloud not take it)
      result = await saveProject(leaving.nodes, leaving.connections, leaving.history, leaving.transform, userId, id, leaving.name, featureAccess.limits.cloudSyncEnabled, thumbnail, changed,
        { ...listsLeftToCloud(note?.localCopy, note?.saved, note?.cloudHistory, leaving, changed === null), keepLocalDate: !carriesChanges });
    } catch (error) {
      failure = { error };
    }
    const cloud = result ? result.cloud : 'failed';
    if (cloud === 'failed') forgetThumbnail(id);
    // Still the workspace on the canvas (the other one could not be opened): what the cloud did not take is not noted as saved
    if (savedNoteOf(id)) (result ? marksAfterSave(result, marked) : marked).forEach(nodeId => dirtyNodeIdsRef.current.add(nodeId));
    // A save that carried nothing leaves the file as it is: its date says when the workspace last changed here
    if (folderFiles && carriesChanges) {
      try {
        await saveWorkspaceLocally(leaving.name, {
          nodes: leaving.nodes,
          connections: leaving.connections,
          history: leaving.history,
          transform: leaving.transform,
          projectId: id,
          projectName: leaving.name,
          cloudPending: cloudPendingAfterSave(cloud, carriesChanges, wasPending),
        }, folderFiles, userId);
      } catch (folderError) {
        console.warn('Local workspace save failed:', folderError);
      }
    }
    if (failure) throw failure.error;
    // A save the cloud did not take is reported like one that failed outright, when changes were lost to the cloud with it
    if (result && carriesChanges) failIfCloudFailed(result);
  };

  // Handler to go back to the landing page
  const handleGoHome = async () => {
    // A second click while leaving does nothing (the loading screen may be a moment in coming, see below)
    if (switchingRef.current) return;
    switchingRef.current = true;
    try {
      await leaveForHome();
    } finally {
      switchingRef.current = false;
    }
  };
  const leaveForHome = async () => {
    const container = canvasRef.current?.containerRef.current || null;
    const covered = coveredCanvasEdges();
    // (a workspace shared for viewing only is not saved, and so not pictured: it may not be written to)
    const leavingId = user && currentProjectId ? (isViewOnly ? null : currentProjectId) : currentLocalWorkspaceId;
    // Its picture in the library: its newest generated picture, from this device or (without a local
    // folder, with more time allowed) from the cloud (utils/workspaceCover.ts), else a picture of the
    // canvas. Both are begun at once
    const cover = leavingId ? workspaceCover(nodesRef.current, coverMediaOptions(), workspaceAutoSave ? COVER_WAIT_MS : COVER_WAIT_CLOUD_MS) : null;
    // In the desktop app the shell takes the canvas's picture, which it has to do before the
    // loading screen covers it (a short wait at most); in a browser there is nothing to wait for
    const shell = leavingId ? pictureBeforeCover(container, covered) : null;
    const pictured = shell ? await shell : undefined;

    // Show skeleton loader immediately while saving in background
    setIsNavigatingHome(true);
    // On screen before the picture is drawn here (a few milliseconds; the canvas stays under the loading screen)
    if (leavingId && !pictured) await afterPaint();
    const knownThumbnail = leavingId ? libraryWorkspaces.find(w => w.id === leavingId)?.thumbnail : undefined;
    const coverPicture = cover ? await cover : undefined;

    // Auto-save current workspace before leaving (with thumbnail)
    if (user && currentProjectId && !isViewOnly) {
      try {
        const thumbnail = thumbnailToSave(currentProjectId, generateThumbnail(container, currentProjectName, coverPicture ?? pictured, covered), knownThumbnail);
        logger.debug('Thumbnail', '🏠 GoHome: Generated cloud thumbnail:', { hasThumbnail: !!thumbnail, size: thumbnail?.length });
        cancelPendingAutoSave();
        // (the lists as they are now: a cloud update may have come in while the picture was taken)
        await autoSavesRef.current.whileSaving(saveCloudWorkspaceOnLeaving(currentProjectId, {
          name: currentProjectName,
          nodes: nodesRef.current,
          connections: connectionsRef.current,
          history: historyRef.current,
          transform: transformRef.current,
        }, thumbnail));
      } catch (err) {
        forgetThumbnail(currentProjectId);
        console.warn('Failed to save before going home:', err);
        toast.error(`"${currentProjectName}" may not be fully saved`, 'Saving it before going to the library failed. Open it again to check your latest changes.');
      }
    } else if (currentLocalWorkspaceId && !(user && currentProjectId)) {
      try {
        const thumbnail = thumbnailToSave(currentLocalWorkspaceId, generateThumbnail(container, currentProjectName, coverPicture ?? pictured, covered), knownThumbnail);
        logger.debug('Thumbnail', '🏠 GoHome: Generated local thumbnail:', { hasThumbnail: !!thumbnail, size: thumbnail?.length });
        cancelPendingAutoSave();
        await autoSavesRef.current.whileSaving(saveLocalWorkspace(currentLocalWorkspaceId, {
          name: currentProjectName,
          nodes: nodesRef.current,
          connections: connectionsRef.current,
          history: historyRef.current,
          transform: transformRef.current,
          thumbnail,
          ownerId: user?.sub,
        }));
      } catch (err) {
        forgetThumbnail(currentLocalWorkspaceId);
        console.warn('Failed to save local workspace before going home:', err);
      }
    }
    
    // Reset workspace state
    setCurrentProjectId(null);
    setCurrentLocalWorkspaceId(null);
    setNodes([]);
    setConnections([]);
    setHistory([]);
    setTransform({ x: 0, y: 0, scale: 1 });
    
    // One load refreshes the project list, local workspaces, folders and thumbnails
    await pendingSavesRef.current.waitForAll();
    await loadRecentFiles();
    setShowLandingPage(true);
    setIsNavigatingHome(false);
  };

  // Handler to switch between workspaces
  const handleSwitchWorkspace = async (workspaceId: string, isLocal?: boolean) => {
    // A second click while switching, or on the open workspace, does nothing
    if (switchingRef.current || workspaceId === (currentLocalWorkspaceId ?? currentProjectId)) return;
    switchingRef.current = true;
    const target = libraryWorkspaces.find(w => w.id === workspaceId);

    try {
      // The next workspace is read from the first moment, while this one is pictured and saved
      // (a cloud one; a local one reads at once)
      const prefetched = isLocal ? undefined : pendingSavesRef.current.waitFor(workspaceId)
        .then(() => readWorkspace(workspaceId, featureAccess.limits.cloudSyncEnabled));
      prefetched?.catch(() => undefined); // reported when it is opened
      // The workspace being left is saved, with a picture of its canvas, unless it may not be written to
      const openId = currentLocalWorkspaceId ?? currentProjectId;
      const leavingId = openId && (currentLocalWorkspaceId || (user && !isViewOnly)) ? openId : null;
      const container = canvasRef.current?.containerRef.current || null;
      const covered = coveredCanvasEdges();
      // Its picture in the library: its newest generated picture on this device, else the canvas's
      const cover = leavingId ? workspaceCover(nodesRef.current, coverMediaOptions(), workspaceAutoSave ? COVER_WAIT_MS : COVER_WAIT_CLOUD_MS) : null;
      // In the desktop app the shell takes the canvas's picture, which it has to do before the
      // loading screen covers it (a short wait at most); in a browser there is nothing to wait for
      const shell = leavingId ? pictureBeforeCover(container, covered) : null;
      const pictured = shell ? await shell : undefined;

      setSwitchingTo({ name: target?.name || 'workspace', step: 'saving' });
      await afterPaint();
      // What is being left, as it is now: it is saved behind the next workspace
      const leaving = {
        name: currentProjectName,
        nodes: nodesRef.current,
        connections: connectionsRef.current,
        history: historyRef.current,
        transform: transformRef.current,
      };
      if (leavingId) {
        // Without the shell's picture one is drawn here, from the canvas still mounted under the loading screen
        const coverPicture = cover ? await cover : undefined;
        const thumbnail = thumbnailToSave(leavingId, generateThumbnail(container, leaving.name, coverPicture ?? pictured, covered), libraryWorkspaces.find(w => w.id === leavingId)?.thumbnail);
        // A cloud workspace: only the nodes changed since they were saved (with the thumbnail and the
        // workspace's details), the whole workspace when that is not known; and the folder's file
        const save = currentLocalWorkspaceId
          ? saveLocalWorkspace(leavingId, { name: leaving.name, nodes: leaving.nodes, connections: leaving.connections, history: leaving.history, transform: leaving.transform, thumbnail, ownerId: user?.sub })
          : saveCloudWorkspaceOnLeaving(leavingId, leaving, thumbnail);
        cancelPendingAutoSave();
        pendingSavesRef.current.track(leavingId, save, (err) => {
          forgetThumbnail(leavingId);
          console.warn('Failed to save the workspace switched away from:', err);
          toast.error(`"${leaving.name}" may not be fully saved`, 'Saving it while switching failed. Open it again to check your latest changes.');
        });
      }

      setSwitchingTo({ name: target?.name || 'workspace', step: 'loading' });
      await handleOpenWorkspace(workspaceId, isLocal, prefetched);
    } finally {
      switchingRef.current = false;
      setSwitchingTo(null);
    }
  };

  // ── Ideas and tasks of a workspace ───────────────────────────────────────
  // (utils/ideas.ts, components/IdeasPage.tsx): written on the workspace's Ideas page, in the
  // cloud with the workspace, shared with the people the workspace is shared with.
  const ideaUser = useMemo<IdeaUser | null>(
    () => (user ? { id: user.sub, name: user.name || user.email || 'Someone', email: user.email || '', picture: user.picture || undefined } : null),
    [user],
  );
  const ideaProjectId = user && currentProjectId && !isLocalWorkspaceMode ? currentProjectId : null;
  // The people of the open workspace: its owner and everyone it is shared with. Read when the
  // workspace opens and again after the Share dialog closes (someone may have been added).
  const [workspacePeople, setWorkspacePeople] = useState<WorkspacePerson[]>([]);
  const peopleRound = useRef(0);
  const readPeople = useCallback(() => {
    if (!ideaProjectId || !ideaUser) { setWorkspacePeople([]); return; }
    const round = ++peopleRound.current;
    readWorkspacePeople(ideaProjectId, ideaUser, currentProjectMeta?.ownerName ?? currentProjectMeta?.sharedBy ?? null).then(
      (people) => { if (round === peopleRound.current) setWorkspacePeople(people); },
      (e) => { if (round === peopleRound.current) { console.warn('[ideas] people of the workspace not read:', e); setWorkspacePeople(ideaUser ? [{ id: ideaUser.id, name: ideaUser.name, email: ideaUser.email, permission: 'owner' }] : []); } },
    );
  }, [ideaProjectId, ideaUser, currentProjectMeta?.ownerName, currentProjectMeta?.sharedBy]);
  useEffect(() => { readPeople(); }, [readPeople]);
  useEffect(() => { if (!showShareModal) readPeople(); }, [showShareModal, readPeople]);

  /** The idea the Ideas page is asked to open: from a link, or just made from a node */
  const [askedIdeaId, setAskedIdeaId] = useState<string | null>(null);
  const clearAskedIdea = useCallback(() => setAskedIdeaId(null), []);

  /**
   * One person's reaction on a node, given or taken back: the same emojis as on Ideas cards, kept
   * in the node and saved with it. Only those who may change the workspace react.
   */
  // ── Reactions on nodes ───────────────────────────────────────────────────
  // (utils/nodeReactions.ts, utils/reactionStore.ts): kept in the cloud beside the comments, so
  // those who may only view the workspace react too; the ones kept in the nodes' data from before
  // are shown with them. A workspace on this device alone keeps them in the node's data as before.
  const [cloudReactions, setCloudReactions] = useState<NodeReaction[]>([]);
  const cloudReactionsRef = useRef(cloudReactions);
  cloudReactionsRef.current = cloudReactions;
  useEffect(() => {
    setCloudReactions([]);
    if (!ideaProjectId) return;
    let stop: (() => void) | undefined;
    try {
      stop = watchReactions(ideaProjectId, setCloudReactions, (message) => console.warn('[reactions]', message));
    } catch (e) {
      console.warn('[reactions]', e instanceof Error ? e.message : e);
    }
    return () => stop?.();
  }, [ideaProjectId]);
  // The reactions in the nodes' data, as a list that keeps its identity while none of them changes, so a drag re-renders no node for them
  const legacyReactionsRef = useRef<ReadonlyArray<readonly [string, NodeReactions | undefined]>>([]);
  const legacyReactions = useMemo(() => {
    const next = nodes.flatMap(node => (node.data.reactions ? [[node.id, node.data.reactions] as const] : []));
    const prev = legacyReactionsRef.current;
    if (prev.length === next.length && prev.every((pair, i) => pair[0] === next[i][0] && pair[1] === next[i][1])) return prev;
    legacyReactionsRef.current = next;
    return next;
  }, [nodes]);
  const reactionsForNode = useMemo(() => mergeNodeReactions(legacyReactions, cloudReactions), [legacyReactions, cloudReactions]);
  const handleReactToNode = useCallback((nodeId: string, emoji: string) => {
    if (!user) return;
    const legacyMine = !!nodesRef.current.find(node => node.id === nodeId)?.data.reactions?.[emoji]?.includes(user.sub);
    if (ideaProjectId) {
      const mine = cloudReactionsRef.current.some(r => r.nodeId === nodeId && r.userId === user.sub && r.emoji === emoji);
      const give = !mine && !legacyMine;
      (give ? giveReaction(ideaProjectId, nodeId, emoji, user.sub) : mine ? takeBackReaction(ideaProjectId, nodeId, emoji, user.sub) : Promise.resolve())
        .catch((e) => toast.error('Reaction', e instanceof Error ? e.message : 'The reaction did not go through.'));
      // One given before there was a cloud for them lives in the node's data: its editor takes it back there
      if (!legacyMine || isViewOnly) return;
    } else if (isViewOnly) return;
    dirtyNodeIdsRef.current.add(nodeId);
    setNodes(prev => prev.map(node => (node.id === nodeId
      ? { ...node, data: { ...node.data, reactions: toggleNodeReaction(node.data.reactions, emoji, user.sub) } }
      : node)));
  }, [ideaProjectId, isViewOnly, user, toast]);
  /** The people of the workspace by id, for who gave a reaction */
  const reactorNames = useMemo(() => Object.fromEntries(workspacePeople.map(person => [person.id, person.name])), [workspacePeople]);

  // ── Comments on nodes ────────────────────────────────────────────────────
  // (utils/nodeComments.ts, components/NodeCommentsPanel.tsx): a conversation about one node, in
  // the cloud with the workspace, for everyone it is shared with, heard as it is written.
  const [comments, setComments] = useState<NodeComment[]>([]);
  const [commentsLoading, setCommentsLoading] = useState(false);
  const [commentsProblem, setCommentsProblem] = useState<string | null>(null);
  /** The node whose comments are open in the panel at the right */
  const [commentNodeId, setCommentNodeId] = useState<string | null>(null);
  useEffect(() => {
    setComments([]);
    setCommentNodeId(null);
    setCommentsProblem(null);
    if (!ideaProjectId) { setCommentsLoading(false); return; }
    setCommentsLoading(true);
    let stop: (() => void) | undefined;
    try {
      stop = watchComments(
        ideaProjectId,
        (next) => { setComments(next); setCommentsLoading(false); setCommentsProblem(null); },
        (message) => { setCommentsLoading(false); setCommentsProblem(message); },
      );
    } catch (e) {
      setCommentsLoading(false);
      setCommentsProblem(e instanceof Error ? e.message : 'The comments could not be loaded.');
    }
    return () => stop?.();
  }, [ideaProjectId]);
  const commentCountByNode = useMemo(() => commentCounts(comments), [comments]);
  /** A node's comment button: its conversation opens at the right (the same node again closes it), and the node is selected */
  const handleOpenComments = useCallback((nodeId: string) => {
    setCommentNodeId(open => (open === nodeId ? null : nodeId));
    setIsStoragePanelOpen(false);
    setShowChangelogPanel(false);
    setIsGalleryOpen(false);
    setSelectedNodeId(nodeId);
  }, [setSelectedNodeId]);

  /** Go to a node: select it and bring it into view. */
  const goToNode = useCallback((nodeId: string) => {
    const node = nodesRef.current.find(n => n.id === nodeId);
    if (!node) return;
    setSelectedNodeId(nodeId);
    canvasRef.current?.panToNode?.(node.position.x, node.position.y, node.size?.width || 340, node.size?.height || 300);
  }, [setSelectedNodeId]);

  /** What a link asks for in a workspace: a node on the canvas, or an idea on the Ideas page */
  type LinkTarget = { projectId: string; nodeId?: string | null; view?: WorkspaceView | null; ideaId?: string | null };
  const showLinkTarget = useCallback((target: LinkTarget) => {
    if (target.view === 'ideas' || target.ideaId) {
      setAskedIdeaId(target.ideaId ?? null);
      setSelectedNodeId(null);
      setWorkspaceView('ideas');
    } else if (target.nodeId) {
      setWorkspaceView('nodes');
      goToNode(target.nodeId);
    }
  }, [goToNode, setSelectedNodeId]);

  /** The target to show once a workspace opened from a link has loaded (a notification's) */
  const linkTarget = useRef<LinkTarget | null>(null);
  useEffect(() => {
    const wanted = linkTarget.current;
    if (!wanted || wanted.projectId !== currentProjectId || isLoadingWorkspace) return;
    if (wanted.nodeId && nodes.length === 0) return; // the nodes are not in yet
    linkTarget.current = null;
    requestAnimationFrame(() => showLinkTarget(wanted));
  }, [currentProjectId, isLoadingWorkspace, nodes.length, showLinkTarget]);

  const linkTargetOf = (projectId: string, asked: URLSearchParams): LinkTarget => ({
    projectId, nodeId: asked.get('node'), view: asked.get('view') === 'ideas' || asked.get('view') === 'tasks' ? 'ideas' : null, ideaId: asked.get('idea'),
  });

  /** Open a workspace from a link (/workspaces?open=<id>&node=<id>, or &view=ideas&idea=<id>; links sent before the page was renamed say view=tasks): the library's, a notification's. */
  const openWorkspaceFromLink = async (target: LinkTarget) => {
    const openId = currentLocalWorkspaceId ?? currentProjectId;
    if (openId === target.projectId) { showLinkTarget(target); return; }
    linkTarget.current = target;
    if (!openId) { setShowLandingPage(false); await handleOpenWorkspace(target.projectId, false); return; }
    await handleSwitchWorkspace(target.projectId, false);
  };

  // A link the app was opened with: the library's address with a workspace to open. Read once,
  // at the start: the address is rewritten before the signed-in person is known, which drops
  // the query; what it asked for is kept until the library has loaded.
  const askedAtLoad = useRef<URLSearchParams | null>(
    window.location.pathname === '/workspaces' && new URLSearchParams(window.location.search).get('open') ? new URLSearchParams(window.location.search) : null,
  );
  useEffect(() => {
    const asked = askedAtLoad.current;
    if (!asked || !user || !libraryLoaded) return;
    askedAtLoad.current = null;
    void openWorkspaceFromLink(linkTargetOf(asked.get('open')!, asked));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, libraryLoaded]);

  /** The pages a link can name, each shown by its own flag (the address follows, see the URL sync above) */
  const PAGE_FLAGS: Record<string, (shown: boolean) => void> = {
    '/settings': setShowSettingsPage, '/subscription': setShowSubscriptionPage, '/analytics': setShowAnalyticsPage,
    '/about': setShowAboutPage, '/terms': setShowTermsPage, '/privacy': setShowPrivacyPage,
    '/bugs': setShowBugsPage, '/bugs/review': setShowBugReviewPage,
  };

  /**
   * Shows the library, from wherever the person is: an open workspace is saved and left as the
   * Library button does; a page over it is closed. `jump` is where in it to land, by default
   * where the library was last.
   */
  const showLibrary = async (jump: LibraryJump | null = null) => {
    Object.values(PAGE_FLAGS).forEach(set => set(false));
    setSettingsInitialTab('status');
    setLibraryJump(jump);
    if (!showLandingPage && (currentProjectId || currentLocalWorkspaceId)) await handleGoHome();
    else setShowLandingPage(true);
  };

  /**
   * A notification's link, opened by the app itself without loading the page again (loading it
   * again showed the front page while the sign-in was checked); true when it was. The library's
   * address, with a workspace to open (?open=), a project to show (?project=, under Shared
   * with me with view=shared) or neither; or one of the pages.
   */
  const openAppLink = (url: string): boolean => {
    const [path, query = ''] = url.split('?', 2);
    if (path !== '/workspaces') {
      const show = PAGE_FLAGS[path];
      if (!show) return false;
      Object.values(PAGE_FLAGS).forEach(set => set(false));
      setShowLandingPage(false);
      show(true);
      return true;
    }
    const asked = new URLSearchParams(query);
    const workspaceId = asked.get('open');
    if (workspaceId) { void openWorkspaceFromLink(linkTargetOf(workspaceId, asked)); return true; }
    const folderId = asked.get('project');
    const shared = asked.get('view') === 'shared';
    const jump: LibraryJump | null = folderId || shared ? { location: { view: shared ? 'shared' : 'library', folderId }, at: Date.now() } : null;
    void showLibrary(jump);
    return true;
  };

  // The bell of every page: a task's notification opens its workspace on the idea, without loading the app again
  const bell = (
    <NotificationBell
      userId={user?.sub}
      onOpenLink={openAppLink}
      // Heard live: an invitation to a project waits under Shared with me at once, and an
      // accepted one shows its new member in the owner's library
      onArrive={(arrived) => {
        if (arrived.some(n => n.type === 'project_invitation') && featureAccess.limits.cloudSyncEnabled) {
          void listMyProjectInvitations().then(setProjectInvitations).catch(() => undefined);
        }
        if (arrived.some(n => n.type === 'invitation_accepted' || n.type === 'workspace_shared')) void loadRecentFiles();
        // A copy of a project sent to this person waits under Shared with me; one accepted shows in the sender's sent list
        if (arrived.some(n => n.type === 'project_copy' || n.type === 'project_copy_done') && featureAccess.limits.cloudSyncEnabled) {
          void listProjectCopies().then(setProjectCopies).catch(() => undefined);
        }
      }}
      onAnswerInvitation={async (invitationId, accept) => {
        try {
          await handleAnswerProjectInvitation(invitationId, accept);
          if (accept) toast.success('Project added', 'You find it under Shared with me in your library.');
          return true;
        } catch (error) {
          toast.error('Could not answer the invitation', error instanceof Error ? error.message : 'Please try again.');
          return false;
        }
      }}
    />
  );

  const handleDuplicateLocalWorkspace = async (workspaceId: string) => {
      try {
          const duplicate = await duplicateLocalWorkspace(workspaceId);
          await refreshLocalWorkspaces();
          await loadLocalWorkspaceById(duplicate.id);
      } catch (e: any) {
          console.error(e);
          toast.error('Error duplicating workspace', e.message);
      }
  };
  
  const handleRenameProject = async () => {
      if (!user || !currentProjectId) return;
      const newName = prompt("Enter new workspace name:", currentProjectName);
      if (newName && newName !== currentProjectName) {
           const oldName = currentProjectName;
           setCurrentProjectName(newName);
           // Force an immediate save to update metadata in list
           await saveProject(nodesRef.current, connections, history, transformRef.current, user.sub, currentProjectId, newName, featureAccess.limits.cloudSyncEnabled);
           
           // RENAME LOCAL FOLDER if workspace auto-save is enabled
           if (workspaceAutoSave && autoSaveFolderName) {
               try {
                   const renameResult = await renameWorkspaceFolder(oldName, newName, user?.sub);
                   if (!renameResult.success) {
                       console.warn('Local folder rename failed:', renameResult.error);
                   }
               } catch (renameErr) {
                   console.warn('Local folder rename error:', renameErr);
               }
           }
           
           await refreshProjectList();
      }
      setIsFileMenuOpen(false);
  };

  const handleDeleteProject = async (projectId: string) => {
      if (!confirm("Are you sure you want to delete this workspace?")) return;
      try {
          await deleteProject(projectId, featureAccess.limits.cloudSyncEnabled);
      } catch (e: any) {
          console.error('Failed to delete workspace:', e);
          toast.error('Could not delete workspace', e?.message || 'Please try again.');
          return;
      }
      await clearLastOpened(projectId);
      await refreshProjectList();
      if (currentProjectId === projectId) {
          if (projectList.length > 1) {
             const next = projectList.find(p => p.id !== projectId);
             if (next) loadWorkspace(next.id, featureAccess.limits.cloudSyncEnabled);
          } else {
             const newId = await createProject(user!.sub, "Untitled", featureAccess.limits.cloudSyncEnabled);
             await refreshProjectList();
             await loadWorkspace(newId, featureAccess.limits.cloudSyncEnabled);
          }
      }
  };

  const handleLeaveSharedProject = async (projectId: string, projectName: string) => {
      if (!confirm(`Leave "${projectName}"? You will lose access to this shared workspace.`)) return;
      if (!user) return;
      try {
          const result = await unshareProject(projectId, { userId: user.sub });
          if (result.success) {
              await refreshProjectList();
              if (currentProjectId === projectId) {
                  const remaining = projectList.filter(p => p.id !== projectId);
                  if (remaining.length > 0) {
                      await loadWorkspace(remaining[0].id, featureAccess.limits.cloudSyncEnabled);
                  } else {
                      const newId = await createProject(user.sub, "Untitled", featureAccess.limits.cloudSyncEnabled);
                      await refreshProjectList();
                      await loadWorkspace(newId, featureAccess.limits.cloudSyncEnabled);
                  }
              }
          } else {
              toast.error('Failed to leave', result.error || 'Could not leave shared workspace');
          }
      } catch {
          toast.error('Failed to leave', 'Could not leave shared workspace');
      }
  };

  const initializeUserWorkspaces = async (userId: string, cloudSync: boolean = false) => {
      await migrateLegacyProject(userId);
      
      // SYNC: Push all local-only projects to cloud first
      if (cloudSync) {
          console.log('[init] Starting local→cloud sync...');
          try {
              const syncResult = await syncAllLocalProjectsToCloud(userId);
              console.log(`[init] Sync result: ${syncResult.synced} synced, ${syncResult.failed} failed, ${syncResult.skipped} skipped`);
          } catch (e) {
              console.warn('[init] Local→cloud sync failed (non-fatal):', e);
          }
      }
      
      let projects = await listProjects(userId, cloudSync);
      
      // Also refresh local workspaces for the workspace switcher
      await refreshLocalWorkspaces();
      
      // For free users on a new browser: check if local storage has workspaces we can import
      if (projects.length === 0 && workspaceAutoSave && autoSaveFolderName) {
          logger.debug('Storage', '📁 No projects in IndexedDB, checking local storage for workspaces...');
          // Workspace folders hold their .json file; they sit under their projects and folders
          const workspaceFolders = (await findLocalWorkspaceFolders(userId)).map(dir => ({ name: dir[dir.length - 1], dir }));
          if (workspaceFolders.length > 0) {
              for (const folder of workspaceFolders) {
                  try {
                      const localResult = await loadWorkspaceLocallyAt(folder.dir, userId);
                      if (localResult.success && localResult.data) {
                          logger.debug('Storage', '📁 Found local workspace:', folder.name);
                          // Create a project entry in IndexedDB for this workspace
                          const projectId = localResult.data.projectId || generateId();
                          await saveProject(
                              localResult.data.nodes || [],
                              localResult.data.connections || [],
                              localResult.data.history || [],
                              localResult.data.transform || { x: 0, y: 0, scale: 1 },
                              userId,
                              projectId,
                              localResult.data.projectName || folder.name,
                              false // Don't sync to cloud for free users
                          );
                      }
                  } catch (e) {
                      console.warn('Could not load local workspace:', folder.name, e);
                  }
              }
              // Refresh project list after importing
              projects = await listProjects(userId, cloudSync);
          }
      }
      
      setProjectList(projects);
      // When the library page is the landing, nothing is opened behind it: the user
      // picks (or creates) a workspace there, and a brand-new account sees the
      // first-run hero instead of an auto-created "My First Workspace".
      if (showLandingPage) return;
      if (projects.length > 0) {
          await loadWorkspace(projects[0].id, cloudSync);
      } else {
          const newId = await createProject(userId, "My First Workspace", cloudSync);
          setProjectList(await listProjects(userId, cloudSync));
          await loadWorkspace(newId, cloudSync);
      }
  };

  // Reset the library when the signed-in account changes so the page shows
  // skeletons (and never the previous account's workspaces) until it reloads.
  useEffect(() => {
    const uid = user?.sub ?? null;
    if (libraryUidRef.current === uid) return;
    libraryUidRef.current = uid;
    setLibraryLoaded(false);
    setLibraryWorkspaces([]);
    setFolders([]); setSharedFolders([]); setProjectInvitations([]); setProjectCopies({ sent: [], received: [] });
  }, [user?.sub]);

  // --- AUTH & LOAD ---
  // Wait for both user AND subscription to be loaded before initializing workspaces
  useEffect(() => {
    if (user && subscriptionLoaded) {
        setIsLoadingWorkspace(true);
        // Now we have accurate cloudSyncEnabled value from subscription
        initializeUserWorkspaces(user.sub, featureAccess.limits.cloudSyncEnabled)
          .then(() => {
            // Load recent files after workspace initialization
            return loadRecentFiles();
          })
          .catch(err => {
             console.error("Workspace init error:", err);
             // Suppress blocking modal if it's just permissions (fallback likely handled or allows offline mode)
             if (err.code === 'permission-denied') {
                 console.warn("Permission error suppressed to allow offline fallback.");
             } else {
                 setPermissionError(true);
                 setShowSettingsPage(true);
                 setConfigTab('account');
             }
             // The library must still finish loading (it shows skeletons until then).
             return loadRecentFiles();
          })
          .finally(() => {
             // Add minimum loading time for smoother UX
             setTimeout(() => setIsLoadingWorkspace(false), 800);
          });
    } else if (!user && authChecked) {
        // Not logged in - initialize local workspaces for guest user
        setIsLoadingWorkspace(true);
        (async () => {
            try {
                // Load local workspaces list and recent files
                await refreshLocalWorkspaces();
                await loadRecentFiles();
                
                // Don't auto-load workspace for guest users - show landing page
                setShowLandingPage(true);
                
                logger.debug('Storage', '✅ Initialized local workspaces for guest user');
            } catch (err) {
                console.error('Failed to initialize local workspaces:', err);
            } finally {
                setTimeout(() => setIsLoadingWorkspace(false), 500);
            }
        })();
    }
  }, [user, subscriptionLoaded, authChecked]);

  // --- AUTO SAVE ---
  useEffect(() => {
    if (!user || !currentProjectId) return;
    // View-only users must not auto-save: their writes would be rejected by Firestore rules.
    if (isViewOnly) return;
    // Opening a workspace is not an edit: nothing is saved while the canvas holds what was loaded or last saved
    // (nor is a copy read from this device saved before something is changed here or the cloud is heard)
    const contents = { nodes, connections, history, name: currentProjectName };
    const openNote = savedNoteOf(currentProjectId);
    if (!changedHere(openNote?.saved ?? null, openNote?.localCopy, contents, dirtyNodeIdsRef.current)) return;

    logger.debug('Sync', '☁️ Cloud auto-save triggered, canvas ref:', !!canvasRef.current);

    // In two seconds, unless the canvas changes again or the workspace is left first (what is
    // returned takes the save back). Closing the window runs it at once (flushSaves).
    return autoSavesRef.current.later('cloud', async () => {
      try {
          // Another workspace has been loaded meanwhile: the leave-save of this one has covered it
          if (savedNoteRef.current && savedNoteRef.current.key !== currentProjectId) return;

          // NOTE: Thumbnail is NOT generated during auto-save for performance.
          // Thumbnails are only generated when navigating away from the canvas (handleGoHome, handleSwitchWorkspace).

          // What to write: the nodes replaced since they were loaded or saved and those marked as
          // changed (incremental per-node cloud writes, v2 schema); no node at all when only the
          // connections, the history or the name changed; every node only when nodes were removed
          const marked = new Set(dirtyNodeIdsRef.current);
          const note = savedNoteOf(currentProjectId);
          const plan = whatToSave(note?.saved ?? null, contents, marked);
          if (!plan.save || !changedHere(note?.saved ?? null, note?.localCopy, contents, marked)) return;
          // The connections and the history are left to the cloud while it may hold newer ones
          const lists = listsLeftToCloud(note?.localCopy, note?.saved, note?.cloudHistory, contents, plan.nodeIds === null);
          const historyUnchanged = !plan.historyChanged && lists.historyUnchanged;
          // For the folder's file, as things are when this save starts (see below)
          const wasPending = !!note?.localCopy?.pending;
          const leavesBefore = leaveCountRef.current;
          // Clear dirty IDs optimistically — will be restored on save failure
          dirtyNodeIdsRef.current.clear();
          // Until the save is done, a cloud update must not replace these nodes
          const savingIds = plan.nodeIds ?? marked;
          savingIds.forEach(id => savingNodeIdsRef.current.add(id));
          const savedView = transformRef.current;

          let result;
          try {
            result = await saveProject(nodes, connections, history, savedView, user.sub, currentProjectId, currentProjectName, featureAccess.limits.cloudSyncEnabled, undefined, plan.nodeIds,
              { historyUnchanged, connectionsUnchanged: lists.connectionsUnchanged });
          } catch (saveErr) {
            // Restore dirty IDs so they get retried on the next save
            marked.forEach(id => dirtyNodeIdsRef.current.add(id));
            savingIds.forEach(id => savingNodeIdsRef.current.delete(id));
            throw saveErr;
          }
          savingIds.forEach(id => savingNodeIdsRef.current.delete(id));
          if (result.cloud === 'failed') {
            // The cloud did not take it (this device's copy was written): the marks come back and
            // what is noted as saved stays as it was, so the next save sends these changes again
            marksAfterSave(result, marked).forEach(id => dirtyNodeIdsRef.current.add(id));
          } else if (!savedNoteRef.current || savedNoteRef.current.key === currentProjectId) {
            // The cloud now holds what was saved (and what a cloud update brought while the save was under way)
            const noted = savedNoteRef.current
              ?? (savedNoteRef.current = { key: currentProjectId, saved: contents, cloudView: null, cloudHistory: null, ownStamps: new Set<number>(), localCopy: null });
            noted.saved = { ...contents, nodes: savedNodesAfterSave(noted.saved.nodes, nodes, plan.nodeIds) };
            if (result.cloud === 'saved') {
              noted.cloudView = savedView;
              // (a history that was not sent is the cloud's only where that was already known)
              if (!historyUnchanged) noted.cloudHistory = history;
              noted.ownStamps.add(result.stamp);
            }
            // (a cloud update merged in while the save was under way has brought nodes of its own)
            syncedNodeIdsRef.current = syncedNodeIdsAfterSave(nodes, plan.nodeIds === null, syncedNodeIdsRef.current, nodesRef.current);
            syncedConnectionIdsRef.current = new Set(connections.map(c => c.id));
            // A node written without its media (the upload failed) is not saved yet: it stays
            // marked, so the next save, or the save on leaving, uploads again
            marksAfterSave(result, marked).forEach(id => dirtyNodeIdsRef.current.add(id));
          }

          // AUTO-SAVE WORKSPACE LOCALLY if enabled. Not once the workspace has been left meanwhile:
          // its leave-save took the canvas as it was then, which is newer, and writes the file itself
          if (workspaceAutoSave && autoSaveFolderName && leaveCountRef.current === leavesBefore) {
              try {
                  await saveWorkspaceLocally(currentProjectName, {
                      nodes,
                      connections,
                      history,
                      transform: transformRef.current,
                      projectId: currentProjectId,
                      projectName: currentProjectName,
                      // Whether this copy holds work the cloud has not taken: opened again, it is then the newer one
                      cloudPending: cloudPendingAfterSave(result.cloud, true, wasPending),
                  }, getLocalFileMap(), user?.sub); // Pass localFileMap for offline image resolution
              } catch (localSaveErr) {
                  console.warn('Local workspace save failed:', localSaveErr);
              }
          }
          
          setPermissionError(false);
      } catch (err: any) {
          if (err.code === 'permission-denied') {
              setPermissionError(true);
          } else {
              console.error('Auto-save failed:', err);
              logger.error('Sync', `Auto-save failed: ${err.message || err}`);
          }
      }
    }, 2000);
  }, [nodes, connections, history, user, currentProjectId, currentProjectName, featureAccess.limits.cloudSyncEnabled, isViewOnly]);

  // --- AUTO SAVE FOR LOCAL WORKSPACES (no user required) ---
  useEffect(() => {
    // Only run if in local workspace mode (no user or explicit local mode)
    if (!isLocalWorkspaceMode || !currentLocalWorkspaceId) return;
    // As for a cloud workspace: opening one is not an edit, and what is already saved is not saved again
    const contents = { nodes, connections, history, name: currentProjectName };
    if (!whatToSave(savedNoteOf(currentLocalWorkspaceId)?.saved ?? null, contents).save) return;

    logger.debug('Sync', '💾 Local auto-save triggered, canvas ref:', !!canvasRef.current);

    // (in two seconds, as the cloud auto-save above)
    return autoSavesRef.current.later('local', async () => {
      try {
          // NOTE: Thumbnail is NOT generated during auto-save for performance.
          // Thumbnails are only generated when navigating away from the canvas (handleGoHome, handleSwitchWorkspace).

          await saveLocalWorkspace(currentLocalWorkspaceId, {
              name: currentProjectName,
              nodes,
              connections,
              history,
              transform: transformRef.current,
              localFileMap: getLocalFileMap(),
              ownerId: user?.sub,
          });
          const noted = savedNoteOf(currentLocalWorkspaceId);
          if (noted) noted.saved = contents;
          else if (!savedNoteRef.current) noteLoaded(currentLocalWorkspaceId, contents);

          // Also save to file system if enabled
          if (workspaceAutoSave && autoSaveFolderName) {
              try {
                  await saveWorkspaceLocally(currentProjectName, {
                      nodes,
                      connections,
                      history,
                      transform: transformRef.current,
                      projectId: currentLocalWorkspaceId,
                      projectName: currentProjectName,
                  }, getLocalFileMap(), user?.sub);
              } catch (localSaveErr) {
                  console.warn('Local file system save failed:', localSaveErr);
              }
          }
          
          logger.debug('Sync', '✅ Auto-saved local workspace:', currentProjectName);
      } catch (err: any) {
          console.error('Local workspace save failed:', err);
      }
    }, 2000);
  }, [nodes, connections, history, currentLocalWorkspaceId, currentProjectName, isLocalWorkspaceMode]);

  // --- CLOSING OR RELOADING THE WINDOW ---
  // An auto-save waits two seconds, and a workspace switched away from is saved behind the next
  // one: a window that goes meanwhile would take that work along. In the desktop app the shell
  // asks the page to save first and waits for it, a few seconds at most (desktop/main.cjs). A
  // browser tab cannot wait: it asks the person, while a save is waiting or under way.
  useEffect(() => {
    const desktop = desktopApp();
    if (desktop) return desktop.onBeforeClose?.(() => flushSaves());
    const askToStay = (event: BeforeUnloadEvent) => {
      // (opening File Explorer through the helper link fires this too, but the page stays open)
      if (launchedFolderHelperRecently()) return;
      if (!autoSavesRef.current.busy() && !pendingSavesRef.current.isSavingAny()) return;
      event.preventDefault();
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', askToStay);
    return () => window.removeEventListener('beforeunload', askToStay);
  }, []);

  // --- REAL-TIME SYNC (Pro users only) ---
  useEffect(() => {
    if (!user || !currentProjectId || !featureAccess.limits.cloudSyncEnabled) return;
    
    logger.debug('Sync', '🔄 Setting up real-time sync for project:', currentProjectId);
    
    // Updates still on their way when this workspace is left (the listener is asynchronous) must not reach the next one
    let active = true;
    const unsubscribe = subscribeToProject(currentProjectId, (data) => {
      if (!active || !data) return;

      // Echo suppression is handled in subscribeToProject via sessionId matching. What does
      // arrive (the workspace as the cloud has it when the listeners start, then every save made
      // elsewhere) is merged in piece by piece: a piece that is what the canvas already has keeps
      // its object, so an update that changes nothing, as the first one normally does, sets no
      // state and renders nothing.
      logger.debug('Sync', '📥 Received remote update, lastSaved:', new Date(data.lastSaved).toLocaleTimeString());

      // Merge with what was done here since the last sync (nodes just pasted or added, unsaved edits),
      // instead of replacing it: the update may have been sent before those changes were saved
      const note = savedNoteOf(currentProjectId);
      // Another workspace has been loaded and is about to show: this update is for the one being left
      if (!note && savedNoteRef.current) return;
      // The first update after a copy read from this device was put on the canvas (the folder's
      // file, or the device's own copy) settles which of the two is the newer one, by the cloud's
      // own date. The device's: all of it is kept and nothing of it counts as synced or saved, so
      // the merge below replaces and removes nothing, adds what only the cloud has, and the
      // auto-save that follows writes everything. The cloud's: the canvas follows it, as always.
      const localIsNewer = !!note && localCopyIsNewer(note.localCopy, data.lastSaved, !isViewOnlyRef.current);
      if (note) note.localCopy = null;
      if (note && localIsNewer) {
        const whole = keepWholeLocalCopy(nodesRef.current, note.saved);
        whole.marks.forEach(id => dirtyNodeIdsRef.current.add(id));
        syncedNodeIdsRef.current = new Set();
        syncedConnectionIdsRef.current = new Set();
        note.saved = whole.saved;
      }
      const saved = note?.saved ?? null;
      const localNodes = nodesRef.current;
      // What the merge is worked out from, kept as it is now for doing the merge again (see below)
      const syncedNodes = syncedNodeIdsRef.current;
      const syncedLinks = syncedConnectionIdsRef.current;
      const marks = [...dirtyNodeIdsRef.current, ...savingNodeIdsRef.current];
      // (A view-only session saves nothing, so nothing of it is waiting to be saved: it follows the cloud)
      const savedNodes = isViewOnlyRef.current ? null : saved?.nodes ?? null;
      const savedHistory = saved?.history ?? null;
      const ownStamps = note?.ownStamps;
      const keepLocal = nodesToKeep(localNodes, savedNodes, marks);
      const mergedNodes = mergeRemoteNodes(localNodes, data.nodes, syncedNodes, keepLocal, ownStamps);
      const localConnections = connectionsRef.current;
      const mergedConnections = mergeRemoteConnections(localConnections, data.connections, syncedLinks, mergedNodes);
      const localHistory = historyRef.current;
      const { merged: mergedHistory, unsaved: historyUnsaved } = mergeRemoteHistory<HistoryItem | LightHistoryItem>(localHistory, data.history, savedHistory);
      // The lists read above are the ones last rendered. A change made here from outside an event
      // (a generation that just finished, a pasted picture, a history entry) may still be on its
      // way to the screen, and setting a list to the merged one outright would lose it. So each
      // list is set by a function of the list as it is by then: the merged one when nothing came
      // between, else the merge done again on that list, which keeps the change like any other
      // unsaved work (see afterMerge). The refs move at once: a second update that arrives before
      // the next render starts from this one, and does not take a node it added for removed here.
      if (mergedNodes !== localNodes) {
        setNodes(afterMerge(localNodes, mergedNodes, latest => mergeRemoteNodes(latest, data.nodes, syncedNodes, nodesToKeep(latest, savedNodes, marks), ownStamps)));
        nodesRef.current = mergedNodes;
      } else if (localIsNewer) {
        // Nothing to merge in, but the whole copy is still to be saved: the auto-save plans again
        setNodes(latest => [...latest]);
      }
      if (mergedConnections !== localConnections) {
        setConnections(afterMerge(localConnections, mergedConnections, latest =>
          mergeRemoteConnections(latest, data.connections, syncedLinks, mergedNodes, nodeIdsAddedWith(latest, localConnections, localNodes))));
        connectionsRef.current = mergedConnections;
      }
      if (mergedHistory !== localHistory) {
        setHistory(afterMerge(localHistory, mergedHistory, latest => mergeRemoteHistory<HistoryItem | LightHistoryItem>(latest, data.history, savedHistory).merged));
        historyRef.current = mergedHistory;
      }
      // An auto-save still waiting was planned from the lists this update replaces, while what is
      // noted as saved moves just below: fired before the next render, it would take what the
      // update added for removed here and write the older lists back. The render that follows
      // plans it again, from the merged lists.
      if (mergedNodes !== localNodes || mergedConnections !== localConnections || mergedHistory !== localHistory) {
        autoSavesRef.current.cancelWaiting('cloud');
      }
      if (data.name) setCurrentProjectName(data.name);
      syncedNodeIdsRef.current = new Set(data.nodes.map(n => n.id));
      syncedConnectionIdsRef.current = syncedConnectionIds(data.connections, mergedNodes);
      if (!note || !saved) return;
      // What now follows the cloud is saved as it is; what was kept from here is still to be saved
      note.saved = {
        nodes: saved.nodes ? savedNodesAfterMerge(mergedNodes, data.nodes, saved.nodes, keepLocal) : null,
        connections: localConnections === saved.connections ? mergedConnections : saved.connections,
        history: historyUnsaved ? saved.history : mergedHistory,
        name: data.name || saved.name,
      };
      if (!historyUnsaved) note.cloudHistory = mergedHistory;
      // The view follows the cloud only when another session really moved it: not on the first
      // update after opening, and not when this session's own save comes back with other news
      const knownView = note.cloudView;
      note.cloudView = data.transform;
      if (data.fromOtherSession && knownView && !sameJson(knownView, data.transform)) setTransform(data.transform);
    });
    
    return () => {
      logger.debug('Sync', '🔄 Cleaning up real-time sync');
      active = false;
      unsubscribe();
    };
  }, [user, currentProjectId, featureAccess.limits.cloudSyncEnabled]);

  // --- SESSION LIFECYCLE (concurrent access) ---
  useEffect(() => {
    if (!user || !authChecked) return;

    const initSession = async () => {
      try {
        const manager = new SessionManager(user.sub);
        sessionManagerRef.current = manager;

        let result = await manager.register(() => {
          // Session was revoked remotely → force sign-out
          logger.info('Session', '⚠️ Session revoked remotely, signing out');
          handleLogout();
        });

        // If Firebase Auth wasn't ready, retry after a short delay
        if (!result.success && (result.error === 'Firebase Auth not ready' || result.error === 'Firestore not ready')) {
          logger.debug('Session', '⏳ Firebase Auth not ready, retrying in 2s...');
          await new Promise(r => setTimeout(r, 2000));
          result = await manager.register(() => {
            logger.info('Session', '⚠️ Session revoked remotely, signing out');
            handleLogout();
          });
        }

        if (!result.success) {
          console.warn('Session registration failed:', result.error);
          return;
        }

        setCurrentSessionId(manager.getSessionId());

        logger.info('Session', '✅ Registered session:', manager.getSessionId());
      } catch (err) {
        console.error('Session registration failed:', err);
      }
    };

    initSession();

    // Clean up on tab close
    const handleBeforeUnload = () => {
      // Opening File Explorer through the helper link fires this too, but the page stays open
      if (launchedFolderHelperRecently()) return;
      sessionManagerRef.current?.markInactive();
    };
    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      sessionManagerRef.current?.destroy();
      sessionManagerRef.current = null;
      setActiveSessions(previous => (previous && previous.length === 0 ? previous : []));
      setCurrentSessionId(null);
    };
  }, [user, authChecked]);

  // The real-time list of devices is only shown in Settings, so it is listened to only while
  // Settings is open: every signed-in device writes a heartbeat each 30 s, and each of those
  // rendered the whole canvas
  useEffect(() => {
    const manager = sessionManagerRef.current;
    if (!showSettingsPage || !currentSessionId || !manager) return;
    // Nothing to show yet: "loading", not "no devices"
    setActiveSessions(previous => (previous && previous.length > 0 ? previous : undefined));
    return manager.subscribeToSessions((sessions) => {
      setActiveSessions(previous => (previous && sameJson(previous, sessions) ? previous : sessions));
    });
  }, [showSettingsPage, currentSessionId]);

  // --- SCAN AND IMPORT LOCAL WORKSPACES ---
  const scanAndImportLocalWorkspaces = async (
      onProgress?: (current: number, total: number) => void
  ): Promise<{ count: number }> => {
      if (!user) return { count: 0 };
      
      logger.debug('Storage', '📁 Scanning for local workspaces...');
      // Workspace folders hold their .json file; they sit under their projects and folders
      const folders = (await findLocalWorkspaceFolders(user?.sub)).map(dir => ({ name: dir[dir.length - 1], dir }));

      if (folders.length === 0) {
          logger.debug('Storage', '📁 No local workspaces found');
          return { count: 0 };
      }

      let importedCount = 0;
      let firstWorkspace: { id: string; name: string } | null = null;
      const totalFolders = folders.length;
      // Only workspaces this device does not have yet are brought in (a folder set up on another
      // device). One it has is never written over with the folder's copy, which may be older
      // than the cloud's, and an id-less copy is matched by its name so it is not added again.
      const isKnown = knownWorkspaceCheck([...projectList, ...libraryWorkspaces]);
      
      // Look for workspace folders (they contain .json files)
      for (let i = 0; i < folders.length; i++) {
          const folder = folders[i];
          
          // Report progress
          if (onProgress) {
              onProgress(i + 1, totalFolders);
          }
          
          try {
              const localResult = await loadWorkspaceLocallyAt(folder.dir, user?.sub);
              if (localResult.success && localResult.data && !isKnown(localResult.data.projectId, localResult.data.projectName || folder.name)) {
                      logger.debug('Storage', '📁 Found local workspace:', folder.name);
                  
                  // Create a project entry in IndexedDB for this workspace
                  const projectId = localResult.data.projectId || generateId();
                  await saveProject(
                      localResult.data.nodes || [],
                      localResult.data.connections || [],
                      localResult.data.history || [],
                      localResult.data.transform || { x: 0, y: 0, scale: 1 },
                      user.sub,
                      projectId,
                      localResult.data.projectName || folder.name,
                      false // Don't sync to cloud for free users
                  );
                  
                  importedCount++;
                  if (!firstWorkspace) {
                      firstWorkspace = { id: projectId, name: localResult.data.projectName || folder.name };
                  }
              }
          } catch (e) {
              console.warn('Could not load local workspace:', folder.name, e);
          }
      }
      
      if (importedCount > 0) {
          logger.debug('Storage', `📁 Imported ${importedCount} workspace(s) from local storage`);
          
          // Refresh project list
          const projects = await listProjects(user.sub, featureAccess.limits.cloudSyncEnabled);
          setProjectList(projects);
          
          // Open the first one brought in, only when no workspace is open: choosing the folder
          // from inside a workspace used to switch to another workspace
          if (firstWorkspace && !currentProjectId && !currentLocalWorkspaceId) {
              await loadWorkspace(firstWorkspace.id, featureAccess.limits.cloudSyncEnabled);
          }
      }
      
      return { count: importedCount };
  };

  // --- CENTRALIZED LOCAL FOLDER SELECTION HANDLER ---
  const handleSelectLocalFolder = async (options?: { 
      closePromptOnSuccess?: boolean;
      closeOnboardingOnSuccess?: boolean;
  }) => {
      // Step 1: Show connecting state (waiting for folder picker)
      setLocalSyncStatus({ 
          isActive: true, 
          step: 'connecting', 
          message: 'Please select a folder in the browser dialog...' 
      });
      
      try {
          const handle = await pickSaveDirectory(user?.sub);
          
          if (!handle) {
              // User cancelled the picker
              setLocalSyncStatus({ isActive: false, step: 'idle', message: '' });
              return false;
          }
          
          // Step 2: Update state and save preferences
          setLocalSyncStatus({ 
              isActive: true, 
              step: 'scanning', 
              message: 'Connecting to folder...', 
              folderName: handle.name 
          });
          
          setAutoSaveFolderName(handle.name);
          setAutoSaveEnabledState(true);
          setAutoSaveEnabled(true, user?.sub);
          setLocalFiles([]);
          setLocalSaveStats(null);
          
          // Sync preferences to cloud
          await syncLocalStoragePrefsToCloud(user?.sub, true, handle.name);
          
          // Step 3: Scan for workspaces with progress
          setLocalSyncStatus({ 
              isActive: true, 
              step: 'importing', 
              message: 'Scanning for existing workspaces...', 
              folderName: handle.name 
          });
          
          const importResult = await scanAndImportLocalWorkspaces((current, total) => {
              setLocalSyncStatus(prev => ({ 
                  ...prev, 
                  message: `Importing workspace ${current} of ${total}...`,
                  progress: { current, total }
              }));
          });
          
          // Step 4: Show success
          setLocalSyncStatus({ 
              isActive: true, 
              step: 'complete', 
              message: importResult.count > 0 
                  ? `Found ${importResult.count} workspace(s)` 
                  : 'Ready to save your work!',
              folderName: handle.name 
          });
          
          // Close other prompts if requested
          if (options?.closePromptOnSuccess) {
              setShowLocalStoragePrompt(false);
          }
          if (options?.closeOnboardingOnSuccess) {
              setShowOnboarding(false);
              if (user) {
                  await completeOnboarding(user.sub);
              }
          }
          
          // The folder catches up with the open workspace: its files that are only in the cloud are copied in
          setFolderRound(n => n + 1);

          // Auto-dismiss after 2.5 seconds
          setTimeout(() => {
              setLocalSyncStatus({ isActive: false, step: 'idle', message: '' });
          }, 2500);
          
          return true;
      } catch (error: any) {
          console.error('Error selecting local folder:', error);
          setLocalSyncStatus({ 
              isActive: true, 
              step: 'error', 
              message: error.message || 'Failed to connect to folder' 
          });
          return false;
      }
  };

  // --- EXPORT / IMPORT ---
  const handleExport = async () => {
      try {
          // Use refs to ensure we have the absolute latest state when function is called
          const currentNodes = nodesRef.current;
          
          if (currentNodes.length === 0) {
              toast.warning('Workspace is empty', 'Add nodes before exporting.');
              return;
          }
          
          const processedNodes = await processNodesForExport(currentNodes);
          
          // The History as the file keeps it (light entries have no node data to convert)
          const processedHistory = await processHistoryForExport(history);

          const data = {
              app: "infinite-canvas",
              version: "1.0",
              name: currentProjectName,
              nodes: processedNodes,
              connections,
              history: processedHistory,
              transform: transformRef.current // Use ref to ensure latest view
          };
          const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
          const url = URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = `${currentProjectName.replace(/\s+/g, '_')}_workspace.json`;
          a.click();
          URL.revokeObjectURL(url);
          setIsFileMenuOpen(false);
      } catch (e) {
          console.error("Export failed", e);
          toast.error('Failed to export workspace', 'Check console for details.');
      }
  };

  const handleImport = (e: React.ChangeEvent<HTMLInputElement>) => {
      setIsFileMenuOpen(false);
      const file = e.target.files?.[0];
      if (!file) return;
      
      const reader = new FileReader();
      
      reader.onload = async (ev) => {
          try {
              const jsonContent = ev.target?.result as string;
              if (!jsonContent) throw new Error("File is empty");

              const json = JSON.parse(jsonContent);
              
              // Support both exported files (app: "infinite-canvas") and local workspace files (version + projectId)
              const isExportedFile = json.app === "infinite-canvas";
              const isLocalWorkspaceFile = json.version && json.projectId && Array.isArray(json.nodes);
              
              if (!isExportedFile && !isLocalWorkspaceFile) {
                  throw new Error("Invalid file format: Not a recognized workspace file");
              }
              
              if (confirm("Importing will replace current workspace. Continue?")) {
                  setShowLandingPage(false); // Hide landing page when importing
                  // Validate Transform to prevent blank screen
                  // Ensure scale is reasonable (0.1 to 5) and coords are numbers
                  let validTransform = { x: 0, y: 0, scale: 1 };
                  if (json.transform && 
                      Number.isFinite(json.transform.x) && 
                      Number.isFinite(json.transform.y) && 
                      Number.isFinite(json.transform.scale) && 
                      json.transform.scale > 0.05
                  ) {
                      validTransform = json.transform;
                  }
                  
                  // Rehydrate Nodes (convert base64 strings back to blob urls for performance)
                  const rawNodes = Array.isArray(json.nodes) ? json.nodes : [];
                  const hydratedNodes = await rehydrateNodes(rawNodes);
                  
                  // Rehydrate History
                  // We can't reuse rehydrateNodes directly because type mismatch, but logic is similar
                  // For safety, we just pass raw history if rehydration is complex, or manual map
                  const rawHistory = Array.isArray(json.history) ? json.history : [];
                  
                  // Fallback: If rehydration returned empty but we had nodes, use raw nodes to prevent blank workspace
                  const nodesToSet = (hydratedNodes.length === 0 && rawNodes.length > 0) ? rawNodes : hydratedNodes;

                  // Clear pending states
                  setPendingConnection(null);
                  setSelectedNodeId(null);

                  // Force batch updates
                  setTransform(validTransform);
                  setNodes(nodesToSet);
                  setConnections(Array.isArray(json.connections) ? json.connections : []);
                  setHistory(rawHistory);
                  
                  if (json.name) setCurrentProjectName(json.name);
                  if (json.projectName) setCurrentProjectName(json.projectName);
                  
                  // Load localFileMap for offline image resolution
                  if (json.localFileMap && typeof json.localFileMap === 'object') {
                      setLocalFileMap(json.localFileMap);
                      logger.debug('Storage', `📁 Loaded localFileMap with ${Object.keys(json.localFileMap).length} entries`);
                  }
                  
                  logger.debug('Storage', `Imported ${nodesToSet.length} nodes successfully.`);

                  // Trigger save immediately to persist import if logged in
                  if (user && currentProjectId) {
                      try {
                          await saveProject(nodesToSet, json.connections || [], rawHistory, validTransform, user.sub, currentProjectId, json.name || currentProjectName, featureAccess.limits.cloudSyncEnabled);
                      } catch (saveErr) {
                          console.warn("Auto-save after import failed", saveErr);
                      }
                  }
              }
          } catch (err: any) {
              console.error("Import Error:", err);
              toast.error('Failed to import', err.message || 'Invalid JSON or corrupt file.');
          } finally {
              // Reset input value to allow re-importing same file even if it failed
              if (fileInputRef.current) fileInputRef.current.value = '';
          }
      };

      reader.onerror = () => {
          toast.error('Failed to read file');
          if (fileInputRef.current) fileInputRef.current.value = '';
      };
      
      reader.readAsText(file);
  };

  const handleLogout = async () => {
      await logoutUser();
      clearSessionBlobs(); // the next account must not be served this session's media
      sessionStorage.removeItem('app_current_page');
      setUser(null);
      setIsGalleryOpen(false);
      setHistory([]);
      setConnections([]);
      setNodes([{ id: '1', type: NodeType.MEDIA_REFERENCE, position: { x: 100, y: 100 }, size: { width: 320, height: 300 }, data: { title: 'Reference', timestamp: Date.now() } }]);
      setTransform({ x: 0, y: 0, scale: 1 });
      setCurrentProjectId(null);
      // A local workspace is closed as well, in the same render as the canvas is emptied: left
      // open, its auto-save would write the placeholder canvas over it two seconds later
      setCurrentLocalWorkspaceId(null);
      setIsLocalWorkspaceMode(false);
      setCurrentProjectName("Untitled");
      setIsFileMenuOpen(false);
  };

  // --- HANDLERS ---
  const EMPTY_KEY_INPUTS = { googleGenAi: '', openAi: '', venice: '', vertexApiKey: '', vertexProjectId: '', vertexLocation: '' };

  // Only fields with something typed change; a blank field keeps its saved value.
  // Removing a key is an explicit action (handleRemoveApiKey). The keys are in use at once;
  // where they are kept is said once they are (utils/apiKeyStore.ts).
  const handleSaveApiKeys = async () => {
      const next: ApiKeys = { ...apiKeys };
      const typedKeys: [keyof ApiKeys, string][] = [];
      (Object.keys(API_KEY_STORAGE) as (keyof ApiKeys)[]).forEach((name) => {
          const typed = (inputApiKeys[name] || '').trim();
          if (!typed || typed === apiKeys[name]) return;
          next[name] = typed;
          typedKeys.push([name, typed]);
      });
      setApiKeys(next);
      setInputApiKeys(EMPTY_KEY_INPUTS);
      if (typedKeys.length === 0) {
          toast.info('Nothing to save', 'Use Replace on a saved key to enter a new one.');
          return;
      }
      const kept = await Promise.all(typedKeys.map(([name, value]) => saveApiKey(name, value)));
      const problem = kept.find(result => result.problem)?.problem;
      if (problem) toast.warning('API keys saved until the app closes', problem);
      else if (kept.every(result => result.kept === 'computer')) toast.success('API keys saved', 'Kept on this computer, encrypted by Windows for your account.');
      else toast.success('API keys saved', 'Stored in session (clears on tab close).');
  };

  const handleRemoveApiKey = (name: keyof ApiKeys) => {
      setApiKeys(prev => ({ ...prev, [name]: name === 'vertexLocation' ? 'us-central1' : '' }));
      setInputApiKeys(prev => ({ ...prev, [name]: '' }));
      void removeApiKey(name);
      toast.success('Key removed', keysLast ? 'It is no longer kept on this computer.' : 'It is no longer stored in this session.');
  };

  const finishLogin = async (userProfile: UserProfile, isNewAccount: boolean = false) => {
      setUser(userProfile);
      setShowAuthModal(false);
      
      // Check if user already has local storage set up elsewhere
      if (autoSaveSupported) {
          const cloudPrefs = await getLocalStoragePrefs(userProfile.sub);
          const localHandle = await getStoredDirectoryHandle(userProfile.sub);
          
          if (cloudPrefs?.hasLocalStorageEnabled && !localHandle) {
              // User has local storage elsewhere but not on this browser
              // The cross-device detection useEffect will show the appropriate prompt
              // Don't show onboarding - the cross-device prompt is more helpful
              setCloudLocalStoragePrefs(cloudPrefs);
              setShowLocalStoragePrompt(true);
          } else if (isNewAccount) {
              // Truly new signup - show onboarding
              setShowOnboarding(true);
          } else if (!cloudPrefs?.hasLocalStorageEnabled) {
              // Existing user who hasn't set up local storage anywhere
              const onboardingStatus = await getOnboardingStatus(userProfile.sub);
              if (!onboardingStatus?.completed) {
                  setShowOnboarding(true);
              }
          }
      }
      
      const win = window as any;
      if (win.aistudio && win.aistudio.hasSelectedApiKey) {
          const hasKey = await win.aistudio.hasSelectedApiKey();
          if (!hasKey && win.aistudio.openSelectKey) await win.aistudio.openSelectKey();
      }
  };

  const resetConfig = () => {
      setShowSettingsPage(true);
      if (permissionError) setConfigTab('rules');
  };

  // Node Manipulation
  const handleSelectNode = useCallback((id: string, additive?: boolean) => {
    setSelectedNodeIds(prev => nextSelection(prev, id, !!additive));
  }, []);
  const clearSelection = useCallback(() => setSelectedNodeIds([]), []);
  const handleMarqueeSelect = useCallback((ids: string[], additive: boolean) => {
    setSelectedNodeIds(prev => (additive ? Array.from(new Set([...prev, ...ids])) : ids));
  }, []);

  /** The nodes that move when `id` is dragged: the selection it is part of, and a script's outputs. */
  const dragGroupOf = useCallback((all: CanvasNode[], id: string): string[] => {
    let ids = [id];
    // Dragging one of several selected nodes moves them all
    const selection = selectedNodeIdsRef.current;
    if (selection.length > 1 && selection.includes(id)) ids = [...selection];
    if (all.find(n => n.id === id)?.type === NodeType.SCRIPT_PROCESSOR) {
        const childIds = connections.filter(c => c.sourceId === id).map(c => c.targetId);
        ids = Array.from(new Set([...ids, ...childIds]));
    }
    return ids;
  }, [connections]);
  const getDragGroup = useCallback((id: string) => dragGroupOf(nodesRef.current, id), [dragGroupOf]);

  const handleNodePositionChange = useCallback((id: string, newPos: Position) => {
    if (isViewOnly) return; // View-only users cannot move nodes
    setNodes((prev) => {
        const movingNode = prev.find(n => n.id === id);
        if (!movingNode) return prev;
        const dx = newPos.x - movingNode.position.x;
        const dy = newPos.y - movingNode.position.y;
        if (!dx && !dy) return prev;

        const idsToMove = dragGroupOf(prev, id);

        // Mark all moved nodes as dirty for incremental sync
        idsToMove.forEach(nid => dirtyNodeIdsRef.current.add(nid));

        return prev.map((node) => {
            if (idsToMove.includes(node.id)) {
                if (node.id === id) return { ...node, position: newPos };
                return { ...node, position: { x: node.position.x + dx, y: node.position.y + dy } };
            }
            return node;
        });
    });
  }, [dragGroupOf, isViewOnly]);

  const handleNodeResize = useCallback((id: string, newSize: { width: number; height: number }) => {
    if (isViewOnly) return; // View-only users cannot resize nodes
    dirtyNodeIdsRef.current.add(id);
    setNodes((prev) => prev.map((node) => (node.id === id ? { ...node, size: newSize } : node)));
  }, [isViewOnly]);

  // Handle storage node expand/settle toggle
  const handleStorageNodeExpandToggle = useCallback((nodeId: string) => {
    const node = nodes.find(n => n.id === nodeId);
    if (!node) return;
    
    const isCurrentlyExpanded = expandedStorageNodes[nodeId];
    
    if (isCurrentlyExpanded) {
      // Settle: restore original size
      setNodes(prev => prev.map(n => 
        n.id === nodeId ? { ...n, size: isCurrentlyExpanded } : n
      ));
      setExpandedStorageNodes(prev => {
        const newState = { ...prev };
        delete newState[nodeId];
        return newState;
      });
    } else {
      // Expand: save current size and expand to fullscreen-like size
      const originalSize = node.size || { width: 380, height: 450 };
      setExpandedStorageNodes(prev => ({ ...prev, [nodeId]: originalSize }));
      
      // Expand to near fullscreen (leaving some margin)
      const expandedWidth = Math.min(window.innerWidth - 100, 1200);
      const expandedHeight = Math.min(window.innerHeight - 150, 800);
      
      setNodes(prev => prev.map(n => 
        n.id === nodeId ? { ...n, size: { width: expandedWidth, height: expandedHeight } } : n
      ));
    }
  }, [nodes, expandedStorageNodes]);

  const handleNodeDelete = useCallback((id: string) => {
    if (isViewOnly) return; // View-only users cannot delete nodes
    setNodes((prev) => prev.filter((node) => node.id !== id));
    setConnections(prev => prev.filter(c => c.sourceId !== id && c.targetId !== id));
    setSelectedNodeIds(prev => (prev.includes(id) ? prev.filter(x => x !== id) : prev));
    // Clean up expanded state if storage node is deleted
    setExpandedStorageNodes(prev => {
      const newState = { ...prev };
      delete newState[id];
      return newState;
    });
    // Remove from dirty set and delete from cloud sub-collection
    dirtyNodeIdsRef.current.delete(id);
    if (user && currentProjectId && featureAccess.limits.cloudSyncEnabled) {
      deleteNodeFromCloud(currentProjectId, id).catch(err =>
        console.warn('Failed to delete node sub-doc:', err)
      );
    }
  }, [user, currentProjectId, isLocalWorkspaceMode, featureAccess.limits.cloudSyncEnabled, isViewOnly]);

  const handleRemoveConnection = useCallback((connectionId: string) => {
      if (isViewOnly) return; // View-only users cannot remove connections
      setConnections(prev => prev.filter(c => c.id !== connectionId));
  }, [isViewOnly]);

  // Auto-save status indicator callback - can be called by nodes
  const showAutoSaveIndicator = useCallback((type: 'local' | 'cloud' | 'both', message: string) => {
    logger.debug('Sync', '📥 Auto-save indicator:', type, message);
    
    // Clear any existing timeout
    if (autoSaveStatusTimeoutRef.current) {
      clearTimeout(autoSaveStatusTimeoutRef.current);
    }
    
    // Show indicator
    setAutoSaveStatus({
      isActive: true,
      type,
      message,
      timestamp: Date.now()
    });
    
    // Auto-hide after 2 seconds (shorter since we just show "Autosave")
    autoSaveStatusTimeoutRef.current = setTimeout(() => {
      setAutoSaveStatus(prev => ({ ...prev, isActive: false }));
    }, 2000);
  }, []);

  const hideAutoSaveIndicator = useCallback(() => {
    if (autoSaveStatusTimeoutRef.current) {
      clearTimeout(autoSaveStatusTimeoutRef.current);
    }
    setAutoSaveStatus(prev => ({ ...prev, isActive: false }));
  }, []);

  // Comprehensive auto-save function for generated files
  const handleAutoSaveFile = useCallback(async (
    fileData: string | Blob,
    fileName: string,
    category: 'Generated_Images' | 'Uploaded_Images' | 'Videos' | 'Audio' | 'Sketches' | 'Uploads' = 'Generated_Images',
    fileExtension: string = 'png'
  ) => {
    const localEnabled = await isAutoSaveReady(user?.sub);
    
    if (!localEnabled) {
      return; // No auto-save enabled
    }
    
    type SaveResult = { type: string; success: boolean; error?: string };
    const tasks: Promise<SaveResult>[] = [];
    
    // Show initial status
    showAutoSaveIndicator('local', `Saving ${fileName}...`);
    
    // Add local save task if enabled
    if (localEnabled) {
      tasks.push(
        autoSaveFile(currentProjectName, category, fileData, fileExtension, fileName, user?.sub)
          .then((result: any) => ({ type: 'local', success: result.success, error: result.error }))
      );
    }
    
    try {
      const results = await Promise.all(tasks);
      const localResult = results.find((r: SaveResult) => r.type === 'local');
      
      if (localResult && localResult.success) {
        showAutoSaveIndicator('local', `Saved ${fileName}`);
      } else {
        // Show error for failed saves
        const errors = results.filter((r: SaveResult) => !r.success).map((r: SaveResult) => r.error).filter(Boolean);
        showAutoSaveIndicator('local', `Save failed: ${errors[0] || 'Unknown error'}`);
      }
      
    } catch (error: any) {
      console.error('Auto-save failed:', error);
      showAutoSaveIndicator('local', `Save failed: ${error.message}`);
    }
  }, [user, currentProjectName, showAutoSaveIndicator]);

  // ── The local folder caught up with the cloud (utils/folderCatchUp.ts) ──────
  // A workspace's files that are only in the cloud (made while no folder was connected, or on
  // another device) are copied into the folder in the background, each downloaded once: when a
  // workspace opens with a folder connected, and when a folder is connected with a workspace
  // open. Files already in the folder are left alone, and nothing is asked of the person.
  const catchUpHistoryRef = useRef(history);
  useEffect(() => { catchUpHistoryRef.current = history; }, [history]);
  useEffect(() => {
    const userId = user?.sub;
    if (!userId || !currentProjectId || isLocalWorkspaceMode || isLoadingWorkspace || !autoSaveEnabled) return;
    const controller = new AbortController();
    const workspaceName = currentProjectName;
    // A few seconds after the canvas is up, so the opening itself is not slowed
    const timer = setTimeout(() => {
      void (async () => {
        const entries = workspaceCatchUpEntries(nodesRef.current, catchUpHistoryRef.current, workspaceName, projectOwnerId);
        if (entries.length === 0) return;
        const result = await catchUpFolder({
          entries, userId, signal: controller.signal,
          onProgress: ({ done, total, filename }) => { if (!controller.signal.aborted) showAutoSaveIndicator('local', `Copying ${done + 1} of ${total} to your folder: ${filename}`); },
        });
        if (controller.signal.aborted) return;
        // The save chip only spins; what came of it is said once, as a toast
        const summary = catchUpSummary(result);
        if (summary) showAutoSaveIndicator('local', summary);
        if (result.copied > 0) toast.success('Copied to your folder', summary ?? undefined);
        else if (result.failed > 0) toast.warning('Some files were not copied to your folder', summary ?? undefined);
      })().catch((e) => console.warn('[folder] catching up with the cloud failed:', e));
    }, 4000);
    return () => { clearTimeout(timer); controller.abort(); };
  }, [user?.sub, currentProjectId, currentProjectName, isLocalWorkspaceMode, isLoadingWorkspace, autoSaveEnabled, folderRound, projectOwnerId, showAutoSaveIndicator, toast]);

  /** Every file of the library into the folder, for the folder window: what the registry lists, under each file's workspace */
  const copyCloudFilesToFolder = useCallback(async (onProgress: (progress: CatchUpProgress) => void, signal: AbortSignal): Promise<CatchUpResult> => {
    if (!user) return { copied: 0, present: 0, failed: 0, unknown: 0, stopped: true };
    const files = await listUserFiles(user.sub);
    return catchUpFolder({ entries: libraryCatchUpEntries(files), userId: user.sub, onProgress, signal });
  }, [user]);

  /** Append a lightweight history entry for a finished generation or a new upload (deduplicated by timestamp). */
  const recordGeneration = useCallback((type: NodeType, newData: NodeData) => {
    setTimeout(() => {
      setHistory(h => {
        if (h.some(item => item.timestamp === newData.timestamp)) return h;
        // Lightweight history format: metadata plus file references, never base64
        return [...h, {
          id: generateId(),
          type,
          timestamp: newData.timestamp || Date.now(),
          title: newData.title,
          prompt: newData.prompt,
          cost: newData.cost,
          imageRef: newData.generatedImageUri || newData.imageUrl,
          videoRef: newData.videoUri,
        }];
      });
    }, 0);
  }, []);

  // Image Ref nodes are retired: any that arrive (an older workspace, a paste, a collaborator's
  // sync, the history) become Reference nodes, and are saved that way unless this is view-only
  useEffect(() => {
    if (!hasLegacyNodes(nodes)) return;
    setNodes(prev => {
      const migrated = migrateLegacyNodes(prev);
      if (migrated !== prev && !isViewOnly) {
        migrated.forEach((node, i) => { if (node !== prev[i]) dirtyNodeIdsRef.current.add(node.id); });
      }
      return migrated;
    });
  }, [nodes, isViewOnly]);

  const handleNodeDataChange = useCallback((id: string, newData: NodeData) => {
    if (isViewOnly) return; // View-only users cannot modify node data
    dirtyNodeIdsRef.current.add(id);
    setNodes((prev) => {
      return prev.map((node) => {
        if (node.id !== id) return node;
        let newSize = node.size;
        // Resize SHARE_NODE when image dimensions are detected
        if (node.type === NodeType.SHARE_NODE && newData.originalWidth && newData.originalHeight && 
            (newData.originalWidth !== node.data.originalWidth || newData.originalHeight !== node.data.originalHeight)) {
            const aspectRatio = newData.originalWidth / newData.originalHeight;
            const currentWidth = node.size?.width || 320;
            // Calculate image preview height (use ~50% of node for image, rest for caption/buttons)
            const imagePreviewHeight = Math.min(currentWidth / aspectRatio, 300);
            // Add space for caption (~100px), char counter (~24px), and buttons (~80px)
            const nonImageHeight = 220;
            const newHeight = imagePreviewHeight + nonImageHeight;
            newSize = { width: currentWidth, height: Math.max(newHeight, 400) };
        }
        
        const isGenerationComplete = newData.generationStatus === 'completed' && node.data.generationStatus !== 'completed';
        if (isGenerationComplete) recordGeneration(node.type, newData);
        return { ...node, data: newData, size: newSize };
      });
    });
  }, [isViewOnly, recordGeneration]);

  /**
   * Merge a partial update into a node's live data. Used by work that finishes long
   * after it started (a background upload swapping a blob URL for its file reference):
   * the guard sees the node's current data, so a late result never overwrites newer
   * edits and is dropped if the node has moved on. A history entry recorded against
   * the blob URL is pointed at the file reference at the same time.
   */
  const handleNodeDataPatch = useCallback((id: string, patch: Partial<NodeData>, guard?: (current: NodeData) => boolean): boolean => {
    if (isViewOnly) return false;
    const target = nodesRef.current.find((node) => node.id === id);
    if (!target || (guard && !guard(target.data))) return false;
    dirtyNodeIdsRef.current.add(id);
    const merged = { ...target.data, ...patch };
    if (merged.generationStatus === 'completed' && target.data.generationStatus !== 'completed') recordGeneration(target.type, merged);
    setNodes((prev) => prev.map((node) => {
      if (node.id !== id || (guard && !guard(node.data))) return node;
      return { ...node, data: { ...node.data, ...patch } };
    }));
    return true;
  }, [isViewOnly, recordGeneration]);

  /** A node renamed in its header (Image and Video nodes): its new name, on its live data */
  const handleNodeRename = useCallback((id: string, title: string) => {
    handleNodeDataPatch(id, { title, timestamp: Date.now() });
  }, [handleNodeDataPatch]);

  // A generation keeps going when the user switches workspace: its result is held for its own
  // workspace instead of being lost, and put on its node when that workspace is open again
  const nodePatcher = useHeldNodeResults({
    workspaceKey: currentProjectId || currentLocalWorkspaceId || null,
    nodes,
    ready: !isLoadingWorkspace,
    applyPatch: handleNodeDataPatch,
    // From the click that leaves a workspace until the leave is done: its save does not hold what arrives then
    leaving: switchingRef,
  });

  const handleSpawnScriptNodes = useCallback((parentId: string, items: any[], type: NodeType = NodeType.TEXT_PROMPT) => {
      setNodes(prev => {
          const parentNode = prev.find(n => n.id === parentId);
          if (!parentNode) return prev;
          const newNodes: CanvasNode[] = [];
          const newConnections: Connection[] = [];
          const cardWidth = 320;
          
          // Calculate start position relative to parent
          let startX: number;
          let startY: number;
          
          if (type === NodeType.TEXT_PROMPT) {
             startX = parentNode.position.x + (parentNode.size?.width || 400) + 100;
             startY = parentNode.position.y;
          } else {
             startX = parentNode.position.x;
             startY = parentNode.position.y + (parentNode.size?.height || 600) + 100;
          }

          // Calculate sizes for all items first
          const nodeSizes = items.map((item: any) => {
              if (type === NodeType.TEXT_PROMPT) {
                  const text = item.visualDescription || item.description || "";
                  const charsPerLine = 42;
                  const lines = Math.ceil(text.length / charsPerLine);
                  const calculatedHeight = Math.max(160, (lines * 18) + 80 + 24);
                  return { width: cardWidth, height: calculatedHeight };
              } else {
                  return { width: 300, height: 350 };
              }
          });

          // Find empty positions for all nodes
          const layout = type === NodeType.TEXT_PROMPT ? 'vertical' : 'grid';
          const positions = findEmptyPositionsForMultiple(
              startX,
              startY,
              nodeSizes,
              prev,
              layout as 'vertical' | 'grid',
              3 // gridColumns for entity nodes
          );

          items.forEach((item: any, index: number) => {
              const nodeId = generateId();
              let nodeData: NodeData = { title: 'Untitled', timestamp: Date.now() };
              const size = nodeSizes[index];
              const position = positions[index];

              if (type === NodeType.TEXT_PROMPT) {
                  const text = item.visualDescription || item.description || "";
                  nodeData = { title: `Scene ${item.sceneNumber || (index+1)}`, timestamp: Date.now(), textPromptData: { sceneNumber: item.sceneNumber || (index+1).toString(), visualDescription: text, cameraAngle: item.cameraAngle || item.shotType || "Wide" } };
                  newNodes.push({ id: nodeId, type, position, size, data: nodeData });
              } else {
                  nodeData = { title: item.name, timestamp: Date.now(), entityData: { name: item.name, description: item.description } };
                  newNodes.push({ id: nodeId, type, position, size, data: nodeData });
              }

              if (type === NodeType.TEXT_PROMPT) {
                  newConnections.push({ id: generateId(), sourceId: parentId, targetId: nodeId });
              }
          });
          setTimeout(() => setConnections(prevConns => [...prevConns, ...newConnections]), 0);
          return [...prev, ...newNodes];
      });
  }, []);

  const handleCanvasTextDrop = useCallback((text: string, position: { x: number; y: number }) => {
      const nodeId = generateId();
      const charsPerLine = 42;
      const lines = Math.ceil(text.length / charsPerLine);
      const calculatedHeight = Math.max(160, (lines * 18) + 80 + 24);
      const size = { width: 320, height: calculatedHeight };
      
      // Track node position for smooth pan
      let nodePosition: { x: number; y: number } | null = null;
      
      // Find empty position for dropped text node
      setNodes(prev => {
          const emptyPosition = findEmptyPosition(position.x, position.y, size.width, size.height, prev);
          nodePosition = emptyPosition;
          const newNode: CanvasNode = {
              id: nodeId, type: NodeType.TEXT_PROMPT, position: emptyPosition, size,
              data: { title: 'Extracted Script', timestamp: Date.now(), textPromptData: { sceneNumber: 'Excerpt', visualDescription: text, cameraAngle: 'N/A' } }
          };
          return [...prev, newNode];
      });
      setSelectedNodeId(nodeId);
      
      // Smooth pan to dropped node
      requestAnimationFrame(() => {
          if (nodePosition && canvasRef.current?.panToNode) {
              canvasRef.current.panToNode(nodePosition.x, nodePosition.y, size.width, size.height);
          }
      });
  }, []);

  // Handle file drop from Storage Browser
  const handleStorageFileDrop = useCallback((file: { name: string; url: string; type: 'image' | 'video' | 'file'; fullPath: string }, position: { x: number; y: number }) => {
      const nodeId = generateId();
      
      if (file.type === 'image') {
          // Create an Image Viewer node for images
          const img = new Image();
          img.onload = () => {
              const aspectRatio = img.naturalWidth / img.naturalHeight;
              const width = 320;
              const height = Math.min(width / aspectRatio + 50, 600);
              
              // Track position for smooth pan
              let nodePosition: { x: number; y: number } | null = null;
              
              // Find empty position for image node
              setNodes(prev => {
                  const emptyPosition = findEmptyPosition(
                      position.x - width / 2,
                      position.y - height / 2,
                      width,
                      height,
                      prev
                  );
                  nodePosition = emptyPosition;
                  const newNode: CanvasNode = {
                      id: nodeId,
                      type: NodeType.MEDIA_REFERENCE,
                      position: emptyPosition,
                      size: { width, height },
                      data: {
                          title: file.name,
                          referenceKind: 'image',
                          imageUrl: file.url,
                          originalWidth: img.naturalWidth,
                          originalHeight: img.naturalHeight,
                          timestamp: Date.now()
                      }
                  };
                  return [...prev, newNode];
              });
              setSelectedNodeId(nodeId);
              
              // Smooth pan to dropped image
              requestAnimationFrame(() => {
                  if (nodePosition && canvasRef.current?.panToNode) {
                      canvasRef.current.panToNode(nodePosition.x, nodePosition.y, width, height);
                  }
              });
          };
          img.onerror = () => {
              // Fallback if image fails to load dimensions
              const width = 320;
              const height = 320;
              
              let nodePosition: { x: number; y: number } | null = null;
              
              setNodes(prev => {
                  const emptyPosition = findEmptyPosition(
                      position.x - width / 2,
                      position.y - height / 2,
                      width,
                      height,
                      prev
                  );
                  nodePosition = emptyPosition;
                  const newNode: CanvasNode = {
                      id: nodeId,
                      type: NodeType.MEDIA_REFERENCE,
                      position: emptyPosition,
                      size: { width, height },
                      data: { title: file.name, referenceKind: 'image', imageUrl: file.url, timestamp: Date.now() }
                  };
                  return [...prev, newNode];
              });
              setSelectedNodeId(nodeId);
              
              // Smooth pan to dropped image
              requestAnimationFrame(() => {
                  if (nodePosition && canvasRef.current?.panToNode) {
                      canvasRef.current.panToNode(nodePosition.x, nodePosition.y, width, height);
                  }
              });
          };
          img.src = file.url;
      } else if (file.type === 'video') {
          // A Reference node holding the video
          const width = 400;
          const height = 280;
          
          let nodePosition: { x: number; y: number } | null = null;
          
          setNodes(prev => {
              const emptyPosition = findEmptyPosition(
                  position.x - width / 2,
                  position.y - height / 2,
                  width,
                  height,
                  prev
              );
              nodePosition = emptyPosition;
              const newNode: CanvasNode = {
                  id: nodeId,
                  type: NodeType.MEDIA_REFERENCE,
                  position: emptyPosition,
                  size: { width, height },
                  data: {
                      title: file.name,
                      referenceKind: 'video',
                      videoUri: file.url,
                      videoFileName: file.name,
                      timestamp: Date.now()
                  }
              };
              return [...prev, newNode];
          });
          setSelectedNodeId(nodeId);
          
          // Smooth pan to dropped video
          requestAnimationFrame(() => {
              if (nodePosition && canvasRef.current?.panToNode) {
                  canvasRef.current.panToNode(nodePosition.x, nodePosition.y, width, height);
              }
          });
      }
  }, []);

  const handleConnectStart = useCallback((id: string, e: React.PointerEvent) => {
      if (isViewOnly) return; // View-only users cannot create connections
      setPendingConnection({ sourceId: id, mousePos: { x: e.clientX, y: e.clientY } });
  }, [isViewOnly]);

  const handleConnectEnd = useCallback((targetId: string) => {
      if (isViewOnly) return; // View-only users cannot create connections
      if (pendingConnection) {
          const sourceId = pendingConnection.sourceId;
          if (sourceId !== targetId && !connections.some(c => c.sourceId === sourceId && c.targetId === targetId)) {
              setConnections(prev => [...prev, { id: generateId(), sourceId, targetId }]);
          }
          setPendingConnection(null);
      }
  }, [pendingConnection, connections, isViewOnly]);

  // Track pointer movement while dragging a connection so the preview line follows the cursor
  useEffect(() => {
      if (!pendingConnection) return;

      const handleMove = (e: PointerEvent) => {
          setPendingConnection(prev => prev ? { ...prev, mousePos: { x: e.clientX, y: e.clientY } } : null);
      };

      const handleUp = () => setPendingConnection(null);

      window.addEventListener('pointermove', handleMove);
      window.addEventListener('pointerup', handleUp);

      return () => {
          window.removeEventListener('pointermove', handleMove);
          window.removeEventListener('pointerup', handleUp);
      };
  }, [pendingConnection]);

  // The Edit page keeps its timeline in one node that is saved with the workspace but never drawn on the canvas
  const canvasNodes = useMemo(() => nodes.filter(node => !isWorkspaceEditNode(node)), [nodes]);
  // A workspace with nothing in it yet: no nodes, or only the empty Reference card a new workspace starts with
  const canvasIsEmpty = useMemo(
    () => canvasNodes.every(node => node.type === NodeType.MEDIA_REFERENCE && !node.data?.imageUrl && !node.data?.videoUrl && !node.data?.audioUrl),
    [canvasNodes],
  );
  // A workspace that opens with only that card is brought to the middle of the view, clear of the
  // library panel, so the one thing on it is the first thing seen; once per workspace opened
  const centredFor = useRef<string | null>(null);
  useEffect(() => {
    const key = currentProjectId || currentLocalWorkspaceId || null;
    if (!key || isLoadingWorkspace || workspaceView !== 'nodes' || centredFor.current === key) return;
    if (canvasNodes.length !== 1 || !canvasIsEmpty) return;
    centredFor.current = key;
    const node = canvasNodes[0];
    requestAnimationFrame(() => canvasRef.current?.panToNode?.(node.position.x, node.position.y, node.size?.width || 320, node.size?.height || 300));
  }, [currentProjectId, currentLocalWorkspaceId, isLoadingWorkspace, workspaceView, canvasNodes, canvasIsEmpty]);
  const workspaceEditData = useMemo(() => nodes.find(isWorkspaceEditNode)?.data, [nodes]);
  const workspaceVideos = useMemo(() => workspaceView === 'edit' ? collectWorkspaceVideos(nodes, history) : [], [workspaceView, nodes, history]);
  const workspaceAudio = useMemo(() => workspaceView === 'edit' ? collectWorkspaceAudio(nodes) : [], [workspaceView, nodes]);

  const handleWorkspaceEditChange = useCallback((newData: NodeData) => {
    if (isViewOnly) return;
    const data: NodeData = { ...newData, title: 'Edit', timestamp: Date.now() };
    dirtyNodeIdsRef.current.add(WORKSPACE_EDIT_NODE_ID);
    // Created by the first edit; the fixed id keeps it to one per workspace, also between collaborators
    setNodes(prev => prev.some(isWorkspaceEditNode)
      ? prev.map(node => isWorkspaceEditNode(node) ? { ...node, data } : node)
      : [...prev, { id: WORKSPACE_EDIT_NODE_ID, type: NodeType.QUICK_EDITOR, position: { x: 0, y: 0 }, size: { width: 980, height: 620 }, data }]);
  }, [isViewOnly]);

  const handleWorkspaceViewChange = useCallback((view: WorkspaceView) => {
    if (view !== 'nodes') setSelectedNodeId(null);
    setWorkspaceView(view);
    rememberWorkspaceView(workspaceKeyRef.current, view);
  }, []);

  const addNode = useCallback((type: NodeType, title: string, size: {width: number, height: number}, data: Partial<NodeData> = {}) => {
      // Check node limit for free users
      const currentNodeCount = canvasNodes.length;
      if (!featureAccess.canAddNode(currentNodeCount)) {
          // Show upgrade page/modal instead of adding node
          if (user) {
              setShowSubscriptionPage(true);
          } else {
              setShowSubscriptionModal(true);
          }
          return;
      }
      
      // Track the new node position for smooth pan
      let newNodePosition: { x: number; y: number } | null = null;
      
      // Generate ID before setNodes so we can mark it dirty for cloud sync
      const newNodeId = generateId();
      dirtyNodeIdsRef.current.add(newNodeId);
      
      setNodes(prev => {
          const t = transformRef.current;
          // Calculate viewport center in canvas coordinates
          const centerX = (window.innerWidth / 2 - t.x) / t.scale;
          const centerY = (window.innerHeight / 2 - t.y) / t.scale;
          
          // Find empty position that doesn't overlap existing nodes
          const { x, y } = findEmptyPosition(
              centerX - (size.width / 2),
              centerY - (size.height / 2),
              size.width,
              size.height,
              prev
          );
          
          // Store position for panning
          newNodePosition = { x, y };
          
          const defaultData: NodeData = { title, timestamp: Date.now() };
          if (type === NodeType.SKETCH_PAD) {
              // ui-ok: the paper and brush colours of the sketch canvas are drawn data, not a style
              defaultData.sketchConfig = { aspectRatio: '1:1', backgroundColor: '#ffffff', brushColor: '#000000', brushSize: 4 };
          }
          if ([NodeType.CHARACTER_NODE, NodeType.PROP_NODE, NodeType.LOCATION_NODE].includes(type)) {
               defaultData.entityData = { name: title, description: '' };
          }
          if (type === NodeType.WRITE_NODE) {
              defaultData.writeData = { draft: '', refined: '', modelId: 'gemini-2.5-flash' };
          }
          if (type === NodeType.STORYBOARD_NODE) {
              // Initialize storyboard with 6x3 grid of empty boxes
              const boxes = Array.from({ length: 18 }, () => ({
                  id: Math.random().toString(36).substr(2, 9),
                  prompt: '',
                  status: 'idle' as const,
              }));
              defaultData.storyboardConfig = {
                  model: 'gemini-2.5-flash-image',
                  aspectRatio: '16:9',
                  columns: 6,
                  rows: 3,
                  boxes,
              };
          }
          const newNode: CanvasNode = { id: newNodeId, type, position: { x, y }, size, data: { ...defaultData, ...data } };
          return [...prev, newNode];
      });
      
      // Smooth pan to the new node after state update
      requestAnimationFrame(() => {
          if (newNodePosition && canvasRef.current?.panToNode) {
              canvasRef.current.panToNode(newNodePosition.x, newNodePosition.y, size.width, size.height);
          }
      });
  }, [canvasNodes.length, featureAccess, user]);

  /** A prompt card from the Ideas page as a node on the canvas: the paste as its prompt, with the generator and the settings it was written for */
  const handlePromptToCanvas = useCallback((card: PromptToCanvas) => {
    const video = card.node === 'video';
    const model = AI_MODELS[card.model]?.type === card.node ? AI_MODELS[card.model] : AI_MODELS[DEFAULT_GENERATOR[card.node]];
    const data: Partial<NodeData> = video
      ? { prompt: card.prompt, videoConfig: { model: model.id, ...videoSettingsOf({ aspectRatio: card.aspectRatio, duration: card.duration }, model) } }
      : { prompt: card.prompt, imageGenConfig: { model: model.id, ...imageSettingsOf({ aspectRatio: card.aspectRatio }, model) } };
    handleWorkspaceViewChange('nodes');
    addNode(video ? NodeType.VIDEO_GENERATOR : NodeType.IMAGE_GENERATOR, card.title || (video ? 'Video' : 'Image'), video ? { width: 400, height: 620 } : { width: 350, height: 500 }, data);
  }, [addNode, handleWorkspaceViewChange]);

  const handleRestoreNode = (item: HistoryItem | LightHistoryItem) => {
      const centerX = (window.innerWidth / 2 - transform.x) / transform.scale;
      const centerY = (window.innerHeight / 2 - transform.y) / transform.scale;
      let size = { width: 300, height: 300 };
      if (item.type === NodeType.VIDEO_GENERATOR) size = { width: 400, height: 620 };
      if (item.type === NodeType.IMAGE_GENERATOR) size = { width: 350, height: 500 };
      if (item.type === NodeType.SKETCH_PAD) size = { width: 400, height: 490 };
      if (item.type === NodeType.SCRIPT_PROCESSOR) size = { width: 350, height: 250 };
      
      // Build node data from either format
      let nodeData: NodeData;
      
      // An old entry holds the node's whole data; a light one only its title, prompt, cost and files
      if (isFullDataHistoryItem(item)) {
          // Legacy format - use data object directly
          nodeData = { ...item.data };
          if (item.type === NodeType.IMAGE_VIEWER && item.data.originalWidth && item.data.originalHeight) {
               const aspectRatio = item.data.originalWidth / item.data.originalHeight;
               size = { width: 320, height: 320 / aspectRatio + 50 };
          }
      } else {
          // New LightHistoryItem format - reconstruct data object
          const lightItem = { ...historyItemFields(item), timestamp: item.timestamp };
          nodeData = {
              title: lightItem.title,
              prompt: lightItem.prompt,
              cost: lightItem.cost,
              timestamp: lightItem.timestamp,
          };
          
          // Set appropriate image/video reference
          if (lightItem.imageRef) {
              if (item.type === NodeType.IMAGE_GENERATOR) {
                  nodeData.generatedImageUri = lightItem.imageRef;
                  nodeData.generatedImages = [lightItem.imageRef];
                  nodeData.generationStatus = 'completed';
              } else {
                  nodeData.imageUrl = lightItem.imageRef;
              }
          }
          if (lightItem.videoRef) {
              nodeData.videoUri = lightItem.videoRef;
              nodeData.generationStatus = 'completed';
          }
      }
      
      // Find empty position for restored node
      const position = findEmptyPosition(
          centerX - size.width / 2,
          centerY - size.height / 2,
          size.width,
          size.height,
          nodes
      );
      
      const newNode: CanvasNode = { id: generateId(), type: item.type, position, size: size, data: nodeData };
      dirtyNodeIdsRef.current.add(newNode.id);
      setNodes(prev => [...prev, newNode]);
      setSelectedNodeId(newNode.id);
      setIsGalleryOpen(false);
      
      // Smooth pan to the restored node
      requestAnimationFrame(() => {
          if (canvasRef.current?.panToNode) {
              canvasRef.current.panToNode(position.x, position.y, size.width, size.height);
          }
      });
  };

  // What reaches a node from others (utils/nodeInputs.ts, shared with the Claude connector so it reports what is sent)
  /** Pictures connected into a node; a Reference node's tag comes along (Kling tells characters from scenes by it). */
  const getInputImagesForNode = (nodeId: string) => connectedImageInputs(nodeId, nodes, connections);
  const getInputEntitiesForNode = (nodeId: string) => connectedEntities(nodeId, nodes, connections);
  /** Audio nodes connected into a node (used by the Video node as music or voice/SFX references). */
  const getInputAudioForNode = (nodeId: string) => connectedAudioInputs(nodeId, nodes, connections);
  /** Video nodes connected into a node (used by the Video node as reference clips to continue or restyle). */
  const getInputVideosForNode = (nodeId: string) => connectedVideoInputs(nodeId, nodes, connections);
  /** This workspace's Reference nodes, then the project's other workspaces' ones (each media once). */
  const referenceChoicesInProject = () => {
      const here = collectReferenceNodes(nodes);
      return [...here, ...sharedReferenceChoices(projectReferences.sources, here)];
  };
  // An Image or Video node's references: what is connected, then the Reference nodes chosen from its list
  const inputImagesWithPicked = (node: CanvasNode) => imageReferenceInputs(node, nodes, connections, projectReferences.findShared);
  const inputAudioWithPicked = (node: CanvasNode) => audioReferenceInputs(node, nodes, connections, projectReferences.findShared);
  const inputVideosWithPicked = (node: CanvasNode) => videoReferenceInputs(node, nodes, connections, projectReferences.findShared);
  /** Removing a reference deletes its connection, or takes a chosen one off the node. */
  const removeNodeInput = (nodeId: string, inputId: string) => {
      if (!isPickedInputId(inputId)) { handleRemoveConnection(inputId); return; }
      const current = nodesRef.current.find(n => n.id === nodeId)?.data.pickedReferences;
      handleNodeDataPatch(nodeId, { pickedReferences: withoutPicked(current, inputId) });
  };

  const getInputTextForNode = (nodeId: string) => connectedPromptSource(nodeId, nodes, connections)?.text ?? '';
  
  const totalUsageCost = useMemo(() => historyTotalCost(history), [history]);

  // --- RENDER ---
  // Determine if we should show loading overlay
  // Show loading when: 1) auth not checked yet, 2) user exists but subscription not loaded yet, 3) workspace is loading
  const showLoadingOverlay = !authChecked || (user && !subscriptionLoaded) || (user && isLoadingWorkspace) || isNavigatingHome || !!switchingTo;
  
  // Legal pages - can be shown from any context
  // 404 page — shown for any unknown route
  if (showNotFoundPage) {
    return <NotFoundPage />;
  }
  
  if (showTermsPage) {
    return (
      <TermsPage
        onBack={() => setShowTermsPage(false)}
        onPrivacy={() => { setShowTermsPage(false); setShowPrivacyPage(true); }}
        onAbout={() => { setShowTermsPage(false); setShowAboutPage(true); }}
      />
    );
  }
  
  if (showPrivacyPage) {
    return (
      <PrivacyPage
        onBack={() => setShowPrivacyPage(false)}
        onTerms={() => { setShowPrivacyPage(false); setShowTermsPage(true); }}
        onAbout={() => { setShowPrivacyPage(false); setShowAboutPage(true); }}
      />
    );
  }
  
  if (showBugReviewPage) {
    return <BugReviewPage onBack={() => { setShowBugReviewPage(false); setShowBugsPage(true); }} />;
  }

  if (showBugsPage) {
    return (
      <BugsPage
        user={user}
        isAdmin={isAdmin}
        onBack={() => setShowBugsPage(false)}
        onSignIn={() => setShowBugsPage(false)}
        onReview={() => { setShowBugsPage(false); setShowBugReviewPage(true); }}
      />
    );
  }

  if (showAboutPage) {
    return (
      <AboutPage
        onBack={() => setShowAboutPage(false)}
        onTerms={() => { setShowAboutPage(false); setShowTermsPage(true); }}
        onPrivacy={() => { setShowAboutPage(false); setShowPrivacyPage(true); }}
      />
    );
  }
  
  // Show HomePage (marketing page) for unauthenticated users
  // HomePage now handles auth internally with animated split-screen
  if (!showLoadingOverlay && !user) {
    return (
      <>
        <HomePage
          onGetStarted={() => {}} // Legacy - handled internally now
          onSignIn={() => {}}     // Legacy - handled internally now
          onLoginSuccess={finishLogin}
          isFirebaseConfigured={isFirebaseConfigured}
          onShowSubscriptionModal={() => setShowSubscriptionModal(true)}
          openAuthRequest={landingAuthRequest}
          onTerms={() => setShowTermsPage(true)}
          onPrivacy={() => setShowPrivacyPage(true)}
          onAbout={() => setShowAboutPage(true)}
        />
        {/* Pro for someone not signed in, reachable from the landing page's Pricing / See Pro
            plans / Get Pro actions: the plans, then "Sign in to pay" opens the page's own
            sign-in panel. The main render branch below never mounts while this early return is active. */}
        <PaymentModal
          isOpen={showSubscriptionModal}
          onClose={() => setShowSubscriptionModal(false)}
          user={null}
          onSignIn={() => setLandingAuthRequest(n => n + 1)}
        />
      </>
    );
  }
  
  // Show the Workspaces library for authenticated users without an open workspace
  if (!showLoadingOverlay && showLandingPage && user) {
    return (
      <>
        <WorkspacesPage
          user={user}
          isPro={featureAccess.isPro}
          jump={libraryJump}
          onJumped={() => setLibraryJump(null)}
          workspaces={libraryWorkspaces}
          folders={folders}
          sharedFolders={sharedFolders}
          onLeaveSharedProject={handleLeaveSharedLibraryProject}
          projectInvitations={projectInvitations}
          onAnswerProjectInvitation={handleAnswerProjectInvitation}
          projectCopies={projectCopies}
          onSendProjectCopy={handleSendProjectCopy}
          onAnswerProjectCopy={handleAnswerProjectCopy}
          onCancelProjectCopy={handleCancelProjectCopy}
          canShareProjects={featureAccess.limits.cloudSyncEnabled}
          localFolderName={workspaceAutoSave ? autoSaveFolderName : null}
          isLoading={!libraryLoaded}
          loadError={libraryError}
          onOpenWorkspace={(id, isLocal) => handleOpenWorkspace(id, isLocal)}
          onCreateWorkspace={handleCreateWorkspaceInFolder}
          onImportFile={handleImportFile}
          onRenameWorkspace={handleRenameWorkspaceFromLibrary}
          onMoveWorkspaces={handleMoveWorkspacesFromLibrary}
          onToggleFavorite={handleToggleFavoriteFromLibrary}
          onDeleteWorkspace={handleDeleteWorkspaceFromLibrary}
          onLeaveSharedWorkspace={handleLeaveSharedWorkspaceFromLibrary}
          onCreateFolder={handleCreateFolder}
          onRenameFolder={handleRenameFolder}
          onRecolorFolder={handleRecolorFolder}
          onMoveFolder={handleMoveFolder}
          onDeleteFolder={handleDeleteFolder}
          onRefresh={handleRefreshLibrary}
          onSignIn={() => setShowAuthModal(true)}
          onSignOut={async () => {
            try {
              // handleLogout also clears the open workspace (nodes, ids, name) so the
              // next account can never autosave the previous account's canvas.
              await handleLogout();
              setCurrentLocalWorkspaceId(null);
              setIsLocalWorkspaceMode(false);
              clearStoredLocations();
              setProjectList([]); // Clear projects on logout
              setLibraryWorkspaces([]);
              setFolders([]); setSharedFolders([]); setProjectInvitations([]); setProjectCopies({ sent: [], received: [] });
              setLibraryLoaded(false);
            } catch (e) {
              console.error('Sign out failed', e);
            }
          }}
          onSubscription={() => { setShowLandingPage(false); setShowSubscriptionPage(true); }}
          onAnalytics={() => { setShowLandingPage(false); setShowAnalyticsPage(true); }}
          onSettings={() => { setShowLandingPage(false); setShowSettingsPage(true); }}
          bell={bell}
        />
        {/* The canvas branch (below) is not mounted here, so "Open file…" needs its own picker */}
        <input type="file" ref={fileInputRef} accept=".json" className="hidden" aria-hidden="true" tabIndex={-1} onChange={handleImport} />
      </>
    );
  }
  
  if (showAnalyticsPage) {
    return (
      <AnalyticsPage
        nodes={nodes}
        history={history}
        recentFiles={recentFiles}
        onBack={() => { setShowAnalyticsPage(false); if (!currentProjectId && !currentLocalWorkspaceId) setShowLandingPage(true); }}
        user={user}
        isPro={featureAccess.isPro}
        onSubscription={() => { setShowAnalyticsPage(false); setShowSubscriptionPage(true); }}
        onSettings={() => { setShowAnalyticsPage(false); setShowSettingsPage(true); }}
        onSignOut={handleLogout}
      />
    );
  }
  
  return (
    <div className="w-screen h-screen flex flex-col bg-cream-100 overflow-hidden text-ink font-sans">
      {/* The workspace fills the window and every panel scrolls on its own; the window itself never scrolls */}
      <PageScrollLock />

      {/* Workspace Loading Overlay - Show during auth check, subscription check, or workspace loading */}
      {showLoadingOverlay && (
        <LoadingScreen
          scene={!authChecked ? 'start' : isNavigatingHome ? 'home' : switchingTo ? 'switch' : 'open'}
          title={!authChecked ? 'Starting up' : isNavigatingHome ? 'Saving your workspace' : switchingTo ? `Opening ${switchingTo.name}` : 'Opening your workspace'}
          detail={!authChecked ? 'Checking your sign-in' : isNavigatingHome ? 'Making a thumbnail and saving your progress' : (user && !subscriptionLoaded) ? 'Checking your account' : switchingTo?.step === 'saving' ? 'Keeping your place here' : 'Loading nodes and media'}
        />
      )}
      
      {/* Workspace Manager Modal - works for all users */}
      {showWorkspaceModal && (
          <div className="ns-modal-scrim z-[120]">
              {/* The shared dialog shell: a header row, a body that scrolls, the action at the bottom right */}
              <div className="ns-modal-sheet ns-modal-sheet-flush max-w-lg">
                   <div className="pl-6 pr-5 pt-5 pb-4 border-b border-cream-300 flex justify-between items-center gap-4">
                       <h3 className="ns-t-dialog">My workspaces</h3>
                       <button onClick={() => setShowWorkspaceModal(false)} className="ns-icon-btn" aria-label="Close"><X /></button>
                   </div>
                   <div className="px-3.5 py-3 flex-1 min-h-0 overflow-y-auto space-y-1">
                        {/* Local Workspaces Section */}
                        {localWorkspaces.length > 0 && (
                            <>
                                <div className="ns-t-label px-2.5 pt-2">
                                    {user ? 'Local workspaces' : 'Your workspaces'}
                                </div>
                                {localWorkspaces.map(ws => (
                                    <div key={ws.id} className="ns-row justify-between cursor-default" aria-current={currentLocalWorkspaceId === ws.id && isLocalWorkspaceMode ? 'true' : undefined}>
                                        <div className="flex-1 min-w-0 cursor-pointer" onClick={() => onceOffScreen(() => setShowWorkspaceModal(false), () => handleSwitchWorkspace(ws.id, true))}>
                                            <div className="flex items-center gap-2">
                                                <span className="ns-t-title text-inherit truncate">{ws.name}</span>
                                                <span className="ns-badge">
                                                    <Database size={12} /> Local
                                                </span>
                                            </div>
                                            <div className="ns-t-small">
                                                {ws.nodeCount} node{ws.nodeCount !== 1 ? 's' : ''} · {ws.updatedAt ? `Edited ${formatRelativeTime(ws.updatedAt)}` : 'Not saved yet'}
                                            </div>
                                        </div>
                                        <div className="flex items-center gap-1 shrink-0">
                                             {/* White on the lavender row: a lavender badge would have no shape there */}
                                             {currentLocalWorkspaceId === ws.id && isLocalWorkspaceMode && <span className="ns-badge bg-white text-lavender-deep">Active</span>}
                                             <button 
                                                 onClick={(e) => { e.stopPropagation(); handleDuplicateLocalWorkspace(ws.id); }} 
                                                 className="ns-icon-btn"
                                                 title="Duplicate"
                                                 aria-label="Duplicate"
                                             >
                                                 <Copy />
                                             </button>
                                             <button 
                                                 onClick={(e) => { e.stopPropagation(); handleDeleteLocalWorkspace(ws.id); }} 
                                                 className="ns-icon-btn ns-icon-btn-danger"
                                                 title="Delete"
                                                 aria-label="Delete"
                                             >
                                                 <Trash2 />
                                             </button>
                                        </div>
                                    </div>
                                ))}
                            </>
                        )}
                        
                        {/* Cloud Workspaces Section (only for logged-in users) */}
                        {user && projectList.length > 0 && (() => {
                            const myProjects = projectList.filter(p => !p.isShared);
                            const sharedProjects = projectList.filter(p => p.isShared);
                            return (
                            <>
                                {/* My Cloud Workspaces */}
                                {myProjects.length > 0 && (
                                <>
                                <div className="flex items-center justify-between px-2.5 pt-4">
                                    <div className="ns-t-label">Cloud workspaces</div>
                                    {featureAccess.limits.cloudSyncEnabled && (
                                        <button
                                            onClick={handleSyncAllToCloud}
                                            disabled={isCloudSyncing}
                                            className="ns-btn ns-btn-xs ns-btn-soft ns-btn-flat"
                                            title="Push all local-only workspaces to cloud"
                                        >
                                            {isCloudSyncing ? <Loader2 size={12} className="animate-spin" /> : <Upload size={12} />}
                                            {isCloudSyncing ? 'Syncing...' : 'Sync all'}
                                        </button>
                                    )}
                                </div>
                                {myProjects.map(proj => (
                                    <div key={proj.id} className="ns-row justify-between cursor-default" aria-current={currentProjectId === proj.id && !isLocalWorkspaceMode ? 'true' : undefined}>
                                        <div className="flex-1 min-w-0 cursor-pointer" onClick={() => onceOffScreen(() => setShowWorkspaceModal(false), () => handleSwitchWorkspace(proj.id, false))}>
                                            <div className="flex items-center gap-2">
                                                <span className="ns-t-title text-inherit truncate">{proj.name}</span>
                                                {proj.isLocal ? (
                                                    <span className="ns-badge">
                                                        <Database size={12} /> Local
                                                    </span>
                                                ) : (
                                                    <span className="ns-badge ns-badge-ok">
                                                        <Cloud size={12} /> Cloud
                                                    </span>
                                                )}
                                            </div>
                                            <div className="ns-t-small">{proj.lastSaved ? `Edited ${formatRelativeTime(proj.lastSaved)}` : 'Not saved yet'}</div>
                                        </div>
                                        <div className="flex items-center gap-1 shrink-0">
                                             {currentProjectId === proj.id && !isLocalWorkspaceMode && <span className="ns-badge bg-white text-lavender-deep">Active</span>}
                                             {proj.isLocal && featureAccess.limits.cloudSyncEnabled && (
                                                 <button 
                                                     onClick={async (e) => { 
                                                         e.stopPropagation();
                                                         const success = await uploadProjectToCloud(proj.id, user.sub);
                                                         if (success) {
                                                             await refreshProjectList();
                                                         } else {
                                                             toast.error('Upload failed', 'Failed to upload workspace to cloud. Please try again.');
                                                         }
                                                     }} 
                                                     className="ns-icon-btn"
                                                     title="Upload to cloud"
                                                     aria-label="Upload to cloud"
                                                 >
                                                     <Upload />
                                                 </button>
                                             )}
                                             <button onClick={() => handleDeleteProject(proj.id)} className="ns-icon-btn ns-icon-btn-danger" aria-label="Delete"><Trash2 /></button>
                                        </div>
                                    </div>
                                ))}
                                </>
                                )}

                                {/* Shared with Me Section */}
                                {sharedProjects.length > 0 && (
                                <>
                                <div className="flex items-center justify-between px-2.5 pt-4">
                                    <div className="ns-t-label flex items-center gap-1">
                                        <Users size={12} /> Shared with me
                                    </div>
                                </div>
                                {sharedProjects.map(proj => (
                                    <div key={proj.id} className="ns-row justify-between cursor-default" aria-current={currentProjectId === proj.id && !isLocalWorkspaceMode ? 'true' : undefined}>
                                        <div className="flex-1 min-w-0 cursor-pointer" onClick={() => onceOffScreen(() => setShowWorkspaceModal(false), () => handleSwitchWorkspace(proj.id, false))}>
                                            <div className="flex items-center gap-2">
                                                <span className="ns-t-title text-inherit truncate">{proj.name}</span>
                                                <span className="ns-badge">
                                                    <Share2 size={12} /> Shared
                                                </span>
                                                {proj.sharedPermission === 'edit' ? (
                                                    <span className="ns-badge">
                                                        <Edit2 size={12} /> Edit
                                                    </span>
                                                ) : (
                                                    <span className="ns-badge ns-badge-warn">
                                                        <Eye size={12} /> View
                                                    </span>
                                                )}
                                            </div>
                                            <div className="ns-t-small">
                                                {proj.sharedBy ? `Shared by ${proj.sharedBy}` : 'Shared workspace'}{proj.lastSaved ? ` · Edited ${formatRelativeTime(proj.lastSaved)}` : ''}
                                            </div>
                                        </div>
                                        <div className="flex items-center gap-1 shrink-0">
                                             {currentProjectId === proj.id && !isLocalWorkspaceMode && <span className="ns-badge bg-white text-lavender-deep">Active</span>}
                                             <button 
                                                 onClick={(e) => { e.stopPropagation(); handleLeaveSharedProject(proj.id, proj.name); }} 
                                                 className="ns-icon-btn"
                                                 title="Leave shared workspace"
                                                 aria-label="Leave shared workspace"
                                             >
                                                 <UserMinus />
                                             </button>
                                        </div>
                                    </div>
                                ))}
                                </>
                                )}
                            </>
                            );
                        })()}
                        
                        {/* Empty state */}
                        {localWorkspaces.length === 0 && (!user || projectList.length === 0) && (
                            <div className="ns-empty">
                                <span className="ns-mark ns-mark-lg ns-mark-lavender"><FolderOpen /></span>
                                <p className="ns-empty-title">No workspaces yet</p>
                                <p className="ns-empty-text">Create your first workspace to get started</p>
                            </div>
                        )}
                   </div>
                   <div className="pl-6 pr-5 py-4 border-t border-cream-300 flex justify-between items-center gap-3">
                       <div className="ns-t-small">
                           {!user ? (
                               <span className="flex items-center gap-1"><Database size={12} className="text-ink-faint" /> Browser storage</span>
                           ) : featureAccess.isPro ? (
                               <span className="flex items-center gap-1"><Cloud size={12} className="text-ok" /> Cloud sync enabled</span>
                           ) : (
                               <span className="flex items-center gap-1"><Database size={12} className="text-ink-faint" /> Local storage only</span>
                           )}
                       </div>
                       <button onClick={handleCreateLocalWorkspace} className="ns-btn ns-btn-sm ns-btn-primary ns-btn-flat"><Plus size={14} /> New workspace</button>
                   </div>
              </div>
          </div>
      )}

      {/* Settings Page */}
      {showSettingsPage && (
        <SettingsPage
          user={user}
          isAdmin={isAdmin}
          onBack={() => { setShowSettingsPage(false); setSettingsInitialTab('status'); if (!currentProjectId && !currentLocalWorkspaceId) setShowLandingPage(true); }}
          initialTab={settingsInitialTab}
          isFirebaseConfigured={isFirebaseConfigured}
          apiKeys={apiKeys}
          inputApiKeys={inputApiKeys}
          onInputApiKeysChange={setInputApiKeys}
          onSaveApiKeys={handleSaveApiKeys}
          onRemoveApiKey={handleRemoveApiKey}
          keysLast={keysLast}
          autoSaveEnabled={autoSaveEnabled}
          autoSaveFolderName={autoSaveFolderName}
          autoSaveAccountFolder={autoSaveAccountFolder}
          onAutoSaveEnabledChange={async (enabled) => {
            setAutoSaveEnabledState(enabled);
            setAutoSaveEnabled(enabled, user?.sub);
            await syncLocalStoragePrefsToCloud(user?.sub, enabled, autoSaveFolderName);
          }}
          onSelectLocalFolder={() => handleSelectLocalFolder({ closePromptOnSuccess: true })}
          onShowTwitterAuth={() => setShowTwitterAuthOverlay(true)}
          onDeleteAccount={async () => {
            if (user) {
              const result = await deleteUserAccount(user.sub);
                                                       if (result.success) {
                setShowSettingsPage(false);
              }
            }
          }}
          comfyUIConfig={getComfyUIConfig()}
          onComfyUIConfigChange={saveComfyUIConfig}
          customWorkflows={customWorkflows}
          onRefreshWorkflows={refreshCustomWorkflows}
          customWorkflowsLoading={customWorkflowsLoading}
          customWorkflowInfo={customWorkflowInfo}
          customWorkflowError={customWorkflowError}
          onEnsureWorkflowsFolder={ensureWorkflowsFolder}
          isPro={featureAccess.isPro}
          onSubscription={() => { setShowSettingsPage(false); setShowSubscriptionPage(true); }}
          onAnalytics={() => { setShowSettingsPage(false); setShowAnalyticsPage(true); }}
          onSignOut={handleLogout}
          activeSessions={activeSessions}
          currentSessionId={currentSessionId ?? undefined}
          onRevokeSession={async (sid: string) => {
            await sessionManagerRef.current?.revokeSession(sid);
          }}
          onRevokeAllOtherSessions={async () => {
            await sessionManagerRef.current?.revokeAllOtherSessions();
          }}
        />
      )}



      {/* Local Storage Setup Prompt - cross-device/browser detection */}
      {showLocalStoragePrompt && cloudLocalStoragePrefs && (
          <div className="ns-modal-scrim z-[101]">
              <div className="ns-modal-sheet">
                  <div className="flex items-center gap-3 mb-4">
                      <span className="ns-mark ns-mark-lavender">
                          <HardDrive />
                      </span>
                      <div className="min-w-0">
                          <h2 className="ns-t-dialog">Reconnect local storage</h2>
                          <p className="ns-t-small">
                              {reconnectFolderName ? 'Your browser needs permission again' : 'You have local storage set up elsewhere'}
                          </p>
                      </div>
                  </div>

                  {reconnectFolderName ? (
                      <p className="text-sm text-ink-soft mb-4">
                          After a restart the browser asks again before this app may use your folder <span className="font-semibold text-ink">{reconnectFolderName}</span>. Until you allow it, videos and images load from the cloud instead of your drive.
                      </p>
                  ) : (
                      <p className="text-sm text-ink-soft mb-4">
                          Each browser needs permission to access your local folder. Select the same folder you used before to continue syncing.
                      </p>
                  )}

                  {reconnectProblem && (
                      <p className="ns-note mb-4" data-tone="warn">
                          {reconnectProblem === 'missing'
                              ? 'The folder was moved or deleted. Choose it again below.'
                              : 'Access was not allowed. You can choose the folder again below.'}
                      </p>
                  )}

                  {!reconnectFolderName && cloudLocalStoragePrefs.localStoragePath && (
                      <div className="ns-well p-3 mb-4">
                          <p className="ns-t-small font-medium mb-1 flex items-center gap-1.5"><FolderOpen size={14} className="shrink-0" aria-hidden="true" /> Select this folder:</p>
                          <p className="ns-t-mono font-semibold text-ink break-all">
                              {cloudLocalStoragePrefs.localStoragePath}
                          </p>
                          <p className="ns-t-meta mt-1">
                              This is the folder you set up in another browser
                          </p>
                      </div>
                  )}
                  
                  <div className="space-y-2">
                      {reconnectFolderName && (
                          <button
                              onClick={async () => {
                                  // Straight from the click, so the browser can show its prompt
                                  const result = await requestLocalFolderAccess(user?.sub);
                                  if (result === 'granted') {
                                      setShowLocalStoragePrompt(false);
                                      setReconnectFolderName(null);
                                  } else {
                                      setReconnectProblem(result);
                                  }
                              }}
                              className="ns-btn ns-btn-sm ns-btn-primary ns-btn-flat w-full"
                          >
                              <HardDrive size={14} /> Allow access
                          </button>
                      )}
                      <button
                          onClick={() => handleSelectLocalFolder({ closePromptOnSuccess: true })}
                          className={reconnectFolderName
                              ? 'ns-btn ns-btn-sm ns-btn-soft ns-btn-flat w-full'
                              : 'ns-btn ns-btn-sm ns-btn-primary ns-btn-flat w-full'}
                      >
                          <FolderOpen size={14} /> {reconnectFolderName ? 'Choose the folder again' : 'Select folder'}
                      </button>
                      
                      <button
                          onClick={() => setShowLocalStoragePrompt(false)}
                          className="ns-btn ns-btn-sm ns-btn-quiet w-full"
                      >
                          Not now
                      </button>
                      
                      <button
                          onClick={() => {
                              setUserLocalStorage(user?.sub, 'localStoragePromptDismissed', 'true');
                              setLocalStoragePromptDismissed(true);
                              setShowLocalStoragePrompt(false);
                          }}
                          className="ns-btn ns-btn-xs ns-btn-quiet w-full font-medium"
                      >
                          Don't ask again on this device
                      </button>
                  </div>
              </div>
          </div>
      )}

      {/* Local Sync Progress Overlay */}
      {localSyncStatus.isActive && (
          <div className="ns-modal-scrim z-[103]">
              <div className="ns-modal-sheet max-w-sm">
                  <div className="flex flex-col items-center text-center">
                      {/* The mark takes the colours of the step: done, gone wrong, or under way */}
                      <span
                          className="ns-mark ns-mark-lg mb-4"
                          data-tone={localSyncStatus.step === 'complete' ? 'ok' : localSyncStatus.step === 'error' ? 'danger' : 'lavender'}
                      >
                          {localSyncStatus.step === 'complete' ? (
                              <Check />
                          ) : localSyncStatus.step === 'error' ? (
                              <AlertTriangle />
                          ) : (
                              <Loader2 className="animate-spin" />
                          )}
                      </span>

                      {/* Status message */}
                      <h3 className={`ns-t-dialog mb-1 ${
                          localSyncStatus.step === 'complete'
                              ? 'text-ok-deep'
                              : localSyncStatus.step === 'error'
                                  ? 'text-danger-deep'
                                  : ''
                      }`}>
                          {localSyncStatus.step === 'connecting' && 'Waiting for folder'}
                          {localSyncStatus.step === 'scanning' && 'Connecting...'}
                          {localSyncStatus.step === 'importing' && 'Scanning workspaces'}
                          {localSyncStatus.step === 'complete' && 'Connected!'}
                          {localSyncStatus.step === 'error' && 'Connection failed'}
                      </h3>
                      
                      <p className="text-sm text-ink-soft mb-4">{localSyncStatus.message}</p>
                      
                      {/* Folder name badge */}
                      {localSyncStatus.folderName && (
                          <div className="ns-chip mb-4">
                              <FolderOpen size={14} />
                              {localSyncStatus.folderName}
                          </div>
                      )}
                      
                      {/* Progress bar for importing */}
                      {localSyncStatus.progress && localSyncStatus.step === 'importing' && (
                          <div className="w-full mb-4">
                              <div className="ns-meter" data-tone="lavender">
                                  <span style={{ width: `${(localSyncStatus.progress.current / localSyncStatus.progress.total) * 100}%` }} />
                              </div>
                              <p className="ns-t-meta mt-1">
                                  {localSyncStatus.progress.current} / {localSyncStatus.progress.total} workspaces
                              </p>
                          </div>
                      )}
                      
                      {/* Auto-dismiss countdown for success */}
                      {localSyncStatus.step === 'complete' && (
                          <p className="ns-t-meta">Closing automatically...</p>
                      )}
                      
                      {/* Close button for error state */}
                      {localSyncStatus.step === 'error' && (
                          <button
                              onClick={() => setLocalSyncStatus({ isActive: false, step: 'idle', message: '' })}
                              className="ns-btn ns-btn-sm ns-btn-soft ns-btn-flat"
                          >
                              Close
                          </button>
                      )}
                  </div>
              </div>
          </div>
      )}

      {/* New User Onboarding Modal */}
      {showOnboarding && (
          <div className="ns-modal-scrim z-[102]">
              <div className="ns-modal-sheet max-w-lg">
                  <div className="flex flex-col items-center text-center mb-6">
                      <span className="ns-mark ns-mark-lg ns-mark-lavender mb-4">
                          <Sparkles />
                      </span>
                      <h2 className="ns-t-dialog">Welcome to Noodle Studio</h2>
                      <p className="text-sm text-ink-soft mt-1">Let's get you set up for the best experience</p>
                  </div>
                  
                  <div className="ns-well p-4 mb-6">
                      <h3 className="ns-t-title flex items-center gap-2 mb-2">
                          <HardDrive size={16} className="text-lavender-deep" /> Enable local storage
                      </h3>
                      <p className="ns-t-small mb-3">
                          Save your generated images and videos to a local folder on your computer.
                      </p>
                      <div className="ns-t-small space-y-2">
                          <div className="flex items-start gap-2">
                              <Zap size={14} className="text-lavender-deep mt-0.5 shrink-0" />
                              <span><strong className="font-semibold text-ink">Faster loading</strong> - Load your work instantly from local files</span>
                          </div>
                          <div className="flex items-start gap-2">
                              <Cloud size={14} className="text-lavender-deep mt-0.5 shrink-0" />
                              <span><strong className="font-semibold text-ink">Offline access</strong> - Work even without internet connection</span>
                          </div>
                          <div className="flex items-start gap-2">
                              <Save size={14} className="text-lavender-deep mt-0.5 shrink-0" />
                              <span><strong className="font-semibold text-ink">Automatic backup</strong> - Every generation is saved automatically</span>
                          </div>
                          <div className="flex items-start gap-2">
                              <FolderOpen size={14} className="text-lavender-deep mt-0.5 shrink-0" />
                              <span><strong className="font-semibold text-ink">Easy access</strong> - Browse your files in your file manager</span>
                          </div>
                      </div>
                  </div>
                  
                  <div className="space-y-2">
                      <button
                          onClick={() => handleSelectLocalFolder({ closeOnboardingOnSuccess: true })}
                          className="ns-btn ns-btn-sm ns-btn-primary ns-btn-flat w-full"
                      >
                          <FolderOpen size={14} /> Set up local storage
                      </button>
                      
                      <button
                          onClick={async () => {
                              // Mark onboarding as complete even if skipped
                              if (user) {
                                  await completeOnboarding(user.sub);
                              }
                              setShowOnboarding(false);
                          }}
                          className="ns-btn ns-btn-sm ns-btn-quiet w-full"
                      >
                          Maybe later
                      </button>
                  </div>
                  
                  <p className="ns-t-meta text-center mt-4">
                      You can always set this up later in Settings → Auto-save
                  </p>
              </div>
          </div>
      )}

      {/* Paying for Pro: the QR and address for the chosen months */}
      <PaymentModal
          isOpen={showSubscriptionModal}
          onClose={() => setShowSubscriptionModal(false)}
          user={user}
          subscription={subscription || undefined}
          onSubscriptionUpdate={() => {
              // Subscription will auto-update via listener
              if (user) {
                  getDailyGenerationCount(user.sub).then(setDailyGenerationCount);
              }
          }}
          onSignIn={() => setShowAuthModal(true)}
      />

      {/* Subscription Management Page - full page view for logged-in users */}
      {showSubscriptionPage && user && (
          <SubscriptionPage
              user={user}
              subscription={subscription}
              proGrant={proGrant}
              onBack={() => { setShowSubscriptionPage(false); if (!currentProjectId && !currentLocalWorkspaceId) setShowLandingPage(true); }}
              onSubscriptionUpdate={() => {
                  if (user) {
                      getDailyGenerationCount(user.sub).then(setDailyGenerationCount);
                  }
              }}
              onAnalytics={() => { setShowSubscriptionPage(false); setShowAnalyticsPage(true); }}
              onSettings={() => { setShowSubscriptionPage(false); setShowSettingsPage(true); }}
              onSignOut={handleLogout}
              onTerms={() => { setShowSubscriptionPage(false); setShowTermsPage(true); }}
              onPrivacy={() => { setShowSubscriptionPage(false); setShowPrivacyPage(true); }}
              onAbout={() => { setShowSubscriptionPage(false); setShowAboutPage(true); }}
          />
      )}

      {/* Auth Modal - for signing in from main app */}
      <AuthModal 
          isOpen={showAuthModal}
          onClose={() => setShowAuthModal(false)}
          onLoginSuccess={finishLogin}
          isFirebaseConfigured={isFirebaseConfigured}
          onShowSubscriptionModal={() => setShowSubscriptionModal(true)}
          initialAuthMode={authMode}
      />

      {/* Share Modal */}
      {currentProjectId && user && (
          <ShareModal
              isOpen={showShareModal}
              onClose={() => setShowShareModal(false)}
              projectId={currentProjectId}
              projectName={currentProjectName}
              currentUserId={user.sub}
              onEnsureSaved={async () => {
                // Force-save to Firestore so the sharing API can find the project
                try {
                  await saveProject(
                    nodesRef.current, connections, history, transformRef.current,
                    user.sub, currentProjectId, currentProjectName,
                    true, // Force cloud sync enabled
                    undefined, undefined // No thumbnail, full save
                  );
                } catch (err) {
                  console.warn('[App] Pre-share save failed:', err);
                  throw err;
                }
              }}
          />
      )}

      {/* Changelog Panel */}
      {currentProjectId && (
          <ChangelogPanel
              isOpen={showChangelogPanel}
              onClose={() => setShowChangelogPanel(false)}
              projectId={currentProjectId}
          />
      )}

      {/* Menu / File menu anchor at bottom-left of canvas. Like the rail above it, it moves aside
          when the library panel opens, so it never lies on the panel. */}
      <div className={`pointer-events-auto absolute bottom-4 z-50 flex flex-col gap-2 transition-[left] duration-200 ${user && isProjectPanelOpen ? 'left-[328px]' : 'left-4'}`} style={{ display: writingOpen ? 'none' : undefined }}>
        <div className="relative file-menu-container">
            {/* One of the canvas's bars: a glass pill, 4px around a 32px control. The glass frames the
                button only: a menu inside a blurred element could not blur the canvas itself. */}
            <div className="ns-glass ns-glass-strong shrink-0 flex p-1 rounded-full">
                <button
                    onClick={() => setIsFileMenuOpen(!isFileMenuOpen)}
                    className={`ns-chip ns-chip-btn h-8 gap-2 pl-3 pr-3.5 font-semibold text-ink ${isFileMenuOpen ? '' : 'bg-transparent hover:bg-cream-200'}`}
                    aria-haspopup="menu"
                    aria-expanded={isFileMenuOpen}
                >
                    <Menu size={14} /> Menu
                </button>
            </div>
            {isFileMenuOpen && (
                <div className="ns-menu absolute bottom-full left-0 mb-2 w-56 z-10 flex flex-col">
                    <div className="ns-menu-label">
                        Workspace
                    </div>
                    
                    <button onClick={() => { setShowWorkspaceModal(true); setIsFileMenuOpen(false); }} className="ns-menu-item">
                        <FolderOpen /> Switch workspace
                    </button>
                    <button onClick={() => { handleCreateLocalWorkspace(); setIsFileMenuOpen(false); }} className="ns-menu-item">
                        <Plus /> New workspace
                    </button>
                    {autoSaveSupported && (
                        <button onClick={() => { setShowLocalFolder(true); setIsFileMenuOpen(false); }} className="ns-menu-item">
                            <HardDrive /> Show local folder
                        </button>
                    )}
                    
                    <div className="ns-menu-sep"></div>

                    {/* The file input lies over the button, so the row shows the hover itself */}
                    <div className="relative rounded-md hover:bg-cream-200">
                        <button className="ns-menu-item"><Upload /> Import file</button>
                        <input type="file" ref={fileInputRef} accept=".json" className="absolute inset-0 opacity-0 cursor-pointer" onChange={handleImport} />
                    </div>
                    <button onClick={handleExport} className="ns-menu-item"><Download /> Export file</button>

                    <div className="ns-menu-sep"></div>

                    <button onClick={() => { resetConfig(); setIsFileMenuOpen(false); }} className="ns-menu-item">
                        <Settings2 /> Workspace settings
                    </button>
                </div>
            )}
        </div>
      </div>

      {/* Top Bar */}
      <div className="absolute top-4 left-0 w-full px-4 z-50 grid grid-cols-[max-content_minmax(0,1fr)_max-content] items-center gap-3 pointer-events-none">
          {/* The center column holds only the loading bar of the canvas's pictures (node tools live in the left panel) */}
          <div className="col-start-2 row-start-1 justify-self-center min-w-0 max-w-full flex">
              {workspaceView === 'nodes' && (
                  <MediaLoadingBar getLayer={() => canvasRef.current?.contentLayerRef.current ?? null} lookups={mediaLookups} />
              )}
          </div>

          <div className="pointer-events-auto flex items-center gap-2 col-start-1 row-start-1" style={{ display: writingOpen ? 'none' : undefined }}>
              {/* Home Button */}
              <button
                onClick={handleGoHome}
                className="ns-icon-btn ns-icon-btn-lg ns-icon-btn-solid"
                title="Back to your library"
                aria-label="Back to your library"
              >
                {/* The brand mark in place of a house: the strand between two ports */}
                <BrandMark size={20} tone="onDark" />
              </button>

              {/* One glass pill: the Library button, then the path (the project with its tile, the workspace
                  with its picture and a menu of its neighbours), the plan, the cost */}
              <div className="ns-glass ns-glass-strong shrink-0 flex items-center gap-1 p-1 rounded-full relative">
                  {user && (
                    <>
                      <button
                        type="button"
                        onClick={() => { toggleProjectPanel(!isProjectPanelOpen); if (!isProjectPanelOpen) dismissLibraryTip(); }}
                        // A toggle: lavender while the panel is out (aria-pressed), a white button in the bar otherwise
                        className={`ns-chip ns-chip-btn h-8 gap-2 pl-2.5 pr-3 shrink-0 ${isProjectPanelOpen ? '' : 'bg-white text-ink shadow-hair hover:bg-cream-200'}`}
                        data-tone="lavender"
                        title={isProjectPanelOpen ? 'Hide the library' : 'Browse your projects and workspaces'}
                        aria-pressed={isProjectPanelOpen}
                        aria-expanded={isProjectPanelOpen}
                        aria-controls="canvas-library-panel"
                        aria-label="Library"
                        data-library-toggle
                      >
                        <PanelLeft size={14} className="shrink-0" aria-hidden="true" />
                        <span className="hidden sm:inline font-semibold">Library</span>
                      </button>
                      <span className="ns-divider-v h-[18px] mx-0.5 hidden sm:block" aria-hidden="true" />
                      <nav aria-label="Where you are" className="flex items-center gap-0.5 min-w-0">
                        {openWorkspaceProject && (
                          <>
                            <button
                              type="button"
                              className="ns-chip ns-chip-btn h-8 gap-1.5 pl-2 pr-2.5 bg-transparent text-ink hover:bg-cream-200 min-w-0 hidden md:inline-flex"
                              onClick={() => { toggleProjectPanel(true); dismissLibraryTip(); }}
                              title={`Open ${openWorkspaceProject.name} in the library`}
                              data-path-project
                            >
                              <ProjectTile name={openWorkspaceProject.name} color={openWorkspaceProject.color} size="sm" />
                              <span className="truncate max-w-[160px] font-semibold">{openWorkspaceProject.name}</span>
                            </button>
                            <ChevronRight size={12} className="text-ink-faint shrink-0 hidden md:block" aria-hidden="true" />
                          </>
                        )}
                        <LibraryMenu
                          label={`Switch workspace, open: ${currentProjectName}`}
                          items={workspaceSwitcherItems}
                          triggerClassName="ns-chip ns-chip-btn h-8 gap-1.5 pl-2 pr-2 bg-transparent text-ink hover:bg-cream-200 min-w-0 max-w-[42vw] sm:max-w-[260px]"
                          align="left"
                        >
                          <WorkspaceThumb thumbnail={openLibraryWorkspace?.thumbnail} />
                          <span className="truncate font-semibold">{currentProjectName}</span>
                          {isLocalWorkspaceMode && <span className="ns-badge shrink-0">Local</span>}
                          <ChevronDown size={12} className="shrink-0 text-ink-faint" aria-hidden="true" />
                        </LibraryMenu>
                        {/* Shared to view: said once, beside the name, where the eye goes anyway */}
                        {isViewOnly && (
                          <span className="ns-chip h-8 gap-1.5 px-2.5 shrink-0" data-tone="warn" title="Shared with you to view. You can look around, but changes are not saved." data-view-only>
                            <Eye size={14} aria-hidden="true" />
                            <span className="hidden sm:inline">View only</span>
                          </span>
                        )}
                      </nav>
                      {/* The first time: a tip under the button, put away by Got it or by opening the library */}
                      {!libraryTipSeen && !isProjectPanelOpen && openWorkspaceId && (
                        <div role="dialog" aria-label="Your projects live here" className="absolute left-0 top-[calc(100%+12px)] w-[300px] rounded-2xl bg-ink text-cream shadow-pop p-4 z-50" data-library-tip>
                          <span className="absolute left-7 -top-1.5 w-3 h-3 bg-ink rotate-45 rounded-sm" aria-hidden="true" />
                          <p className="text-sm font-bold">Your projects live here</p>
                          <p className="text-[13px] leading-snug opacity-80 mt-1">Open the library to switch workspaces, browse folders and find what people shared with you. The path next to it says where you are.</p>
                          <div className="flex items-center gap-3 mt-3">
                            <button type="button" className="ns-btn ns-btn-cream ns-btn-sm ns-btn-flat" onClick={() => { toggleProjectPanel(true); dismissLibraryTip(); }}>Open the library</button>
                            <button type="button" className="text-[13px] font-semibold opacity-80 hover:opacity-100 rounded-md" onClick={dismissLibraryTip}>Got it</button>
                          </div>
                        </div>
                      )}
                    </>
                  )}

                  {/* The plan, for a guest or the free plan only: with its node limit, and the way to upgrade.
                      A paid plan shows as the gold ring of the account button (ProfileMenu), and in its menu. */}
                  {(!user || !featureAccess.isPro) && (
                    <button
                      onClick={() => user ? setShowSubscriptionPage(true) : setShowSubscriptionModal(true)}
                      // Not filled: in this bar a lavender fill says "on" (the library chip beside it)
                      className="ns-chip ns-chip-btn h-8 bg-transparent text-ink-soft hover:bg-cream-200 hover:text-ink"
                      title={user ? 'Upgrade to Pro for unlimited nodes' : 'Sign up to save your work'}
                    >
                      <Crown size={14} />
                      <span className="hidden sm:inline">{!user ? 'Guest' : (featureAccess.tier === 'free' ? 'Free' : featureAccess.tier.charAt(0).toUpperCase() + featureAccess.tier.slice(1))}</span>
                      {featureAccess.limits.maxNodesPerWorkspace > 0 && (
                        <span className={canvasNodes.length >= featureAccess.limits.maxNodesPerWorkspace ? 'ns-badge ns-badge-warn' : 'ns-badge'}>
                          {canvasNodes.length}/{featureAccess.limits.maxNodesPerWorkspace}
                        </span>
                      )}
                    </button>
                  )}

                  {/* What this workspace's generations have cost */}
                  <div className="ns-chip ns-chip-ok h-8 hidden md:flex whitespace-nowrap" title="What this workspace's generations have cost so far (estimated)" data-total-spent>
                    <span>Total spent ${totalUsageCost.toFixed(2)}</span>
                  </div>
              </div>
          </div>

          {/* Right side - Presence, save status, Storage, History, Gallery, Notifications and Account (the account alone while a screenplay is opened full) */}
          <div className="pointer-events-auto flex items-center gap-2 col-start-3 row-start-1 justify-self-end">
              {/* Active users indicator */}
              {user && currentProjectId && !isLocalWorkspaceMode && otherUsers.length > 0 && !writingOpen && (
                  <PresenceAvatars users={otherUsers} currentUserId={user.sub} />
              )}

              {/* One glass pill for the round buttons. The glass is a layer behind them and not
                  their parent: a menu that opens from a button could not blur the canvas from
                  inside an element that is itself blurred. */}
              <div className={`relative isolate shrink-0 flex items-center gap-0.5 p-1 rounded-full ${writingOpen ? 'ns-chrome-hidden' : ''}`}>
                  <span className="ns-glass ns-glass-strong absolute inset-0 -z-10 rounded-full pointer-events-none" aria-hidden="true" />

                  {/* Save status: a state of the open workspace, so a tinted chip in its bar, and covered
                      with the bar by whatever covers the canvas (a dialog, a page). Both words lie in one
                      cell, so the bar keeps its width when the word changes. */}
                  <div
                      className={`ns-chip h-8 mr-0.5 whitespace-nowrap ${autoSaveStatus.isActive ? 'ns-chip-lavender' : 'ns-chip-ok'}`}
                      title={autoSaveStatus.isActive ? 'Autosave' : 'Synced'}
                      data-save-status={autoSaveStatus.isActive ? 'saving' : 'saved'}
                  >
                      {autoSaveStatus.isActive ? <Loader2 size={14} className="animate-spin" /> : <Cloud size={14} />}
                      <span className="hidden sm:inline-grid justify-items-center">
                          <span className={`col-start-1 row-start-1 ${autoSaveStatus.isActive ? '' : 'invisible'}`}>Autosave</span>
                          <span className={`col-start-1 row-start-1 ${autoSaveStatus.isActive ? 'invisible' : ''}`}>Synced</span>
                      </span>
                  </div>

                  {/* Local folder (the auto-save folder on this computer) */}
                  {autoSaveSupported && (
                      <button
                          onClick={() => setShowLocalFolder(true)}
                          className="ns-icon-btn"
                          title="Local folder"
                          aria-label="Show local folder"
                      >
                          <FolderOpen />
                      </button>
                  )}

                  {/* Storage (cloud media browser) */}
                  {user && featureAccess.limits.cloudSyncEnabled && (
                      <button
                          onClick={() => { const next = !isStoragePanelOpen; setIsStoragePanelOpen(next); if (next) { setShowChangelogPanel(false); setIsGalleryOpen(false); setCommentNodeId(null); } }}
                          className="ns-icon-btn"
                          aria-pressed={isStoragePanelOpen}
                          title="Storage"
                          aria-label="Storage"
                      >
                          <HardDrive />
                      </button>
                  )}

                  {/* Change history (collaboration edits) */}
                  {user && currentProjectId && !isLocalWorkspaceMode && (
                      <button
                          onClick={() => { const next = !showChangelogPanel; setShowChangelogPanel(next); if (next) { setIsGalleryOpen(false); setIsStoragePanelOpen(false); setCommentNodeId(null); } }}
                          className="ns-icon-btn"
                          aria-pressed={showChangelogPanel}
                          title="Change history"
                          aria-label="Change history"
                      >
                          <History />
                      </button>
                  )}

                  {/* Media gallery (generated images & videos) */}
                  {user && (
                      <button
                          onClick={() => { const next = !isGalleryOpen; setIsGalleryOpen(next); if (next) { setShowChangelogPanel(false); setIsStoragePanelOpen(false); setCommentNodeId(null); } }}
                          className="ns-icon-btn"
                          aria-pressed={isGalleryOpen}
                          title="Media gallery"
                          aria-label="Media gallery"
                      >
                          <Images />
                      </button>
                  )}

                  {/* Notification Bell */}
                  {bell}

                  {/* Account menu: the one the library and the pages open, in the same words, with
                      "Share workspace" first where there is a workspace that can be shared */}
                  <ProfileMenu
                      user={user}
                      isPro={featureAccess.isPro}
                      onSignIn={() => { setAuthMode('login'); setShowAuthModal(true); }}
                      onCreateAccount={() => { setAuthMode('signup'); setShowAuthModal(true); }}
                      onShare={currentProjectId && !isLocalWorkspaceMode ? () => setShowShareModal(true) : undefined}
                      onSubscription={() => setShowSubscriptionPage(true)}
                      onAnalytics={() => setShowAnalyticsPage(true)}
                      onSettings={() => setShowSettingsPage(true)}
                      onSignOut={handleLogout}
                  />
              </div>
          </div>
      </div>

      {/* Library panel: the open workspace's project, one step up the whole library */}
      {user && isProjectPanelOpen && (
        <aside id="canvas-library-panel" className="ns-glass ns-glass-strong ns-lib-side absolute left-4 top-[72px] h-[calc(100%-88px)] w-[300px] max-w-[85vw] z-40 flex flex-col animate-[ns-slide-up_240ms_cubic-bezier(0.34,1.45,0.5,1)]" aria-label="Library panel">
          <div className="ns-lib-side-scroll">
            <CanvasLibraryPanel
              folders={folders}
              sharedFolders={sharedFolders}
              workspaces={panelWorkspaces}
              loading={!libraryLoaded}
              openWorkspaceId={openWorkspaceId}
              openContext={projectPanelContext}
              openTargetFolderId={projectPanelTargetFolderId}
              expandedIds={projectPanelExpanded}
              onToggleExpand={(id) => setProjectPanelExpanded(prev => { const n = new Set(prev); if (n.has(id)) n.delete(id); else n.add(id); return n; })}
              onOpenWorkspace={(w) => { void handleSwitchWorkspace(w.id, w.isLocal); }}
              onOpenLibraryPage={handleGoHome}
              onCreateWorkspace={isViewOnly ? undefined : (name, folderId) => { void handleCreateWorkspaceInFolder(name, folderId).catch(() => {}); }}
              onCreateFolder={isViewOnly || !user ? undefined : handleCreateFolderFromPanel}
              renderWorkspaceMenu={(w) => (w.isShared ? null : (
                <LibraryMenu
                  label={`Actions for ${w.name}`}
                  items={[{ id: 'rename', label: 'Rename…', icon: <Edit2 className="w-4 h-4" />, onSelect: () => setRenamingWorkspace(w) }]}
                  triggerClassName="ns-icon-btn ns-icon-btn-sm"
                  triggerTabIndex={-1}
                />
              ))}
            />
          </div>
        </aside>
      )}
      <NameDialog
        open={!!renamingWorkspace}
        title="Rename workspace"
        label="Name"
        initialValue={renamingWorkspace?.name || ''}
        submitLabel="Rename"
        onClose={() => setRenamingWorkspace(null)}
        onSubmit={async (name) => {
          if (!renamingWorkspace) return;
          await handleRenameWorkspaceFromLibrary({ id: renamingWorkspace.id, isLocal: renamingWorkspace.isLocal }, name);
        }}
      />

      {/* Left Tool Panel — vertical node-creation rail (expands on hover); the Ideas page has a rail of its own */}
      {!isViewOnly && workspaceView === 'nodes' && (
        <div className={`absolute top-1/2 -translate-y-1/2 z-40 pointer-events-auto group/rail transition-[left] duration-200 ${user && isProjectPanelOpen ? 'left-[328px]' : 'left-4'}`}>
          {/* One of the canvas's bars: glass, 4px around 32px chips (42px in all). The corner is the
              bar's own: a pill while it is narrow, and still a fit for the round chips when it opens. */}
          <div className="ns-glass ns-glass-strong flex flex-col gap-1 rounded-3xl p-1 w-[42px] group-hover/rail:w-48 transition-[width] duration-200 ease-[cubic-bezier(0.34,1.45,0.5,1)] max-h-[calc(100vh-160px)] overflow-y-auto overflow-x-hidden no-scrollbar">
            {[
              { type: NodeType.SKETCH_PAD,        label: 'Sketch',     addLabel: 'Sketch',     size: {width: 400, height: 490} },
              { type: NodeType.MEDIA_REFERENCE,   label: 'Reference',  addLabel: 'Reference',  size: {width: 320, height: 300} },
              { type: NodeType.SCRIPT_PROCESSOR,  label: 'Script',     addLabel: 'Script',     size: {width: 350, height: 250} },
              { type: NodeType.WRITE_NODE,        label: 'Write',      addLabel: 'Write',      size: {width: 340, height: 420} },
              { type: NodeType.VIDEO_GENERATOR,   label: 'Video',      addLabel: 'Video',      size: {width: 400, height: 620} },
              { type: NodeType.IMAGE_GENERATOR,   label: 'Image',      addLabel: 'Image',      size: {width: 350, height: 500} },
              { type: NodeType.STORYBOARD_NODE,   label: 'Storyboard', addLabel: 'Storyboard', size: {width: 1200, height: 700} },
              { type: NodeType.SHARE_NODE,        label: 'Post',       addLabel: 'New Post',   size: {width: 320, height: 400} },
            ].map(tool => (
              <button
                key={tool.type}
                onClick={() => addNode(tool.type, tool.addLabel, tool.size)}
                className="flex items-center gap-3 w-full h-8 pr-3 rounded-full hover:bg-cream-200 text-ink transition-colors shrink-0"
                title={tool.label}
                data-tone={nodeTone(tool.type)}
              >
                {/* The same round chip, in the same colour, as on the node it adds */}
                <span className="ns-rail-chip"><NodeKindIcon type={tool.type} size={16} /></span>
                <span className="text-xs font-semibold whitespace-nowrap opacity-0 group-hover/rail:opacity-100 transition-opacity duration-150">{tool.label}</span>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* While a drawer is open on the right, the zoom rail and the minimap keep clear of it */}
      <CanvasRightInset open={(!!user && isStoragePanelOpen) || isGalleryOpen || (!!currentProjectId && showChangelogPanel)} />

      {/* Storage Right Panel — collapsible drawer: the one drawer of the right edge (as the gallery and the change history) */}
      {/* The conversation about one node, at the right under the account menu */}
      {commentNodeId && workspaceView === 'nodes' && (() => {
        const commented = nodes.find(n => n.id === commentNodeId);
        return (
          <NodeCommentsPanel
            node={commented ? { id: commented.id, type: commented.type, title: commented.data.title || nodeKind(commented.type).label } : null}
            comments={commentsOf(comments, commentNodeId)}
            userId={user?.sub}
            isOwner={!!user && projectOwnerId === user.sub}
            canWrite={!!ideaUser && !!ideaProjectId}
            problem={commentsProblem}
            loading={commentsLoading}
            onSend={async (text) => {
              if (!ideaUser || !ideaProjectId) return;
              await addComment(ideaProjectId, commentNodeId, text, { id: ideaUser.id, name: ideaUser.name, picture: ideaUser.picture });
            }}
            onDelete={(commentId) => (ideaProjectId ? deleteComment(ideaProjectId, commentId) : Promise.resolve())}
            onShowNode={() => goToNode(commentNodeId)}
            onClose={() => setCommentNodeId(null)}
          />
        );
      })()}

      {user && isStoragePanelOpen && (
        <div className="ns-drawer absolute right-0 top-[72px] h-[calc(100%-72px)] w-[360px] max-w-[90vw] z-40 overflow-hidden animate-[ns-slide-up_240ms_cubic-bezier(0.34,1.45,0.5,1)]" data-canvas-drawer="storage">
          <div className="ns-drawer-head">
            <h2 className="ns-t-heading flex items-center gap-2">
              <HardDrive size={16} className="text-ink-soft" /> Storage
            </h2>
            <button onClick={() => setIsStoragePanelOpen(false)} className="ns-icon-btn" title="Close" aria-label="Close">
              <X />
            </button>
          </div>
          <div className="flex-1 min-h-0 overflow-auto">
            <StorageNode
              data={storagePanelData}
              userId={user.sub}
              onDataChange={setStoragePanelData}
              isViewOnly={isViewOnly}
              ownerUserId={projectOwnerId}
              projectName={currentProjectName}
              projectId={currentProjectId || undefined}
            />
          </div>
        </div>
      )}

      {/* (a workspace shared to view says so in the bar's path, beside its name, not in a band across the top) */}

      {/* Files shown in a shared workspace are its owner's (hooks/useFileUrl.ts) */}
      <MediaOwnerContext.Provider value={projectOwnerId || undefined}>
      <MediaLookupsContext.Provider value={mediaLookups}>
      <InfiniteCanvas 
         ref={(canvas) => {
           logger.debug('Canvas', '🎨 InfiniteCanvas ref callback fired:', !!canvas);
           if (canvas) {
             logger.debug('Canvas', '  → containerRef:', !!canvas.containerRef, '(current:', !!canvas.containerRef.current, ')');
             logger.debug('Canvas', '  → contentLayerRef:', !!canvas.contentLayerRef, '(current:', !!canvas.contentLayerRef.current, ')');
           }
           canvasRef.current = canvas;
         }}
         transform={transform} 
         onTransformChange={setTransform}
         connections={connections}
         nodes={canvasNodes}
         pendingConnection={pendingConnection}
         onConnectionDelete={handleRemoveConnection}
         onTextDrop={handleCanvasTextDrop}
         onStorageFileDrop={handleStorageFileDrop}
         onBackgroundClick={clearSelection}
         onMarqueeSelect={handleMarqueeSelect}
         selectedNodeIds={selectedNodeIds}
         getDragGroup={getDragGroup}
         covered={workspaceView !== 'nodes'}
      >
        {canvasNodes.map((node) => (
          <BaseNode
            key={node.id}
            id={node.id}
            position={node.position}
            size={node.size}
            isSelected={selectedIdSet.has(node.id)}
            isViewOnly={isViewOnly}
            onSelect={handleSelectNode}
            onPositionChange={handleNodePositionChange}
            onResize={handleNodeResize}
            onDelete={handleNodeDelete}
            onConnectStart={handleConnectStart}
            onConnectEnd={(id) => handleConnectEnd(id)}
            title={node.data.title || (node.type === NodeType.SHARE_NODE ? 'New Post' : 'Untitled')}
            tone={nodeTone(node.type)}
            titleIcon={node.type === NodeType.SHARE_NODE ? (
              <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
              </svg>
            ) : <NodeKindIcon type={node.type} />}
            hideConnectors={node.type === NodeType.STORAGE_BROWSER}
            reactions={reactionsForNode[node.id]}
            onReact={user && node.type !== NodeType.STORAGE_BROWSER ? handleReactToNode : undefined}
            myId={user?.sub}
            reactorNames={reactorNames}
            onOpenComments={ideaProjectId && node.type !== NodeType.STORAGE_BROWSER ? handleOpenComments : undefined}
            commentCount={commentCountByNode[node.id] ?? 0}
            commentsOpen={commentNodeId === node.id}
            onRename={node.type === NodeType.IMAGE_GENERATOR || node.type === NodeType.VIDEO_GENERATOR ? handleNodeRename : undefined}
            defaultTitle={node.type === NodeType.IMAGE_GENERATOR ? 'Image' : node.type === NodeType.VIDEO_GENERATOR ? 'Video' : undefined}
            // A Reference node with its preview folded away is one row tall
            minHeight={(node.type === NodeType.MEDIA_REFERENCE || node.type === NodeType.AUDIO_REFERENCE) && node.data.referenceCompact ? REFERENCE_COMPACT_HEIGHT : undefined}
            farView={<CardFarView picture={farPictureOf(node)} film={isFilmCard(node)} userId={user?.sub} workspaceName={currentProjectName} ownerUserId={projectOwnerId} />}
          >
            {node.type === NodeType.QUICK_EDITOR && (
              <QuickEditorNode
                data={node.data}
                onDataChange={(newData) => handleNodeDataChange(node.id, { ...newData, timestamp: Date.now() })}
                userId={user?.sub}
                workspaceName={currentProjectName}
                ownerUserId={projectOwnerId}
                isViewOnly={isViewOnly}
                inputVideos={getInputVideosForNode(node.id)}
                inputAudio={getInputAudioForNode(node.id)}
              />
            )}
            {(node.type === NodeType.MEDIA_REFERENCE || node.type === NodeType.AUDIO_REFERENCE) && (
              <ReferenceNode
                data={node.data}
                onDataChange={(newData) => handleNodeDataChange(node.id, { ...newData, timestamp: Date.now() })}
                userId={user?.sub}
                workspaceName={currentProjectName}
                nodeId={node.id}
                onAutoSaveFile={handleAutoSaveFile}
                isViewOnly={isViewOnly}
                ownerUserId={projectOwnerId}
                getChoices={() => {
                    const here = collectReferenceChoices(nodes, history);
                    return [...here, ...sharedReferenceChoices(projectReferences.sources, here)];
                }}
                sharing={referenceSharing}
                nodeSize={node.size}
                onResize={(size) => handleNodeResize(node.id, size)}
              />
            )}
            {node.type === NodeType.SKETCH_PAD && (
              <SketchNode
                data={node.data}
                nodeId={node.id}
                onDataChange={(newData) => handleNodeDataChange(node.id, {...newData})}
                onResize={handleNodeResize}
                userId={user?.sub}
                workspaceName={currentProjectName}
                onAutoSaveFile={handleAutoSaveFile}
                isViewOnly={isViewOnly}
                ownerUserId={projectOwnerId}
              />
            )}
            {node.type === NodeType.SCRIPT_PROCESSOR && (
               <ScriptNode 
                  data={node.data}
                  nodeId={node.id}
                  onDataChange={(newData) => handleNodeDataChange(node.id, {...newData})}
                  apiKey={apiKeys.googleGenAi}
                  onSpawnNodes={(items, type) => handleSpawnScriptNodes(node.id, items, type)}
                  onResize={handleNodeResize}
                  isViewOnly={isViewOnly}
               />
            )}
            {node.type === NodeType.TEXT_PROMPT && (
               <TextPromptNode data={node.data} />
            )}
                {node.type === NodeType.WRITE_NODE && (
                    <WriteNode 
                        data={node.data}
                        onDataChange={(newData) => handleNodeDataChange(node.id, { ...newData })}
                        googleApiKey={apiKeys.googleGenAi}
                        openAiApiKey={apiKeys.openAi}
                        isViewOnly={isViewOnly}
                    />
                )}
            {[NodeType.CHARACTER_NODE, NodeType.PROP_NODE, NodeType.LOCATION_NODE].includes(node.type) && (
               <EntityNode 
                   data={node.data} 
                   type={node.type}
                   onDataChange={(newData) => handleNodeDataChange(node.id, {...newData})}
                   apiKey={apiKeys.googleGenAi}
                   inputImages={getInputImagesForNode(node.id)}
                   userId={user?.sub}
                   workspaceName={currentProjectName}
                   isViewOnly={isViewOnly}
                   ownerUserId={projectOwnerId}
               />
            )}
            {node.type === NodeType.VIDEO_GENERATOR && (
               <VideoGeneratorNode 
                 data={
                     getInputTextForNode(node.id)
                     ? { ...node.data, prompt: getInputTextForNode(node.id) } 
                     : node.data
                 }
                 onDataChange={(newData) => handleNodeDataChange(node.id, {...newData, timestamp: Date.now()})}
                 inputImages={inputImagesWithPicked(node)}
                 inputAudio={inputAudioWithPicked(node)}
                 inputVideos={inputVideosWithPicked(node)}
                 onRemoveConnection={(inputId) => removeNodeInput(node.id, inputId)}
                 getReferenceChoices={referenceChoicesInProject}
                 referenceSharing={referenceSharing}
                 onPickedReferencesChange={(next) => handleNodeDataPatch(node.id, { pickedReferences: next })}
                 apiKey={apiKeys.googleGenAi}
                 openAiApiKey={apiKeys.openAi}
                 veniceApiKey={apiKeys.venice}
                 onRequestApiKey={openApiKeySettings}
                 userId={user?.sub}
                 workspaceName={currentProjectName}
                 nodeId={node.id}
                 size={node.size}
                 onResize={handleNodeResize}
                 onAutoSaveStatus={showAutoSaveIndicator}
                 onAutoSaveFile={handleAutoSaveFile}
                 onDataPatch={nodePatcher(workspaceKeyRef.current)}
                 isViewOnly={isViewOnly}
                 ownerUserId={projectOwnerId}
               />
            )}
            {node.type === NodeType.IMAGE_GENERATOR && (
               <ImageGeneratorNode
                 data={
                     getInputTextForNode(node.id)
                     ? { ...node.data, prompt: getInputTextForNode(node.id) } 
                     : node.data
                 }
                 onDataChange={(newData) => handleNodeDataChange(node.id, {...newData, timestamp: Date.now()})}
                 inputImages={inputImagesWithPicked(node)}
                 inputEntities={getInputEntitiesForNode(node.id)}
                 onRemoveConnection={(inputId) => removeNodeInput(node.id, inputId)}
                 getReferenceChoices={referenceChoicesInProject}
                 referenceSharing={referenceSharing}
                 onPickedReferencesChange={(next) => handleNodeDataPatch(node.id, { pickedReferences: next })}
                 onDataPatch={nodePatcher(workspaceKeyRef.current)}
                 googleApiKey={apiKeys.googleGenAi}
                 openAiApiKey={apiKeys.openAi}
                 veniceApiKey={apiKeys.venice}
                 onRequestApiKey={openApiKeySettings}
                 userId={user?.sub}
                 workspaceName={currentProjectName}
                 nodeId={node.id}
                 onAutoSaveStatus={showAutoSaveIndicator}
                 onAutoSaveFile={handleAutoSaveFile}
                 customWorkflows={customWorkflows}
                 size={node.size}
                 onResize={handleNodeResize}
                 isViewOnly={isViewOnly}
                 ownerUserId={projectOwnerId}
               />
            )}
            {node.type === NodeType.STORYBOARD_NODE && (
               <StoryboardNode
                 data={node.data}
                 onDataChange={(newData) => handleNodeDataChange(node.id, {...newData, timestamp: Date.now()})}
                 inputImages={getInputImagesForNode(node.id)}
                 inputEntities={getInputEntitiesForNode(node.id)}
                 onRemoveConnection={handleRemoveConnection}
                 googleApiKey={apiKeys.googleGenAi}
                 onRequestApiKey={openApiKeySettings}
                 userId={user?.sub}
                 workspaceName={currentProjectName}
                 nodeId={node.id}
                 onAutoSaveStatus={showAutoSaveIndicator}
                 onAutoSaveFile={handleAutoSaveFile}
                 customWorkflows={customWorkflows}
                 isViewOnly={isViewOnly}
                 ownerUserId={projectOwnerId}
               />
            )}
            {node.type === NodeType.STORAGE_BROWSER && user && (
               <StorageNode
                 data={node.data}
                 userId={user.sub}
                 onDataChange={(newData) => handleNodeDataChange(node.id, {...newData})}
                 isExpanded={!!expandedStorageNodes[node.id]}
                 onExpandToggle={() => handleStorageNodeExpandToggle(node.id)}
                 isViewOnly={isViewOnly}
                 ownerUserId={projectOwnerId}
                 projectName={currentProjectName}
                 projectId={currentProjectId || undefined}
               />
            )}
            {node.type === NodeType.SHARE_NODE && (
               <ShareNode
                 data={node.data}
                 onDataChange={(newData) => handleNodeDataChange(node.id, {...newData})}
                 userId={user?.sub}
                 connectedImageUrl={getInputImagesForNode(node.id)[0]?.url}
                 onRequestTwitterAuth={() => setShowTwitterAuthOverlay(true)}
                 isViewOnly={isViewOnly}
                 ownerUserId={projectOwnerId}
               />
            )}
          </BaseNode>
        ))}
      </InfiniteCanvas>
      </MediaLookupsContext.Provider>
      </MediaOwnerContext.Provider>

      {/* The Ideas page lies over the canvas and under the top bar, the library panel and the drawers,
          which stay at hand; the node rail and the canvas's own controls step aside for it */}
      {workspaceView === 'ideas' && (
        <IdeasPage
          key={currentProjectId || currentLocalWorkspaceId || 'workspace'}
          projectId={ideaProjectId}
          user={ideaUser}
          people={workspacePeople}
          isViewOnly={isViewOnly}
          textKeys={{ venice: apiKeys.venice, google: apiKeys.googleGenAi, openai: apiKeys.openAi }}
          askedIdeaId={askedIdeaId}
          onAskedIdeaOpened={clearAskedIdea}
          onShare={() => setShowShareModal(true)}
          panelOpen={!!user && isProjectPanelOpen}
          onFocusChange={setWritingOpen}
          nodes={canvasNodes}
          onSendToCanvas={isViewOnly ? undefined : handlePromptToCanvas}
        />
      )}

      {workspaceView === 'edit' && (
        <EditPage
          key={currentProjectId || currentLocalWorkspaceId || 'workspace'}
          data={workspaceEditData}
          onDataChange={handleWorkspaceEditChange}
          workspaceName={currentProjectName}
          videos={workspaceVideos}
          audio={workspaceAudio}
          userId={user?.sub}
          ownerUserId={projectOwnerId}
          isViewOnly={isViewOnly}
          // The top bar, with the save status, is under this page: the page shows the status at
          // the end of its own bottom bar, in its colours
          status={
            <span className="ns-chip ns-chip-sm ns-chip-dark whitespace-nowrap" data-save-status={autoSaveStatus.isActive ? 'saving' : 'saved'}>
              {autoSaveStatus.isActive ? <Loader2 size={12} className="animate-spin" /> : <Cloud size={12} className="text-mint" />}
              {autoSaveStatus.isActive ? 'Autosave' : 'Synced'}
            </span>
          }
        />
      )}

      {showLocalFolder && (
        <LocalFolderWindow
          userId={user?.sub}
          workspaceName={currentProjectName}
          onClose={() => setShowLocalFolder(false)}
          onChooseFolder={chooseFolderFromWindow}
          onCopyCloudFiles={user ? copyCloudFilesToFolder : undefined}
        />
      )}

      {/* Page switch: one element in one fixed place for both pages, so it does not move when the
          page changes; only its colours follow the page. It is one of the canvas's bars (16px from
          the window's edge) and lies in their layer: it comes after the Edit page, so it is drawn
          over that page, and under every dialog and page that covers the workspace. */}
      <div className="fixed bottom-4 left-1/2 -translate-x-1/2 z-[60] pointer-events-auto">
        {!writingOpen && <WorkspaceViewSwitch view={workspaceView} onChange={handleWorkspaceViewChange} tone={workspaceView === 'edit' ? 'dark' : 'light'} />}
      </div>

      {/* Cursor sharing — Figma-style live cursors of the other users, and this user's own sent to them.
          A component of its own: a cursor update renders it alone, not the app. */}
      <CollaboratorCursors
        projectId={currentProjectId}
        enabled={enablePresence}
        othersPresent={othersPresent}
        paused={workspaceView !== 'nodes'}
        canvasRef={canvasRef}
        transform={transform}
      />
      
      <Gallery 
          history={history}
          isOpen={isGalleryOpen}
          onClose={() => setIsGalleryOpen(false)}
          onRestore={handleRestoreNode}
          userId={user?.sub}
      />
      
      {/* (The save status is a chip in the top bar's right group; on the Edit page, in that page's bottom bar.) */}

      {/* Twitter Auth Overlay - Full-screen lightbox */}
      <TwitterAuthOverlay
        isOpen={showTwitterAuthOverlay}
        onClose={() => setShowTwitterAuthOverlay(false)}
        onConnectionChange={(connected, username) => {
          setTwitterConnected(connected);
          setTwitterUsername(username || null);
          setTwitterUserTier(getTwitterUserTier());
        }}
      />
    </div>
  );
}

/** How wide a drawer docked to the right edge is (storage, gallery, change history), in px: the `w-[360px]` of the drawer. */
const RIGHT_DRAWER_WIDTH = 360;

/**
 * Says how much room an open right drawer takes of the canvas: while one is open,
 * `--canvas-right-inset` is set on the root element, in px, and it is removed when none is.
 * The zoom rail and the minimap (components/InfiniteCanvas.tsx) keep that far from the right
 * edge, as the node rail moves aside for the library panel on the left. A component of its
 * own that renders nothing, so the app itself keeps no state and no effect for it.
 */
const CanvasRightInset: React.FC<{ open: boolean }> = ({ open }) => {
  React.useLayoutEffect(() => {
    const root = document.documentElement;
    if (open) root.style.setProperty('--canvas-right-inset', `${RIGHT_DRAWER_WIDTH}px`);
    return () => { root.style.removeProperty('--canvas-right-inset'); };
  }, [open]);
  return null;
};
